// Master Apps · Cadastrar / editar aplicação.
//
// Formulário controlado com autosave de rascunho em localStorage e base de
// conhecimento de tecnologias. Os controles (Select, SiteUrlInput, StackPicker)
// abrem em portal + position fixed para nunca serem cortados pelo container de
// scroll — mesma estratégia do `InfoPopover`.
//
// A MESMA tela atende os dois modos: `mode="create"` (POST) e `mode="edit"`
// (PATCH, com `initial` preenchendo todos os campos).
//
// VALIDAÇÃO: escrita à mão em `validatePayload`, espelhando regra por regra o
// `applicationCreateSchema` da API (mensagens idênticas). O schema entra aqui
// apenas como TIPO (`import type`): importá-lo como valor arrastava zod + os 52
// locales + drizzle-orm/pg-core + drizzle-zod para o bundle do navegador —
// 293 kB, 42,6% do chunk principal, por uma linha de import.

import {
  APPLICATION_MAX_IMAGES,
  type ApplicationCreateInput,
  horasDeProjeto,
  missingStatusUrl,
} from '@masterapps/shared';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import {
  IconBolt,
  IconCheck,
  IconChevronD,
  IconChevronR,
  IconGlobe,
  IconLock,
  IconRefresh,
  IconSearch,
  IconShield,
  IconUsers,
  IconX,
} from '@/components/icons';
import { InfoPopover, type InfoSection } from '@/components/info-popover';
import { MultiSelect, type MultiSelectOption } from '@/components/multi-select';
import { ButtonGhost, Sidebar, TopBar } from '@/components/primitives';
import {
  type AccessCollaborator,
  useCollaboratorSearch,
  useCollaborators,
  useSectors,
  useUnits,
} from '@/hooks/use-access';
import { ApiError, type AppWithAccess, readApiError } from '@/hooks/use-applications';
import { type FormDraftStatus, useFormDraft } from '@/hooks/use-form-draft';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import {
  FULLSTACK_DEFAULT_STACKS,
  NON_SELECTABLE_APP_TYPES,
  STACK_COLORS,
} from '@/lib/catalog-config';
import { FIELD_HELP } from '@/lib/field-help';
import { STACK_CATALOG, searchStacks, stackCategoryOf } from '@/lib/stacks-catalog';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
  type ScreenProps,
} from '@/lib/theme';
import { getClerk } from '@/store/session';

const DANGER = '#dc2626';
const OK_GREEN = '#16a34a';
const HML_YELLOW = '#ca8a04';

/** Chave do rascunho de CRIAÇÃO no localStorage. */
const DRAFT_KEY = 'master-apps:draft:application-new';

/**
 * Rascunho da EDIÇÃO: uma chave por aplicação, senão o rascunho de um cadastro
 * novo apareceria dentro da edição de outra app (e vice-versa).
 */
const editDraftKey = (id: string) => `master-apps:draft:application-edit:${id}`;

// ─── Opções dos enums · valor = enum do banco ──
export interface SelectOption {
  value: string;
  label: string;
}

// enum app_type · a lista COMPLETA do banco, um item por valor do enum.
const ALL_TYPE_OPTIONS: SelectOption[] = [
  { value: 'power-apps', label: 'Power Apps' },
  { value: 'fullstack', label: 'Fullstack' },
  { value: 'web', label: 'App web' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'api', label: 'API' },
  { value: 'integracao', label: 'Integração' },
  { value: 'rpa', label: 'RPA' },
  { value: 'data', label: 'Dados / BI' },
  { value: 'infra', label: 'Infra' },
];

/**
 * O que o FORMULÁRIO oferece: a lista do enum menos os tipos que saíram da
 * escolha (`NON_SELECTABLE_APP_TYPES` — hoje só `data`, "Dados / BI"). A lista
 * completa fica acima porque o enum do banco NÃO mudou: o rótulo continua
 * disponível para EXIBIÇÃO em `APP_TYPES` (catálogo, inventário, CSV), e é só a
 * seleção que encolhe. Filtramos em vez de apagar a linha para o dia em que o
 * tipo voltar (ou outro sair) ser uma mudança de um conjunto, não de duas
 * listas que podem divergir.
 */
const TYPE_OPTIONS: SelectOption[] = ALL_TYPE_OPTIONS.filter(
  (o) => !NON_SELECTABLE_APP_TYPES.has(o.value),
);

// enum app_status
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'prod', label: 'Produção' },
  { value: 'homolog', label: 'Homologação' },
  { value: 'dev', label: 'Desenvolvimento' },
  { value: 'manutencao', label: 'Manutenção' },
  // "Pausado" antes de "Depreciado" pela mesma régua do deck: os dois estados
  // parados ficam no fim, e o reversível vem antes do terminal — assim quem
  // abre a lista para marcar um projeto congelado encontra "Pausado" primeiro e
  // não desiste em "Depreciado", que é o rótulo errado para o que só parou.
  { value: 'pausado', label: 'Pausado' },
  { value: 'depreciado', label: 'Depreciado' },
];

// enum criticality
const CRITICALITY_OPTIONS: SelectOption[] = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
];

// enum visibility · cor usada no seletor segmentado
const VISIBILITY_OPTIONS: { value: string; label: string; color: string }[] = [
  { value: 'publico', label: 'Público', color: OK_GREEN },
  { value: 'privado', label: 'Privado', color: HML_YELLOW },
  { value: 'interno', label: 'Interno', color: DANGER },
];

// Os dois times donos de aplicação na estrutura atual. Um cadastro antigo com
// outro valor continua aparecendo no Select da edição (o componente preserva o
// valor corrente), mas cadastro novo só escolhe entre estes.
const TEAM_OPTIONS: string[] = ['Desenvolvimento', 'Dados'];

// ─── Sites de documentação ────────────────────
interface DocSite {
  host: string;
  color: string;
}

/** Site sem host: o campo aceita a URL inteira, sem prefixo montado. */
const CUSTOM_DOC_SITE = 'URL completa';
const DEFAULT_DOC_SITE = 'SharePoint';

const DOC_SITES: Record<string, DocSite> = {
  // Host REAL do tenant (confirmado no Graph: /sites/root → masterboi.sharepoint.com).
  // O anterior ("sharepoint.masterboi.com") não existe — links montados com ele
  // nasciam mortos.
  SharePoint: { host: 'masterboi.sharepoint.com', color: '#0a7bbd' },
  GitHub: { host: 'github.com/masterboiteam', color: '#6e5494' },
  // Escape hatch: links antigos ou de outros domínios (Confluence, Drive…) que
  // não casam com nenhum host conhecido. Também é onde o prefill cai quando
  // desmonta uma URL gravada que não pertence aos sites acima.
  [CUSTOM_DOC_SITE]: { host: '', color: '#64748b' },
};
const FALLBACK_SITE: DocSite = { host: 'masterboi.sharepoint.com', color: '#0a7bbd' };
const SITE_OPTIONS: SelectOption[] = Object.keys(DOC_SITES).map((k) => ({ value: k, label: k }));

// ─── Grades e alturas por faixa de viewport ───
// A tela é 100% estilo inline, então quem escolhe o número de colunas de cada
// bloco é `byViewport` — não há `@media` capaz de vencer um `style` inline.
// Declarados fora dos componentes para não remontar objeto a cada render e para
// a decisão ficar num lugar só.

/** Duas colunas de campos curtos · empilha no celular. */
const FIELD_GRID_2 = { mobile: '1fr', tablet: '1fr 1fr', desktop: '1fr 1fr' } as const;

/**
 * Visibilidade + situação + criticidade. Três colunas só no desktop: a 768px
 * sobram 700px de conteúdo, ou seja, 224px por faixa, e o seletor segmentado da
 * visibilidade precisa de 234px (três botões de 74px medidos, mais 12 de gap).
 */
const FIELD_GRID_3 = {
  mobile: '1fr',
  tablet: 'repeat(2, 1fr)',
  desktop: 'repeat(3, 1fr)',
} as const;

/**
 * Documentação. Cada campo carrega o seletor de site (113px) mais o prefixo do
 * host — "masterboi.sharepoint.com/" mede ~175px e é `nowrap` —, então a linha
 * pede 328px no mínimo. Duas colunas só cabem no desktop.
 */
const DOC_GRID = { mobile: '1fr', tablet: '1fr', desktop: '1fr 1fr' } as const;

/**
 * Altura dos controles de uma linha. 42 e não 40 no compacto porque a borda de
 * 1px entra na conta: com 40 o `<input>` de dentro ficava com 38px de caixa e o
 * alvo de toque continuava abaixo do mínimo de 40px.
 */
const CONTROL_H = { mobile: 42, desktop: 36 } as const;

/** Altura de botão secundário · 32px no desktop, 40px (alvo de toque) no compacto. */
const SMALL_BTN_H = { mobile: 40, desktop: 32 } as const;

/** Altura da ação principal da barra do topo. */
const PRIMARY_BTN_H = { mobile: 40, desktop: 34 } as const;

/**
 * Largura de um quadro da tira de telas adicionais. O piso vem da linha de
 * ações do próprio quadro: quatro botões (capa, dois de ordem, remover) medem
 * ~146px no desktop e ~158px no compacto, onde o alvo de toque cresce. Abaixo
 * disso os botões quebram para duas linhas dentro do quadro.
 */
const GALERIA_THUMB_W = { mobile: 158, tablet: 162, desktop: 172 } as const;

/** Altura dos botões de um quadro da galeria · alvo maior no compacto. */
const GALERIA_BTN_H = { mobile: 34, desktop: 28 } as const;

// O diâmetro do "(i)" continua vindo do `size={18}` de cada campo, sem ajuste por
// faixa aqui: o próprio `InfoPopover` já aplica um piso de 26px no compacto e um
// expansor invisível de área tocável. Repetir a decisão nesta tela só criaria
// duas fontes de verdade para o mesmo número.

// ─── Estado do formulário ─────────────────────
type LgpdKey = 'dadosPessoais' | 'dadosSensiveis' | 'integracaoExt' | 'ia';

interface FormState {
  name: string;
  slug: string;
  /** Chave do objeto da CAPA no bucket (`null` = sem imagem). */
  imageKey: string | null;
  /**
   * Telas ADICIONAIS, na ordem em que a apresentação vai mostrá-las. A capa não
   * entra aqui: a sequência do carrossel é `[imageKey, ...galeriaKeys]`, e é por
   * isso que o teto de `APPLICATION_MAX_IMAGES` conta o conjunto (a capa ocupa
   * uma das vagas), não só esta lista.
   */
  galeriaKeys: string[];
  /**
   * Assinatura das imagens SERVIDAS quando o formulário abriu — não é campo do
   * usuário e não viaja no payload. Existe só para o rascunho: um rascunho
   * escrito contra uma versão anterior do registro não pode ressuscitar a
   * galeria antiga por cima das telas de quem salvou depois (ver `mergeDraft`).
   */
  imagensServidas: string;
  type: string;
  description: string;
  visibility: string;
  status: string;
  criticality: string;
  ticket: string;
  project: string;
  team: string;
  owner: string;
  urlProd: string;
  urlHml: string;
  urlDev: string;
  docsTech: string;
  docsTechSite: string;
  docsBiz: string;
  docsBizSite: string;
  docsApi: string;
  requirements: string;
  requirementsSite: string;
  repo: string;
  stacks: string[];
  /**
   * Bloco "Projeto" — tudo opcional. Os dois primeiros são a calculadora de
   * esforço e ficam como STRING no formulário, não como número: campo numérico
   * vazio não tem representação em `number` (`NaN` e `0` mentem coisas
   * diferentes), e o que o usuário digitou tem de sobreviver intacto até o
   * envio. A conversão para número acontece uma vez só, ao montar o payload.
   */
  projetoDias: string;
  projetoHorasDia: string;
  projetoRetorno: string;
  projetoArea: string;
  projetoDono: string;
  lgpd: Record<LgpdKey, boolean>;
  /**
   * "Desabilitar apresentação": fora do deck e do PDF. É a MESMA coluna que a
   * lista "Plataformas" do lobby grava ao desmarcar — a ficha mostra o estado
   * que o lobby deixou, e vice-versa.
   */
  apresentacaoDesabilitada: boolean;
  /** Acesso · só usado quando a visibilidade é `privado`. */
  accessUnits: string[];
  accessSectors: string[];
  accessCollaborators: string[];
  perCollaborator: boolean;
}

/**
 * Estado inicial vazio. Fora do componente porque também é o `pristine` do
 * autosave (nada é gravado enquanto o formulário estiver assim) e a base da
 * comparação "formulário sujo".
 *
 * Nenhum default de conveniência aqui: stacks e os toggles de LGPD saem
 * vazios/falsos. Marcar LGPD por padrão publicaria evidência de auditoria
 * falsa.
 */
const EMPTY_FORM: FormState = {
  name: '',
  slug: '',
  imageKey: null,
  galeriaKeys: [],
  imagensServidas: '',
  type: '',
  description: '',
  visibility: 'publico',
  status: 'prod',
  criticality: 'media',
  ticket: '',
  project: '',
  team: '',
  owner: '',
  urlProd: '',
  urlHml: '',
  urlDev: '',
  docsTech: '',
  docsTechSite: DEFAULT_DOC_SITE,
  docsBiz: '',
  docsBizSite: DEFAULT_DOC_SITE,
  docsApi: '',
  requirements: '',
  requirementsSite: DEFAULT_DOC_SITE,
  repo: '',
  stacks: [],
  projetoDias: '',
  projetoHorasDia: '',
  projetoRetorno: '',
  projetoArea: '',
  projetoDono: '',
  lgpd: { dadosPessoais: false, dadosSensiveis: false, integracaoExt: false, ia: false },
  apresentacaoDesabilitada: false,
  accessUnits: [],
  accessSectors: [],
  accessCollaborators: [],
  perCollaborator: false,
};

/**
 * Assinatura do conjunto de imagens de um registro — capa e telas adicionais na
 * ordem. Serve de carimbo para comparar duas leituras do MESMO registro: o
 * rascunho guarda a assinatura de quando foi escrito e `mergeDraft` a confere
 * contra o que o servidor está entregando agora.
 *
 * `\n` como separador porque a chave é `applications/<uuid>.<ext>` e nunca tem
 * quebra de linha — nenhum conteúdo consegue forjar a fronteira entre dois itens.
 */
function assinaturaImagens(capa: string | null, galeria: readonly string[]): string {
  return [capa ?? '', ...galeria].join('\n');
}

/**
 * Estado do formulário a partir de uma aplicação existente (modo edição).
 * Prefill de TODOS os campos, inclusive o bloco de acesso e os quatro toggles de
 * conformidade. As URLs de documentação voltam a ser (site, caminho) via
 * `splitDocUrl` — o inverso exato do `docUrl` que as montou.
 */
function formFromApp(app: AppWithAccess): FormState {
  const tech = splitDocUrl(app.docsTechnical);
  const biz = splitDocUrl(app.docsBusiness);
  const req = splitDocUrl(app.requirements);
  const access = app.access;
  const galeria = app.galeriaKeys ?? [];
  return {
    name: app.name,
    slug: app.slug,
    imageKey: app.imageKey ?? null,
    galeriaKeys: [...galeria],
    imagensServidas: assinaturaImagens(app.imageKey ?? null, galeria),
    type: app.type,
    description: app.description,
    visibility: app.visibility,
    status: app.status,
    criticality: app.criticality,
    ticket: app.ticket ?? '',
    project: app.project ?? '',
    team: app.team,
    owner: app.owner,
    urlProd: app.url ?? '',
    urlHml: app.urlHml ?? '',
    urlDev: app.urlDev ?? '',
    docsTech: tech.path,
    docsTechSite: tech.site,
    docsBiz: biz.path,
    docsBizSite: biz.site,
    docsApi: app.docsApi ?? '',
    requirements: req.path,
    requirementsSite: req.site,
    repo: app.repo ?? '',
    stacks: [...app.stacks],
    // Número → string: o formulário guarda texto, e `null` (nunca preenchido)
    // tem de virar campo VAZIO, não "0" — zero dia é uma afirmação, branco não.
    projetoDias: app.projetoDias === null ? '' : String(app.projetoDias),
    projetoHorasDia: app.projetoHorasDia === null ? '' : String(app.projetoHorasDia),
    projetoRetorno: app.projetoRetorno ?? '',
    projetoArea: app.projetoArea ?? '',
    projetoDono: app.projetoDono ?? '',
    lgpd: {
      dadosPessoais: app.lgpd,
      dadosSensiveis: app.dadosSensiveis,
      integracaoExt: app.integracaoExterna,
      ia: app.usoIa,
    },
    apresentacaoDesabilitada: app.apresentacaoDesabilitada,
    accessUnits: [...(access?.units ?? [])],
    accessSectors: [...(access?.sectors ?? [])],
    accessCollaborators: [...(access?.collaborators ?? [])],
    // A coluna é a fonte da verdade do toggle; `access` só espelha.
    perCollaborator: access?.restrictedByCollaborator ?? app.restrictedByCollaborator,
  };
}

type FieldErrors = Partial<Record<string, string>>;

// ─── Utilidades ───────────────────────────────
/**
 * `trim` de separadores nas pontas, à mão. Era `/^-+|-+$/g`: uma alternância de
 * quantificadores ancorados que o motor reavalia posição por posição (custo
 * super-linear em nomes longos). O laço faz o mesmo recorte em uma passada.
 */
function trimSeparators(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && s[start] === '-') start += 1;
  while (end > start && s[end - 1] === '-') end -= 1;
  return s.slice(start, end);
}

function slugify(s: string): string {
  const dashed = s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-');
  return trimSeparators(dashed);
}

/**
 * `slugify` aplicado durante a digitação: preserva um único separador no fim
 * para não travar o usuário no meio de "portal-" + "pecuarista".
 */
function slugifyInput(s: string): string {
  const base = slugify(s);
  // O teste do separador pendente olha o último caractere JÁ SEM diacrítico:
  // contra o valor cru, "João" digitado letra a letra virava "joa-o" — o "ã"
  // não casa com a-z ASCII e era tratado como separador no meio da palavra.
  const last = s.slice(-1).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const trailing = base.length > 0 && last !== '' && /[^a-z0-9]/.test(last) ? '-' : '';
  return base + trailing;
}

function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function docUrl(site: string, path: string): string | null {
  const raw = path.trim();
  if (!raw) return null;
  const cfg = DOC_SITES[site] ?? FALLBACK_SITE;
  // Site sem host = modo "URL completa": o que o usuário digitou já é a URL.
  if (!cfg.host) return raw;
  return `https://${cfg.host}/${raw.replace(/^\/+/, '')}`;
}

/**
 * Inverso do `docUrl`: desmonta a URL gravada em (site, caminho) escolhendo o
 * host conhecido que casar. Sem casar nenhum (link antigo, outro domínio) cai no
 * modo "URL completa" com a URL inteira no campo — nada é perdido no prefill.
 */
function splitDocUrl(url: string | null | undefined): { site: string; path: string } {
  const raw = (url ?? '').trim();
  if (!raw) return { site: DEFAULT_DOC_SITE, path: '' };
  const lower = raw.toLowerCase();
  for (const [site, cfg] of Object.entries(DOC_SITES)) {
    if (!cfg.host) continue;
    for (const scheme of ['https://', 'http://']) {
      const prefix = `${scheme}${cfg.host.toLowerCase()}`;
      if (lower.startsWith(`${prefix}/`)) return { site, path: raw.slice(prefix.length + 1) };
      // host cru, sem caminho nenhum depois dele
      if (lower === prefix) return { site, path: '' };
    }
  }
  return { site: CUSTOM_DOC_SITE, path: raw };
}

/**
 * O usuário COLOU uma URL inteira (com esquema, ou começando por um host
 * conhecido)? Desmonta e devolve o par (site, caminho) para o campo trocar o
 * site sozinho — antes, colar um link do GitHub com o seletor em "SharePoint"
 * montava "https://masterboi.sharepoint.com/https://github.com/…" e o
 * formulário ficava visivelmente errado. Texto que é só caminho devolve null
 * (segue como digitado).
 */
function smartDocSplit(raw: string): { site: string; path: string } | null {
  const v = raw.trim();
  if (isHttpUrl(v)) return splitDocUrl(v);
  const lower = v.toLowerCase();
  for (const cfg of Object.values(DOC_SITES)) {
    if (!cfg.host) continue;
    const host = cfg.host.toLowerCase();
    if (lower.startsWith(`${host}/`) || lower === host) return splitDocUrl(`https://${v}`);
  }
  return null;
}

function normalizeOptions(options?: Array<string | SelectOption>): SelectOption[] {
  return (options ?? []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

// ─── Campos numéricos do bloco "Projeto" ──────
/**
 * Tetos da calculadora de esforço · os MESMOS do Zod da API
 * (`applicationInsertSchema`, em packages/shared). Repetidos aqui porque a tela
 * barra a digitação antes do envio; se o servidor mudar os limites, é este par
 * que precisa acompanhar.
 */
const MAX_PROJETO_DIAS = 3650;
const MAX_PROJETO_HORAS_DIA = 24;

/**
 * Texto do formulário → número do payload.
 *
 * Campo em branco vira `null`, e não `0`: a coluna é nullable e "não informado"
 * é uma afirmação diferente de "zero dia" — a distinção que o bloco inteiro faz
 * questão de manter. Por isso o vazio sai antes do `Number()`, que devolveria 0
 * para `''`.
 *
 * A VÍRGULA é aceita como separador decimal porque é assim que se digita em
 * português ("7,5" horas por dia) e `Number('7,5')` sozinho é `NaN`.
 *
 * Qualquer coisa que não resulte em número finito (letras, "1,2,3", `Infinity`)
 * também vira `null`: `NaN` viajaria no JSON como `null` de qualquer forma, mas
 * por acidente — aqui a decisão é explícita e o payload nunca carrega lixo.
 */
function numeroOuNull(valor: string): number | null {
  const bruto = valor.trim().replace(',', '.');
  if (bruto === '') return null;
  const numero = Number(bruto);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Filtro da DIGITAÇÃO dos dois campos da calculadora. Não normaliza nada para o
 * payload (quem converte é `numeroOuNull`): só impede de entrar o que o servidor
 * recusaria — sinal negativo, letras e valor acima do teto.
 *
 * O texto parcial sobrevive de propósito: quem digita "7,5" passa por "7," e um
 * filtro que exigisse número válido a cada tecla apagaria a vírgula recém-digitada.
 *
 * Estourar o teto devolve o PRÓPRIO teto em vez de recusar a tecla: campo que
 * simplesmente ignora o que se digita parece travado, sem dizer o porquê.
 */
function digitacaoNumerica(bruto: string, maximo: number, decimal: boolean): string {
  const limpo = bruto.replace(decimal ? /[^\d.,]/g : /\D/g, '');
  // Um separador decimal só: "7,5,2" vira "7,52" em vez de virar NaN no envio.
  const [inteiro = '', ...resto] = limpo.split(/[.,]/);
  const texto = resto.length > 0 ? `${inteiro},${resto.join('')}` : inteiro;
  const numero = numeroOuNull(texto);
  if (numero !== null && numero > maximo) return String(maximo);
  return texto;
}

/** Rola até o campo com erro e devolve o foco ao primeiro controle dele. */
function focusField(field: string): void {
  const host = document.querySelector(`[data-field="${field}"]`);
  if (!host) return;
  host.scrollIntoView({ behavior: 'smooth', block: 'center' });
  host.querySelector<HTMLElement>('input, textarea, button')?.focus({ preventScroll: true });
}

/** Há um popover/menu aberto? Usado para o Esc não cancelar o cadastro. */
function hasOpenLayer(): boolean {
  return document.querySelector('[role="dialog"],[data-mapp-panel]') !== null;
}

// ─── Campos obrigatórios · fonte única ────────
// A MESMA lista alimenta a barra de progresso e o bloqueio do publish, então
// os dois nunca discordam.
interface RequiredSpec {
  field: keyof FormState;
  label: string;
  ok: (f: FormState) => boolean;
  message: string;
}

const REQUIRED_SPECS: RequiredSpec[] = [
  {
    field: 'name',
    label: 'Nome',
    ok: (f) => f.name.trim().length >= 2,
    message: 'Informe o nome da aplicação (mínimo 2 caracteres).',
  },
  {
    field: 'slug',
    label: 'Identificador',
    ok: (f) => /^[a-z0-9-]+$/.test(f.slug.trim()),
    message: 'O identificador aceita apenas letras minúsculas, números e hífen.',
  },
  {
    field: 'type',
    label: 'Tipo',
    ok: (f) => f.type !== '',
    message: 'Escolha o tipo da aplicação.',
  },
  {
    field: 'description',
    label: 'Descrição',
    ok: (f) => f.description.trim().length >= 5,
    message: 'Descreva a aplicação em pelo menos 5 caracteres.',
  },
  {
    field: 'ticket',
    label: 'Chamado',
    ok: (f) => f.ticket.trim() !== '',
    message: 'Informe o chamado de origem.',
  },
  {
    field: 'project',
    label: 'Projeto',
    ok: (f) => f.project.trim() !== '',
    message: 'Informe o número do projeto.',
  },
  {
    field: 'team',
    label: 'Time',
    ok: (f) => f.team.trim() !== '',
    message: 'Escolha o time responsável.',
  },
  {
    field: 'owner',
    label: 'Responsável',
    ok: (f) => f.owner.trim() !== '',
    message: 'Escolha o responsável pela aplicação.',
  },
  {
    field: 'docsTech',
    label: 'Documentação técnica',
    ok: (f) => f.docsTech.trim() !== '',
    message: 'Informe o caminho da documentação técnica.',
  },
  {
    field: 'docsBiz',
    label: 'Regra de negócio',
    ok: (f) => f.docsBiz.trim() !== '',
    message: 'Informe o caminho da regra de negócio.',
  },
  {
    field: 'stacks',
    label: 'Tecnologias',
    ok: (f) => f.stacks.length > 0,
    message: 'Selecione pelo menos uma tecnologia.',
  },
];

/**
 * Tipo DISPENSADO do repositório: Power Apps é low-code da Power Platform, vive
 * no Dataverse/SharePoint e não tem repositório Git para apontar. Todo o resto
 * (fullstack, web, mobile, api, integração, rpa, infra) passa a exigir o link.
 *
 * Verificado no banco de produção: nenhuma das 42 aplicações está sem
 * repositório, então a regra não invalida registro existente — a edição de
 * qualquer app atual continua podendo ser salva.
 */
const REPO_EXEMPT_TYPE = 'power-apps';

/** O tipo escolhido exige repositório? Regra única da tela (form + progresso). */
function repoIsRequired(type: string): boolean {
  return type !== '' && type !== REPO_EXEMPT_TYPE;
}

const REPO_SPEC: RequiredSpec = {
  field: 'repo',
  label: 'Repositório',
  ok: (f) => isHttpUrl(f.repo),
  message: 'Informe a URL do repositório no GitHub (só Power Apps é dispensado).',
};

// Obrigatórios condicionais POR SITUAÇÃO (regra única em missingStatusUrl, no
// shared): produção exige a URL de produção; homologação, a de homologação.
// Dev/manutenção/pausado/depreciado não exigem nenhuma — a aplicação pode nem
// estar no ar. O formato de qualquer URL preenchida continua validado à parte.
const URL_PROD_SPEC: RequiredSpec = {
  field: 'urlProd',
  label: 'URL de produção',
  ok: (f) => isHttpUrl(f.urlProd),
  message: 'Aplicação em produção exige a URL de produção completa (https://…).',
};

const URL_HML_SPEC: RequiredSpec = {
  field: 'urlHml',
  label: 'URL de homologação',
  ok: (f) => isHttpUrl(f.urlHml),
  message: 'Aplicação em homologação exige a URL de homologação completa (https://…).',
};

// Obrigatórios condicionais da etapa de acesso · só entram na conta quando a
// aplicação é privada (e o de colaboradores, só com o toggle ligado).
const ACCESS_UNITS_SPEC: RequiredSpec = {
  field: 'accessUnits',
  label: 'Unidades com acesso',
  ok: (f) => f.accessUnits.length > 0,
  message: 'Escolha ao menos uma unidade que poderá visualizar a aplicação.',
};

const ACCESS_SECTORS_SPEC: RequiredSpec = {
  field: 'accessSectors',
  label: 'Setores com acesso',
  ok: (f) => f.accessSectors.length > 0,
  message: 'Escolha ao menos um setor dentro das unidades selecionadas.',
};

const ACCESS_COLLABORATORS_SPEC: RequiredSpec = {
  field: 'accessCollaborators',
  label: 'Colaboradores com acesso',
  ok: (f) => f.accessCollaborators.length > 0,
  message: 'Com confidencialidade por colaborador ligada, escolha ao menos um colaborador.',
};

function requiredSpecs(form: FormState): RequiredSpec[] {
  const list = [...REQUIRED_SPECS];
  if (form.status === 'prod') list.push(URL_PROD_SPEC);
  if (form.status === 'homolog') list.push(URL_HML_SPEC);
  if (repoIsRequired(form.type)) list.push(REPO_SPEC);
  if (form.visibility === 'privado') {
    list.push(ACCESS_UNITS_SPEC, ACCESS_SECTORS_SPEC);
    if (form.perCollaborator) list.push(ACCESS_COLLABORATORS_SPEC);
  }
  return list;
}

/**
 * chave do payload da API -> chave do formulário (para pintar o campo certo).
 * As chaves PONTUADAS são obrigatórias: o servidor manda `campo: 'access.units'`
 * em `details.issues[]`, e resolver só por `access` jogava um erro de
 * colaboradores embaixo do campo Unidades.
 */
const PAYLOAD_TO_FIELD: Record<string, string> = {
  name: 'name',
  slug: 'slug',
  type: 'type',
  visibility: 'visibility',
  status: 'status',
  description: 'description',
  url: 'urlProd',
  urlHml: 'urlHml',
  urlDev: 'urlDev',
  ticket: 'ticket',
  project: 'project',
  requirements: 'requirements',
  docsTechnical: 'docsTech',
  docsBusiness: 'docsBiz',
  docsApi: 'docsApi',
  repo: 'repo',
  stacks: 'stacks',
  owner: 'owner',
  team: 'team',
  criticality: 'criticality',
  // Bloco "Projeto": a chave do payload é a MESMA do formulário, mas o mapa
  // continua explícito — `fieldForPayloadKey` devolve `_` (aviso geral, sem
  // campo pintado) para tudo que não estiver listado aqui.
  projetoDias: 'projetoDias',
  // As duas parcelas da calculadora dividem UM campo na tela ("Tempo de
  // projeto"), logo um só `data-field`: apontar `projetoHorasDia` para si mesmo
  // deixaria o erro sem lugar para aparecer e sem alvo para o scroll.
  projetoHorasDia: 'projetoDias',
  projetoRetorno: 'projetoRetorno',
  projetoArea: 'projetoArea',
  projetoDono: 'projetoDono',
  'access.units': 'accessUnits',
  'access.sectors': 'accessSectors',
  'access.collaborators': 'accessCollaborators',
  'access.restrictedByCollaborator': 'accessCollaborators',
  // Só quando o servidor recusa o bloco inteiro, sem apontar a lista.
  access: 'accessUnits',
};

/** Ordem visual dos campos · define qual erro recebe o scroll e o foco. */
const FIELD_ORDER: string[] = [
  'name',
  'slug',
  'type',
  'description',
  'visibility',
  'status',
  'criticality',
  'accessUnits',
  'accessSectors',
  'accessCollaborators',
  'ticket',
  'project',
  'team',
  'owner',
  // `projetoHorasDia` não entra: a hora por dia é mostrada dentro do campo
  // `projetoDias` ("Tempo de projeto"), e é esse o alvo do scroll.
  'projetoDias',
  'projetoArea',
  'projetoRetorno',
  'projetoDono',
  'urlProd',
  'urlHml',
  'urlDev',
  'docsTech',
  'docsBiz',
  'docsApi',
  'requirements',
  'repo',
  'stacks',
];

/**
 * Campo do formulário para um `campo` do payload. Tenta o caminho COMPLETO e vai
 * encurtando à direita, então `access.collaborators.3` cai em
 * `accessCollaborators` (e não em `accessUnits`) e `stacks.0` cai em `stacks`.
 * Sem correspondência devolve `_`, que a tela mostra no aviso geral.
 */
function fieldForPayloadKey(campo: string): string {
  const parts = campo.split('.').filter(Boolean);
  for (let end = parts.length; end > 0; end -= 1) {
    const hit = PAYLOAD_TO_FIELD[parts.slice(0, end).join('.')];
    if (hit) return hit;
  }
  return '_';
}

/** Bloco `access` do contrato da API · vazio quando não é aplicação privada. */
interface AccessPayload {
  units: string[];
  sectors: string[];
  collaborators: string[];
  restrictedByCollaborator: boolean;
}

/**
 * Deriva o acesso a partir do formulário. A visibilidade manda: `publico` e
 * `interno` viajam com listas vazias mesmo que algo tenha sobrado no estado —
 * acesso inconsistente é recusado pelo servidor.
 */
function buildAccess(form: FormState): AccessPayload {
  if (form.visibility !== 'privado') {
    return { units: [], sectors: [], collaborators: [], restrictedByCollaborator: false };
  }
  return {
    units: form.accessUnits,
    sectors: form.accessSectors,
    collaborators: form.perCollaborator ? form.accessCollaborators : [],
    restrictedByCollaborator: form.perCollaborator,
  };
}

interface PayloadOptions {
  /**
   * `false` OMITE o bloco `access` do payload.
   *
   * PEGADINHA DO PATCH: `access` presente com listas vazias APAGA as associações
   * de unidade/setor/colaborador; `access` ausente PRESERVA o que está gravado.
   * Por isso, na edição, só enviamos o bloco quando ele realmente representa a
   * intenção do usuário (ver `sendAccess` na tela).
   */
  includeAccess?: boolean;
  /**
   * Campos que este formulário NÃO edita e que o PATCH não pode zerar
   * (`favorite`, `iconBg` e `deployedAt` viriam com o default de criação e
   * apagariam o valor gravado). `lastUpdate` entra pelo mesmo motivo: é a data
   * do último trabalho real na APLICAÇÃO, não do último retoque no cadastro —
   * sobrescrever com "hoje" jogava a app para o recorte errado do deck de
   * apresentação (o período filtra por `lastUpdate`).
   */
  preserve?: {
    favorite: boolean;
    iconBg: string;
    deployedAt: string | null;
    lastUpdate: string;
  };
}

/**
 * Data LOCAL como YYYY-MM-DD. `toISOString()` é UTC: depois das 21h em
 * America/Recife gravaria o dia seguinte — e uma publicação em 31/12 à noite
 * entraria no deck do ANO seguinte. Mesmo padrão de `csvFilename` (lib/csv).
 */
function localTodayISO(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/**
 * Monta o payload da API a partir do formulário.
 *
 * O cast final existe porque o estado guarda os enums como `string` (os
 * controles são genéricos): as listas de opções são a garantia de que o valor
 * pertence ao enum, e `validatePayload` roda antes de qualquer envio.
 */
function buildPayload(form: FormState, options: PayloadOptions = {}): ApplicationCreateInput {
  const { includeAccess = true, preserve } = options;
  const name = form.name.trim();
  const slug = (form.slug.trim() || slugify(name)).trim();
  return {
    name,
    slug,
    imageKey: form.imageKey,
    galeriaKeys: form.galeriaKeys,
    type: form.type,
    visibility: form.visibility,
    status: form.status,
    iconBg: preserve?.iconBg ?? NOC_RED,
    iconLetter: (name[0] || 'A').toUpperCase(),
    description: form.description.trim(),
    url: form.urlProd.trim() || null,
    urlHml: form.urlHml.trim() || null,
    urlDev: form.urlDev.trim() || null,
    favorite: preserve?.favorite ?? false,
    ticket: form.ticket.trim() || null,
    project: form.project.trim() || null,
    requirements: docUrl(form.requirementsSite, form.requirements),
    docsTechnical: docUrl(form.docsTechSite, form.docsTech),
    docsBusiness: docUrl(form.docsBizSite, form.docsBiz),
    docsApi: form.docsApi.trim() || null,
    repo: form.repo.trim() || null,
    stacks: form.stacks,
    owner: form.owner.trim(),
    team: form.team.trim(),
    criticality: form.criticality,
    // Os QUATRO toggles de conformidade viajam (cada um tem coluna própria):
    // mandar só `lgpd` publicava evidência de auditoria incompleta.
    lgpd: form.lgpd.dadosPessoais,
    dadosSensiveis: form.lgpd.dadosSensiveis,
    integracaoExterna: form.lgpd.integracaoExt,
    usoIa: form.lgpd.ia,
    apresentacaoDesabilitada: form.apresentacaoDesabilitada,
    // Bloco "Projeto" — tudo opcional, e campo em branco viaja como `null`
    // (a coluna é nullable; `''` gravaria "respondido com nada").
    projetoDias: numeroOuNull(form.projetoDias),
    projetoHorasDia: numeroOuNull(form.projetoHorasDia),
    projetoRetorno: form.projetoRetorno.trim() || null,
    projetoArea: form.projetoArea.trim() || null,
    projetoDono: form.projetoDono.trim() || null,
    ...(includeAccess ? { access: buildAccess(form) } : {}),
    deployedAt: preserve?.deployedAt ?? null,
    lastUpdate: preserve?.lastUpdate ?? localTodayISO(),
  } as ApplicationCreateInput;
}

// ─── Validação do payload · espelho do schema da API ──
// Regra por regra do `applicationCreateSchema` (packages/shared), com as MESMAS
// mensagens, escrita à mão para não importar zod no bundle do navegador. Cobre o
// que a lista de obrigatórios (`requiredSpecs`) não cobre: formato de URL dos
// links opcionais e os mínimos de tamanho que o servidor aplica.
interface PayloadIssue {
  /** Chave do payload (a mesma que o servidor usa em `details.issues[].campo`). */
  campo: string;
  mensagem: string;
}

function validatePayload(payload: ApplicationCreateInput): PayloadIssue[] {
  const issues: PayloadIssue[] = [];
  const push = (campo: string, mensagem: string) => issues.push({ campo, mensagem });

  if (payload.name.trim().length < 2) push('name', 'Nome muito curto');
  if (!/^[a-z0-9-]+$/.test(payload.slug)) push('slug', 'Slug deve ser kebab-case');
  if (payload.description.trim().length < 5) push('description', 'Descrição muito curta');
  // Obrigatoriedade POR SITUAÇÃO — mesma regra que a API aplica no POST/PATCH.
  const faltaUrl = missingStatusUrl(payload);
  if (faltaUrl) push(faltaUrl.campo, faltaUrl.mensagem);
  if (payload.owner.trim().length < 2) push('owner', 'Informe o responsável técnico');
  if (payload.team.trim().length < 2) push('team', 'Informe o time responsável');

  // Nullable + opcional no schema: só valida o formato quando há conteúdo.
  const optionalUrl = (campo: keyof ApplicationCreateInput, mensagem: string) => {
    const value = payload[campo];
    if (typeof value !== 'string' || value.trim() === '') return;
    if (!isHttpUrl(value)) push(campo, mensagem);
  };
  optionalUrl('url', 'URL de produção inválida');
  optionalUrl('urlHml', 'URL de homologação inválida');
  optionalUrl('urlDev', 'URL de desenvolvimento inválida');
  optionalUrl('requirements', 'Link do levantamento de requisitos inválido');
  optionalUrl('docsTechnical', 'Link da documentação técnica inválido');
  optionalUrl('docsBusiness', 'Link da regra de negócio inválido');
  optionalUrl('docsApi', 'Link da documentação de API inválido');
  optionalUrl('repo', 'Link do repositório inválido');

  // Espelho das duas regras do schema sobre a galeria. A tela já impede as duas
  // (o botão de adicionar desabilita no teto e a mesma chave não entra duas
  // vezes), mas um rascunho adulterado à mão chega aqui sem passar pelos botões
  // — e a recusa do servidor viria sem campo para pintar.
  const galeria = payload.galeriaKeys ?? [];
  if (galeria.length > GALERIA_MAX) {
    push(
      'galeriaKeys',
      `No máximo ${APPLICATION_MAX_IMAGES} imagens por aplicação, contando a capa`,
    );
  } else if (new Set(galeria).size !== galeria.length) {
    push('galeriaKeys', 'Imagem repetida na galeria');
  }

  return issues;
}

// ─── Painel ancorado (portal + fixed + clamp) ──
const PANEL_EDGE = 8;

interface PanelPos {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

function samePanelPos(a: PanelPos | null, b: PanelPos): boolean {
  return (
    a !== null &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

/**
 * Posiciona um menu a partir do retângulo do gatilho: abre para baixo, sobe
 * quando não couber, faz clamp horizontal e reposiciona em scroll/resize.
 * Também registra o Escape. `dismiss(true)` = fechado pelo teclado (quem
 * consome devolve o foco ao gatilho).
 */
function useDropdown(
  open: boolean,
  dismiss: (fromKeyboard: boolean) => void,
  anchorRef: RefObject<HTMLElement | null>,
  width: number | 'anchor',
  desiredHeight = 260,
): PanelPos | null {
  const [pos, setPos] = useState<PanelPos | null>(null);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(Math.max(width === 'anchor' ? r.width : width, 160), vw - 2 * PANEL_EDGE);
    const below = vh - r.bottom - PANEL_EDGE - 4;
    const above = r.top - PANEL_EDGE - 4;
    const up = below < Math.min(desiredHeight, 220) && above > below;
    const left = Math.min(Math.max(PANEL_EDGE, r.left), Math.max(PANEL_EDGE, vw - w - PANEL_EDGE));
    const next: PanelPos = up
      ? {
          bottom: Math.max(PANEL_EDGE, vh - r.top + 4),
          left,
          width: w,
          maxHeight: Math.min(desiredHeight, Math.max(140, Math.floor(above))),
        }
      : {
          top: r.bottom + 4,
          left,
          width: w,
          maxHeight: Math.min(desiredHeight, Math.max(140, Math.floor(below))),
        };
    setPos((prev) => (samePanelPos(prev, next) ? prev : next));
  }, [anchorRef, width, desiredHeight]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => measure();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss(true);
    };
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, measure, dismiss]);

  return pos;
}

interface DropdownPanelProps {
  pos: PanelPos | null;
  dark: boolean;
  onDismiss: () => void;
  children: ReactNode;
}

function DropdownPanel({ pos, dark, onDismiss, children }: Readonly<DropdownPanelProps>) {
  const p = makePalette(dark);
  return createPortal(
    <>
      {/* fundo invisível: fecha ao clicar fora sem roubar o foco do gatilho */}
      <div
        aria-hidden="true"
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        onClick={onDismiss}
        style={{ position: 'fixed', inset: 0, zIndex: 8000 }}
      />
      <div
        data-mapp-panel=""
        style={{
          position: 'fixed',
          top: pos?.top,
          bottom: pos?.bottom,
          left: pos?.left ?? 0,
          width: pos?.width ?? 220,
          maxHeight: pos?.maxHeight ?? 260,
          visibility: pos ? 'visible' : 'hidden',
          pointerEvents: pos ? 'auto' : 'none',
          zIndex: 8001,
          display: 'flex',
          flexDirection: 'column',
          background: p.surface,
          border: `1px solid ${p.border}`,
          borderRadius: 8,
          boxShadow: '0 14px 40px -12px rgba(0,0,0,0.28)',
          overflow: 'hidden',
          fontFamily: fontSans,
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ─── Teclado das listas de opções ─────────────
/**
 * Índice seguinte (`delta = 1`) ou anterior (`delta = -1`), com wrap nas pontas.
 */
function stepIndex(i: number, len: number, delta: 1 | -1): number {
  if (delta === 1) return i + 1 >= len ? 0 : i + 1;
  return i <= 0 ? len - 1 : i - 1;
}

/** Teclas que abrem o menu de um combobox fechado. */
const OPEN_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter', ' ']);

interface ComboboxKeys {
  open: boolean;
  /** Quantidade de opções da lista. */
  count: number;
  /** Índice da opção já escolhida (-1 quando não há nenhuma). */
  selectedIndex: number;
  /** Índice em destaque no momento. */
  active: number;
  setActive: (next: number | ((i: number) => number)) => void;
  setOpen: (open: boolean) => void;
  commit: (index: number) => void;
  /** Home/End vão ao primeiro/último item (só o Select os atende). */
  homeEnd: boolean;
  /**
   * true quando o gatilho é um `<input>` de texto (combobox editável). No
   * padrão ARIA editável, espaço é DIGITAÇÃO: sai das teclas de abertura e do
   * mapa de ações e atravessa para o input — só setas/Enter/Esc/Tab comandam o
   * popup. Espaço como comando (abrir/confirmar) é do combobox de BOTÃO, caso
   * do Select e do seletor de site.
   */
  textInput: boolean;
}

/** Ação de cada tecla com o menu ABERTO · mapa em vez de escada de `else if`. */
function openMenuActions(ctx: ComboboxKeys): Record<string, () => void> {
  const { count, active, selectedIndex, setActive, commit } = ctx;
  const confirm = () => commit(active >= 0 ? active : selectedIndex);
  const actions: Record<string, () => void> = {
    ArrowDown: () => setActive((i) => stepIndex(i, count, 1)),
    ArrowUp: () => setActive((i) => stepIndex(i, count, -1)),
    Enter: confirm,
  };
  // No combobox editável, espaço vira texto no input; só no de botão confirma.
  if (!ctx.textInput) actions[' '] = confirm;
  if (ctx.homeEnd) {
    actions.Home = () => setActive(0);
    actions.End = () => setActive(count - 1);
  }
  return actions;
}

/**
 * Teclado do combobox, igual para o Select e para o seletor de site: fechado, as
 * teclas de abertura destacam a opção já escolhida; aberto, as setas navegam,
 * Enter/Espaço confirmam e Tab fecha sem escolher (o Escape é do `useDropdown`).
 * Com `textInput`, espaço nunca é comando — atravessa e digita (ver interface).
 */
function comboboxKeyDown(e: React.KeyboardEvent, ctx: ComboboxKeys): void {
  // Espaço no combobox editável é digitação: nada de abrir menu nem confirmar.
  if (ctx.textInput && e.key === ' ') return;
  if (!ctx.open) {
    if (!OPEN_KEYS.has(e.key)) return;
    e.preventDefault();
    ctx.setActive(Math.max(ctx.selectedIndex, 0));
    ctx.setOpen(true);
    return;
  }
  if (e.key === 'Tab') {
    ctx.setOpen(false);
    ctx.setActive(-1);
    return;
  }
  const run = openMenuActions(ctx)[e.key];
  if (!run) return;
  e.preventDefault();
  run();
}

/**
 * Enter/Espaço equivalem ao clique numa linha de opção. As linhas têm
 * `tabIndex={-1}` (o teclado da lista vive no gatilho), mas o handler garante o
 * comportamento de quem chegar nelas por foco programático — e é o que a regra
 * de acessibilidade exige de um elemento com `onClick`.
 */
function activateOnKey(action: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };
}

// ─── Lista de opções navegável pelo teclado ────
interface OptionListProps {
  /** id da lista · o gatilho do combobox aponta para cá com `aria-controls`. */
  id?: string;
  opts: SelectOption[];
  value?: string;
  active: number;
  dark: boolean;
  accent: string;
  dotOf?: (o: SelectOption) => string | undefined;
  onPick: (index: number) => void;
  onActive: (index: number) => void;
  label: string;
}

function OptionList({
  id,
  opts,
  value,
  active,
  dark,
  accent,
  dotOf,
  onPick,
  onActive,
  label,
}: Readonly<OptionListProps>) {
  const p = makePalette(dark);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const row = listRef.current?.children[active];
    if (row instanceof HTMLElement) row.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <div
      id={id}
      ref={listRef}
      role="listbox"
      aria-label={label}
      style={{
        overflowY: 'auto',
        minHeight: 0,
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {opts.map((o, i) => {
        const selected = o.value === value;
        const dot = dotOf?.(o);
        return (
          <div
            key={o.value}
            role="option"
            aria-selected={selected}
            tabIndex={-1}
            onClick={() => onPick(i)}
            onKeyDown={activateOnKey(() => onPick(i))}
            onMouseEnter={() => onActive(i)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              borderRadius: 5,
              fontSize: 12.5,
              fontFamily: fontSans,
              fontWeight: selected ? 700 : 500,
              color: p.text,
              cursor: 'pointer',
              background: i === active ? p.surface2 : 'transparent',
            }}
          >
            {dot && (
              <span
                style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }}
              />
            )}
            <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
            {selected && <IconCheck size={13} style={{ color: accent }} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Campo ────────────────────────────────────
/** Props que o FormField injeta no controle (id + aria + estado de erro). */
export interface FieldControl {
  id: string;
  'aria-required'?: true;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  invalid?: boolean;
}

interface FormFieldProps {
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  optional?: boolean;
  /** Popover "i" renderizado à direita do texto do label. */
  info?: ReactNode;
  error?: string;
  /** Usado como alvo do scroll-até-o-erro (`data-field`). */
  name?: string;
  children: ReactNode | ((f: FieldControl) => ReactNode);
  dark: boolean;
  span?: number;
}

/**
 * `aria-describedby` do controle: aponta a mensagem de erro quando há erro, a
 * dica quando não há, e nada quando o campo não tem nem uma nem outra.
 */
function describedBy(
  errorId: string,
  hintId: string,
  error?: string,
  hint?: ReactNode,
): { 'aria-describedby'?: string } {
  if (error) return { 'aria-describedby': errorId };
  if (hint) return { 'aria-describedby': hintId };
  return {};
}

function FormField({
  label,
  hint,
  required,
  optional,
  info,
  error,
  name,
  children,
  dark,
  span = 1,
}: Readonly<FormFieldProps>) {
  const p = makePalette(dark);
  const uid = useId();
  const controlId = `${uid}-control`;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;

  const control: FieldControl = {
    id: controlId,
    ...(required ? { 'aria-required': true as const } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...describedBy(errorId, hintId, error, hint),
    invalid: Boolean(error),
  };

  return (
    <div data-field={name} style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
          minHeight: 20,
        }}
      >
        <label
          htmlFor={controlId}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 11,
            fontWeight: 700,
            color: p.text,
          }}
        >
          {label}
          {required && <span style={{ color: DANGER }}>*</span>}
        </label>
        {info}
        {optional && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: p.muted,
              padding: '2px 5px',
              borderRadius: 3,
              background: p.surface2,
              fontFamily: fontSans,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginLeft: 'auto',
            }}
          >
            opcional
          </span>
        )}
      </div>

      {typeof children === 'function' ? children(control) : children}

      {error ? (
        <div
          id={errorId}
          role="alert"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: DANGER,
            marginTop: 5,
            fontFamily: fontSans,
            lineHeight: 1.45,
          }}
        >
          {error}
        </div>
      ) : (
        hint && (
          <div
            id={hintId}
            style={{
              fontSize: 11,
              color: p.muted,
              marginTop: 5,
              fontFamily: fontSans,
              lineHeight: 1.45,
            }}
          >
            {hint}
          </div>
        )
      )}
    </div>
  );
}

// ─── Controles de texto ───────────────────────
interface FieldAria {
  id?: string;
  'aria-required'?: true;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  /**
   * Nome acessível quando o `<label>` do `FormField` NÃO aponta para este
   * controle — caso da calculadora de esforço, onde um único campo ("Tempo de
   * projeto") abriga dois inputs e o `htmlFor` só pode citar um deles.
   */
  'aria-label'?: string;
}

/** Cor da borda de um controle: o erro manda, depois o foco, senão a neutra. */
function frameColor(border: string, accent: string, focused: boolean, invalid?: boolean): string {
  if (invalid) return DANGER;
  if (focused) return accent;
  return border;
}

/** Anel externo do controle: o do foco tem prioridade sobre o de erro. */
function frameRing(ring: string, focused: boolean, invalid?: boolean): string {
  if (focused) return `0 0 0 3px ${ring}`;
  if (invalid) return `0 0 0 3px ${DANGER}22`;
  return 'none';
}

/** Borda + anel de foco compartilhados por todos os controles. */
function frameStyle(dark: boolean, accent: string, focused: boolean, invalid?: boolean) {
  const p = makePalette(dark);
  return {
    border: `1px solid ${frameColor(p.border, accent, focused, invalid)}`,
    boxShadow: frameRing(p.ring, focused, invalid),
    transition: 'border-color 120ms, box-shadow 120ms',
  };
}

interface InputProps extends FieldAria {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  dark: boolean;
  accent?: string;
  mono?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  invalid?: boolean;
  /**
   * Somente leitura (usado no slug em modo edição). `readOnly` em vez de
   * `disabled`: o campo continua focável e o leitor de tela ainda anuncia o
   * valor, que é justamente o que o usuário precisa conferir.
   */
  readOnly?: boolean;
  /** id de um `<datalist>` para autocomplete nativo (campo segue livre). */
  list?: string;
  /**
   * Teclado do celular. `inputMode` e não `type="number"`: o input numérico
   * nativo muda de valor com a roda do mouse sobre o campo (perda silenciosa do
   * que foi digitado) e entrega `''` para qualquer texto inválido, apagando o
   * "7," no meio da digitação de "7,5". Aqui o campo continua sendo texto e quem
   * filtra é `digitacaoNumerica`.
   */
  inputMode?: 'numeric' | 'decimal';
  /**
   * Teclado do próprio campo — usado pelo `OwnerField` para navegar nas
   * sugestões (↑ ↓ Enter Esc). Vive aqui, e não num `<div>` em volta esperando o
   * evento borbulhar: `<div onKeyDown>` é elemento não interativo com handler de
   * teclado (smell S6848), e o foco está no `<input>` de qualquer forma.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

function Input({
  value,
  onChange,
  placeholder,
  dark,
  accent = NOC_RED,
  mono,
  prefix,
  suffix,
  invalid,
  readOnly,
  list,
  inputMode,
  onKeyDown,
  ...aria
}: Readonly<InputProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: byViewport(vp, CONTROL_H),
        padding: '0 10px',
        background: readOnly ? p.surface2 : p.inputBg,
        borderRadius: 7,
        fontSize: 12.5,
        color: p.text,
        fontFamily: mono ? fontMono : fontSans,
        ...frameStyle(dark, accent, focused && !readOnly, invalid),
      }}
    >
      {prefix && (
        <span style={{ color: p.muted, marginRight: 8, fontSize: 11, fontFamily: fontMono }}>
          {prefix}
        </span>
      )}
      <input
        {...aria}
        list={list}
        inputMode={inputMode}
        value={value || ''}
        readOnly={readOnly}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          // No compacto o `<input>` estica até a altura da moldura: o container
          // centraliza o conteúdo, então a caixa clicável media 19px de altura —
          // metade do mínimo de 40px de um alvo de toque. O texto de um input de
          // uma linha continua centralizado pelo navegador, logo o desenho é o
          // mesmo; no desktop a propriedade nem é emitida.
          alignSelf: vp.compact ? 'stretch' : undefined,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: readOnly ? p.textMuted : p.text,
          fontSize: 12.5,
          fontFamily: mono ? fontMono : fontSans,
          cursor: readOnly ? 'default' : 'text',
        }}
      />
      {suffix && (
        <span style={{ color: p.muted, fontSize: 11, fontFamily: fontMono }}>{suffix}</span>
      )}
    </div>
  );
}

interface TextAreaProps extends FieldAria {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  dark: boolean;
  accent?: string;
  rows?: number;
  invalid?: boolean;
}

function TextArea({
  value,
  onChange,
  placeholder,
  dark,
  accent = NOC_RED,
  rows = 3,
  invalid,
  ...aria
}: Readonly<TextAreaProps>) {
  const p = makePalette(dark);
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...aria}
      value={value || ''}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: p.inputBg,
        borderRadius: 7,
        fontSize: 12.5,
        color: p.text,
        fontFamily: fontSans,
        lineHeight: 1.5,
        outline: 'none',
        resize: 'vertical',
        ...frameStyle(dark, accent, focused, invalid),
      }}
    />
  );
}

// ─── Select ───────────────────────────────────
interface SelectProps extends FieldAria {
  value?: string;
  onChange?: (v: string) => void;
  /** Aceita `string[]` (valor = label) ou `{value,label}[]` (valor = enum). */
  options?: Array<string | SelectOption>;
  dark: boolean;
  accent?: string;
  placeholder?: string;
  invalid?: boolean;
}

/** Iniciais para o avatar do colaborador: primeira + última palavra do nome. */
function personInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Realce da sugestão sob o cursor/teclado. Fora do JSX porque a escolha depende
 * de duas condições (é a linha ativa? o tema é escuro?) e, aninhada no `style`,
 * virava ternário dentro de ternário — ilegível e o smell S3358.
 */
function hotBackground(accent: string, dark: boolean): string {
  return dark ? `${accent}1a` : `${accent}0d`;
}

/**
 * Responsável pela aplicação: busca livre na base do RH (espelho TOTVS). A
 * auditoria exige uma pessoa nomeada da Masterboi, então o campo sugere gente
 * que existe de verdade; o texto segue editável para quem já sabe o nome ou
 * para casos que o RH não cobre.
 *
 * O painel de sugestões é o DropdownPanel da casa — era um `<datalist>`, cujo
 * dropdown é UI crua do navegador (sem tema, sem avatar, um visual em cada
 * SO). Aqui cada sugestão traz avatar de iniciais, nome, função · setor e a
 * matrícula, com teclado (↑ ↓ Enter Esc) e estados de busca/vazio.
 */
function OwnerField({
  value,
  onChange,
  dark,
  accent = NOC_RED,
  invalid,
  ...aria
}: Readonly<Omit<InputProps, 'list' | 'prefix' | 'suffix' | 'mono' | 'readOnly'>>) {
  const p = makePalette(dark);
  const search = useCollaboratorSearch(value ?? '');
  const people = search.data ?? [];
  const term = (value ?? '').trim();
  const searching = term.length >= 2;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // Nome recém-escolhido: segura o dropdown para ele não reabrir por causa do
  // onChange disparado pela própria seleção.
  const pickedRef = useRef<string | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const dismiss = useCallback((_fromKeyboard: boolean) => {
    setOpen(false);
    setActive(-1);
  }, []);
  const pos = useDropdown(open, dismiss, anchorRef, 'anchor', 316);

  const commit = (i: number) => {
    const person = people[i];
    if (person) {
      pickedRef.current = person.nome;
      onChange?.(person.nome);
    }
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!searching) return;
    comboboxKeyDown(e, {
      open,
      count: people.length,
      selectedIndex: -1,
      active,
      setActive,
      setOpen,
      commit,
      homeEnd: false,
      // O gatilho é um <input> de texto: espaço digita, não comanda o popup.
      textInput: true,
    });
  };

  return (
    <>
      {/* Só ancoragem do painel: o teclado é do próprio <Input> (prop onKeyDown),
          porque <div onKeyDown> é elemento não interativo com handler (S6848). */}
      <div ref={anchorRef}>
        <Input
          {...aria}
          onKeyDown={onKeyDown}
          value={value}
          onChange={(v) => {
            // Digitação normal abre a busca; o echo da seleção não.
            if (pickedRef.current !== v) {
              pickedRef.current = null;
              setOpen(v.trim().length >= 2);
              setActive(-1);
            }
            onChange?.(v);
          }}
          placeholder="Nome do responsável…"
          dark={dark}
          accent={accent}
          invalid={invalid}
        />
      </div>

      {open && searching && (
        <DropdownPanel pos={pos} dark={dark} onDismiss={() => dismiss(false)}>
          {/* Cabeçalho: diz de onde vêm as sugestões */}
          <div
            style={{
              padding: '7px 12px',
              borderBottom: `1px solid ${p.border}`,
              ...rotuloCaixaAlta,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: p.muted,
              flexShrink: 0,
            }}
          >
            Base do RH · Totvs
          </div>

          <div id={listId} role="listbox" aria-label="Colaboradores" style={{ overflowY: 'auto' }}>
            {people.map((person, i) => {
              const hot = i === active;
              return (
                <div
                  key={person.matricula}
                  role="option"
                  aria-selected={hot}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                  onClick={() => commit(i)}
                  onKeyDown={activateOnKey(() => commit(i))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: hot ? hotBackground(accent, dark) : 'transparent',
                    borderLeft: `2px solid ${hot ? accent : 'transparent'}`,
                    transition: 'background 80ms',
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: dark ? `${accent}26` : `${accent}14`,
                      color: accent,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10.5,
                      fontWeight: 700,
                      flexShrink: 0,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {personInitials(person.nome)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: p.text,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {person.nome}
                    </span>
                    {(person.funcao || person.setorNome) && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 11,
                          color: p.muted,
                          marginTop: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {[person.funcao, person.setorNome].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      fontFamily: fontMono,
                      color: p.muted,
                      background: p.surface2,
                      border: `1px solid ${p.border}`,
                      borderRadius: 4,
                      padding: '2px 6px',
                      flexShrink: 0,
                    }}
                  >
                    {person.matricula}
                  </span>
                </div>
              );
            })}

            {people.length === 0 && (
              <div
                style={{
                  padding: '14px 12px',
                  fontSize: 12,
                  color: p.muted,
                  textAlign: 'center',
                }}
              >
                {search.isFetching
                  ? 'Buscando na base do RH…'
                  : `Ninguém encontrado para “${term}”.`}
              </div>
            )}
          </div>
        </DropdownPanel>
      )}
    </>
  );
}

function Select({
  value,
  onChange,
  options,
  dark,
  accent = NOC_RED,
  placeholder,
  invalid,
  ...aria
}: Readonly<SelectProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const opts = useMemo(() => normalizeOptions(options), [options]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  // `role="combobox"` exige aria-controls apontando a lista que ele comanda.
  const listId = useId();

  const selectedIndex = opts.findIndex((o) => o.value === value);
  const label = selectedIndex >= 0 ? opts[selectedIndex].label : '';

  const dismiss = useCallback((fromKeyboard: boolean) => {
    setOpen(false);
    setActive(-1);
    if (fromKeyboard) btnRef.current?.focus();
  }, []);
  const pos = useDropdown(open, dismiss, btnRef, 'anchor', 280);

  const commit = (i: number) => {
    const o = opts[i];
    if (o) onChange?.(o.value);
    setOpen(false);
    setActive(-1);
    btnRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) =>
    comboboxKeyDown(e, {
      open,
      count: opts.length,
      selectedIndex,
      active,
      setActive,
      setOpen,
      commit,
      homeEnd: true,
      // O gatilho é um <button>: espaço abre/confirma, como manda o ARIA.
      textInput: false,
    });

  return (
    <>
      <button
        {...aria}
        ref={btnRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        onClick={() => {
          setActive(Math.max(selectedIndex, 0));
          setOpen((v) => !v);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          height: byViewport(vp, CONTROL_H),
          padding: '0 10px',
          background: p.inputBg,
          borderRadius: 7,
          fontSize: 12.5,
          color: label ? p.text : p.placeholder,
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: fontSans,
          ...frameStyle(dark, accent, focused || open, invalid),
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>{label || placeholder || 'Selecione…'}</span>
        <IconChevronD size={14} style={{ color: p.muted, flexShrink: 0 }} />
      </button>

      {open && (
        <DropdownPanel pos={pos} dark={dark} onDismiss={() => dismiss(false)}>
          <OptionList
            id={listId}
            opts={opts}
            value={value}
            active={active}
            dark={dark}
            accent={accent}
            onPick={commit}
            onActive={setActive}
            label="Opções"
          />
        </DropdownPanel>
      )}
    </>
  );
}

// ─── URL com seletor de site (SharePoint / GitHub) ──
interface SiteUrlInputProps extends FieldAria {
  site: string;
  onSite?: (v: string) => void;
  value?: string;
  onChange?: (v: string) => void;
  dark: boolean;
  accent?: string;
  placeholder?: string;
  invalid?: boolean;
}

function SiteUrlInput({
  site,
  onSite,
  value,
  onChange,
  dark,
  accent = NOC_RED,
  placeholder,
  invalid,
  ...aria
}: Readonly<SiteUrlInputProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  // `role="combobox"` exige aria-controls apontando a lista que ele comanda.
  const listId = useId();
  const cfg = DOC_SITES[site] ?? FALLBACK_SITE;

  /**
   * No celular a linha vira três faixas empilhadas. Medido a 375px: a coluna do
   * formulário tem ~275px úteis e a linha pede 328px (seletor de site 113 +
   * prefixo do host 175 + campo). A alternativa era encolher o host com
   * reticências, que esconderia justamente o pedaço da URL que o usuário precisa
   * conferir; empilhar não perde informação nenhuma.
   */
  const stacked = vp.mobile;
  const rowHeight = byViewport(vp, CONTROL_H);

  const selectedIndex = SITE_OPTIONS.findIndex((o) => o.value === site);

  const dismiss = useCallback((fromKeyboard: boolean) => {
    setOpen(false);
    setActive(-1);
    if (fromKeyboard) btnRef.current?.focus();
  }, []);
  const pos = useDropdown(open, dismiss, btnRef, 180, 160);

  const commit = (i: number) => {
    const o = SITE_OPTIONS[i];
    if (o) onSite?.(o.value);
    setOpen(false);
    setActive(-1);
    btnRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) =>
    comboboxKeyDown(e, {
      open,
      count: SITE_OPTIONS.length,
      selectedIndex,
      active,
      setActive,
      setOpen,
      commit,
      // o seletor de site nunca atendeu Home/End
      homeEnd: false,
      // O gatilho é um <button>: espaço abre/confirma, como manda o ARIA.
      textInput: false,
    });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'stretch',
        height: stacked ? 'auto' : rowHeight,
        background: p.inputBg,
        borderRadius: 7,
        ...frameStyle(dark, accent, focused || open, invalid),
      }}
    >
      <button
        ref={btnRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={`Site do link · ${site}`}
        onClick={() => {
          setActive(Math.max(selectedIndex, 0));
          setOpen((v) => !v);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          height: stacked ? rowHeight : '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 9px 0 10px',
          background: 'transparent',
          border: 'none',
          // Empilhado, a divisória deixa de ser vertical e passa a ser horizontal.
          borderRight: stacked ? 'none' : `1px solid ${p.border}`,
          borderBottom: stacked ? `1px solid ${p.border}` : 'none',
          cursor: 'pointer',
          fontFamily: fontSans,
          fontSize: 11.5,
          fontWeight: 600,
          color: p.text,
          borderTopLeftRadius: 7,
          borderBottomLeftRadius: stacked ? 0 : 7,
          borderTopRightRadius: stacked ? 7 : 0,
          flexShrink: 0,
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: 2, background: cfg.color }} />
        {site}
        <IconChevronD size={13} style={{ color: p.muted }} />
      </button>

      {open && (
        <DropdownPanel pos={pos} dark={dark} onDismiss={() => dismiss(false)}>
          <OptionList
            id={listId}
            opts={SITE_OPTIONS}
            value={site}
            active={active}
            dark={dark}
            accent={accent}
            dotOf={(o) => DOC_SITES[o.value]?.color}
            onPick={commit}
            onActive={setActive}
            label="Site do link"
          />
        </DropdownPanel>
      )}

      {/* Modo "URL completa" não tem host para prefixar: o campo recebe a URL toda. */}
      {cfg.host !== '' && (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 10,
            paddingTop: stacked ? 6 : 0,
            paddingBottom: stacked ? 6 : 0,
            borderBottom: stacked ? `1px solid ${p.border}` : 'none',
            color: p.muted,
            fontSize: 11,
            fontFamily: fontMono,
            whiteSpace: 'nowrap',
          }}
        >
          {cfg.host}/
        </span>
      )}
      <input
        {...aria}
        value={value || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          // Colou a URL inteira? O site troca sozinho e o campo fica só com o
          // caminho — colar link do GitHub em modo SharePoint não monta mais
          // um endereço híbrido sem sentido.
          const raw = e.target.value;
          const smart = smartDocSplit(raw);
          if (smart) {
            onSite?.(smart.site);
            onChange?.(smart.path);
          } else {
            onChange?.(raw);
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={cfg.host === '' ? 'https://exemplo.com/caminho/do/documento' : placeholder}
        style={{
          // `flex: 1` empilhado seria `flex-basis: 0` no eixo VERTICAL, e a base
          // vence o `height`: o campo colapsava para os 17px da linha de texto
          // (medido). Empilhado ele é item de tamanho fixo.
          flex: stacked ? 'none' : 1,
          minWidth: 0,
          // Na coluna o eixo transversal é o horizontal: a altura deixa de vir do
          // `stretch` e precisa ser declarada.
          height: stacked ? rowHeight : undefined,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: p.text,
          fontSize: 12.5,
          fontFamily: fontMono,
          padding: stacked || cfg.host === '' ? '0 10px' : '0 10px 0 2px',
        }}
      />
    </div>
  );
}

// ─── Tecnologias · chips + base de conhecimento ──
interface StackChip {
  name: string;
  /** Posição na lista · o botão de remover trabalha por índice. */
  index: number;
  /** Chave de render estável, derivada do conteúdo e não da posição. */
  key: string;
}

/**
 * Chips das tecnologias escolhidas. A chave é o nome em minúsculas — o seletor
 * deduplica sem diferenciar caixa — com sufixo de ocorrência para o caso de uma
 * lista gravada fora desta tela trazer repetição.
 */
function stackChips(items: readonly string[]): StackChip[] {
  const seen = new Map<string, number>();
  return items.map((name, index) => {
    const base = name.toLowerCase();
    const before = seen.get(base) ?? 0;
    seen.set(base, before + 1);
    return { name, index, key: before === 0 ? base : `${base}~${before}` };
  });
}

/** Contador ao lado do gatilho: "nenhuma tecnologia" · "1 tecnologia" · "N tecnologias". */
function stackCountLabel(count: number): string {
  if (count === 0) return 'nenhuma tecnologia';
  const noun = count === 1 ? 'tecnologia' : 'tecnologias';
  return `${count} ${noun}`;
}

interface StackGroup {
  label: string;
  items: readonly string[];
}

/**
 * Catálogo agrupado por categoria. Sem busca é o catálogo completo; com busca são
 * só os acertos, ainda agrupados, na ordem de relevância do catálogo.
 */
function groupStacks(query: string): StackGroup[] {
  const q = query.trim();
  if (!q) return STACK_CATALOG.map((c) => ({ label: c.label, items: c.items }));
  const byLabel = new Map<string, string[]>();
  for (const hit of searchStacks(q, 80)) {
    const label = stackCategoryOf(hit) ?? 'Outras';
    const list = byLabel.get(label);
    if (list) list.push(hit);
    else byLabel.set(label, [hit]);
  }
  return STACK_CATALOG.filter((c) => byLabel.has(c.label)).map((c) => ({
    label: c.label,
    items: byLabel.get(c.label) ?? [],
  }));
}

interface StackSearchKeys {
  rowCount: number;
  flat: readonly string[];
  active: number;
  freeText: string;
  showFree: boolean;
  setActive: (next: number | ((i: number) => number)) => void;
  add: (name: string) => void;
}

/**
 * Teclado da busca de tecnologias: setas percorrem as linhas visíveis (inclusive
 * a de texto livre) e Enter adiciona a linha em destaque — ou o próprio texto
 * digitado quando nada do catálogo está em destaque.
 */
function stackSearchKeyDown(e: React.KeyboardEvent, ctx: StackSearchKeys): void {
  const { rowCount, flat, active, freeText, showFree, setActive, add } = ctx;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    setActive((i) => (rowCount === 0 ? 0 : stepIndex(i, rowCount, 1)));
    return;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    setActive((i) => (rowCount === 0 ? 0 : stepIndex(i, rowCount, -1)));
    return;
  }
  if (e.key !== 'Enter') return;
  e.preventDefault();
  if (showFree && active >= flat.length) add(freeText);
  else if (flat[active]) add(flat[active]);
  else if (freeText) add(freeText);
}

interface StackOptionProps {
  name: string;
  /** Já escolhida · a linha fica apagada e não responde ao clique. */
  selected: boolean;
  active: boolean;
  activeRef: RefObject<HTMLDivElement | null>;
  onAdd: () => void;
  onHover: () => void;
  dark: boolean;
  accent: string;
}

/** Uma tecnologia do catálogo dentro do painel de busca. */
function StackOption({
  name,
  selected,
  active,
  activeRef,
  onAdd,
  onHover,
  dark,
  accent,
}: Readonly<StackOptionProps>) {
  const p = makePalette(dark);
  return (
    <div
      ref={active ? activeRef : undefined}
      role="option"
      aria-selected={selected}
      aria-disabled={selected}
      tabIndex={-1}
      onClick={onAdd}
      onKeyDown={activateOnKey(onAdd)}
      onMouseEnter={onHover}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 9px',
        borderRadius: 5,
        fontSize: 12.5,
        fontFamily: fontSans,
        color: selected ? p.muted : p.text,
        cursor: selected ? 'default' : 'pointer',
        background: active && !selected ? p.surface2 : 'transparent',
        opacity: selected ? 0.65 : 1,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: STACK_COLORS[name] || '#475569',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>{name}</span>
      {selected && <IconCheck size={13} style={{ color: accent }} />}
    </div>
  );
}

interface StackFreeOptionProps {
  text: string;
  activeRef: RefObject<HTMLDivElement | null>;
  onAdd: () => void;
  dark: boolean;
  accent: string;
}

/** Linha de escape: nada da base casou com a busca, cadastra como texto livre. */
function StackFreeOption({ text, activeRef, onAdd, dark, accent }: Readonly<StackFreeOptionProps>) {
  const p = makePalette(dark);
  return (
    <div
      ref={activeRef}
      role="option"
      aria-selected={false}
      tabIndex={-1}
      onClick={onAdd}
      onKeyDown={activateOnKey(onAdd)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: 4,
        padding: '9px 10px',
        borderRadius: 6,
        fontSize: 12.5,
        fontFamily: fontSans,
        color: p.text,
        cursor: 'pointer',
        background: dark ? `${accent}1a` : `${accent}0d`,
        border: `1px dashed ${accent}66`,
      }}
    >
      <span style={{ color: accent, fontWeight: 700 }}>+</span>
      <span style={{ flex: 1, minWidth: 0 }}>Adicionar «{text}» como nova tecnologia</span>
    </div>
  );
}

interface StackPickerProps extends FieldAria {
  value: string[];
  onChange: (next: string[]) => void;
  dark: boolean;
  accent: string;
  invalid?: boolean;
}

function StackPicker({
  value,
  onChange,
  dark,
  accent,
  invalid,
  ...aria
}: Readonly<StackPickerProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  const dismiss = useCallback((fromKeyboard: boolean) => {
    setOpen(false);
    setQuery('');
    setActive(0);
    if (fromKeyboard) btnRef.current?.focus();
  }, []);
  const pos = useDropdown(open, dismiss, btnRef, 360, 400);

  // Dedupe sem diferenciar caixa: "React" e "react" são a mesma tecnologia.
  const selectedKeys = useMemo(() => new Set(value.map((v) => v.trim().toLowerCase())), [value]);

  const groups = useMemo(() => groupStacks(query), [query]);
  const chips = useMemo(() => stackChips(value), [value]);

  // Tons derivados do accent, calculados fora dos templates de estilo.
  const chipBg = dark ? `${accent}22` : `${accent}12`;
  const chipBorder = dark ? `${accent}44` : `${accent}33`;
  const triggerOpenBg = dark ? `${accent}1e` : `${accent}0f`;

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const indexOfName = useMemo(() => new Map(flat.map((n, i) => [n, i])), [flat]);
  const freeText = query.trim();
  // Nada na base casou com a busca: oferece cadastrar como texto livre.
  const showFree = freeText.length > 0 && flat.length === 0;
  const rowCount = flat.length + (showFree ? 1 : 0);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (active >= 0) activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const add = (name: string) => {
    const clean = name.trim();
    if (!clean || selectedKeys.has(clean.toLowerCase())) return;
    onChange([...value, clean]);
    if (query) {
      setQuery('');
      searchRef.current?.focus();
    }
  };

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  const onSearchKey = (e: React.KeyboardEvent) =>
    stackSearchKeyDown(e, { rowCount, flat, active, freeText, showFree, setActive, add });

  const count = value.length;

  return (
    <>
      {/* Chips selecionados */}
      <div
        style={{
          minHeight: 76,
          padding: 10,
          background: p.inputBg,
          borderRadius: 7,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          ...frameStyle(dark, accent, open, invalid),
        }}
      >
        {count === 0 && (
          <span
            style={{
              fontSize: 11.5,
              color: p.placeholder,
              fontFamily: fontSans,
              padding: '4px 2px',
              lineHeight: 1.5,
            }}
          >
            Nenhuma tecnologia selecionada. Use o botão abaixo para escolher na base de
            conhecimento.
          </span>
        )}
        {chips.map((chip) => (
          <span
            key={chip.key}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 4px 4px 8px',
              borderRadius: 5,
              fontSize: 11.5,
              fontWeight: 500,
              background: chipBg,
              color: p.text,
              border: `1px solid ${chipBorder}`,
              fontFamily: fontSans,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background: STACK_COLORS[chip.name] || '#475569',
                flexShrink: 0,
              }}
            />
            {chip.name}
            <button
              type="button"
              aria-label={`Remover ${chip.name}`}
              onClick={() => removeAt(chip.index)}
              style={{
                width: 18,
                height: 18,
                borderRadius: 3,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: p.muted,
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
            >
              <IconX size={11} />
            </button>
          </span>
        ))}
      </div>

      {/* Seletor */}
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <button
          {...aria}
          ref={btnRef}
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((v) => !v)}
          style={{
            height: byViewport(vp, SMALL_BTN_H),
            padding: '0 12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            background: open ? triggerOpenBg : p.surface2,
            border: `1px dashed ${open ? accent : p.borderStrong}`,
            borderRadius: 7,
            color: open ? accent : p.text,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: fontSans,
            cursor: 'pointer',
          }}
        >
          {/* Sem espaço entre o "+" e o texto: o gap do flex é que separa os dois. */}
          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>Adicionar tecnologia
        </button>
        <span style={{ fontSize: 11, color: p.muted, fontFamily: fontSans }}>
          {stackCountLabel(count)}
        </span>
      </div>

      {open && (
        <DropdownPanel pos={pos} dark={dark} onDismiss={() => dismiss(false)}>
          {/* Busca */}
          <div style={{ padding: 8, borderBottom: `1px solid ${p.border}`, flexShrink: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 32,
                padding: '0 9px',
                background: p.inputBg,
                borderRadius: 6,
                ...frameStyle(dark, accent, searchFocused),
              }}
            >
              <IconSearch size={13} style={{ color: p.muted, flexShrink: 0 }} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={onSearchKey}
                placeholder="Buscar tecnologia…"
                aria-label="Buscar tecnologia"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: p.text,
                  fontSize: 12.5,
                  fontFamily: fontSans,
                }}
              />
            </div>
          </div>

          {/* Lista agrupada por categoria */}
          <div
            role="listbox"
            aria-label="Tecnologias"
            style={{ overflowY: 'auto', minHeight: 0, padding: 4 }}
          >
            {groups.map((g) => (
              <div key={g.label} style={{ marginBottom: 2 }}>
                <div
                  style={{
                    padding: '8px 8px 4px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: p.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    fontFamily: fontSans,
                  }}
                >
                  {g.label}
                </div>
                {g.items.map((name) => {
                  const i = indexOfName.get(name) ?? -1;
                  return (
                    <StackOption
                      key={name}
                      name={name}
                      selected={selectedKeys.has(name.toLowerCase())}
                      active={i === active}
                      activeRef={activeRef}
                      onAdd={() => add(name)}
                      onHover={() => setActive(i)}
                      dark={dark}
                      accent={accent}
                    />
                  );
                })}
              </div>
            ))}

            {showFree && (
              <StackFreeOption
                text={freeText}
                activeRef={activeRef}
                onAdd={() => add(freeText)}
                dark={dark}
                accent={accent}
              />
            )}
          </div>

          <div
            style={{
              padding: '7px 10px',
              borderTop: `1px solid ${p.border}`,
              background: p.surface2,
              fontSize: 10.5,
              color: p.muted,
              fontFamily: fontSans,
              flexShrink: 0,
            }}
          >
            Setas para navegar · Enter para adicionar · Esc para fechar
          </div>
        </DropdownPanel>
      )}
    </>
  );
}

// ─── Seção do formulário ──────────────────────
interface FormSectionProps {
  step: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
  dark: boolean;
  accent: string;
}

function FormSection({
  step,
  title,
  subtitle,
  children,
  dark,
  accent,
}: Readonly<FormSectionProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        // 24px de respiro de cada lado custam 48px dos ~270px que sobram a 320px:
        // quase um quinto da tela só de moldura.
        padding: byViewport(vp, { mobile: 14, tablet: 18, desktop: 24 }),
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: byViewport(vp, { mobile: 10, tablet: 14, desktop: 14 }),
          marginBottom: byViewport(vp, { mobile: 16, tablet: 20, desktop: 20 }),
          paddingBottom: 16,
          borderBottom: `1px solid ${p.border}`,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
            color: '#fff',
            fontSize: 13,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...numeroTabular,
          }}
        >
          {step}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: p.text,
              letterSpacing: '-0.012em',
              marginBottom: 3,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 12, color: p.muted, fontFamily: fontSans }}>{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Aviso discreto (não é erro) ──────────────
interface NoticeProps {
  color: string;
  icon: ReactNode;
  children: ReactNode;
  dark: boolean;
}

function Notice({ color, icon, children, dark }: Readonly<NoticeProps>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        background: dark ? `${color}18` : `${color}0d`,
        border: `1px solid ${color}44`,
        fontSize: 11.5,
        lineHeight: 1.55,
        color: p.textMuted,
        fontFamily: fontSans,
      }}
    >
      <span style={{ color, marginTop: 1, flexShrink: 0, display: 'inline-flex' }}>{icon}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

// ─── Interruptor ──────────────────────────────
interface ToggleRowProps {
  checked: boolean;
  onToggle: () => void;
  label: string;
  sub: string;
  dark: boolean;
  accent: string;
  info?: ReactNode;
}

function ToggleRow({
  checked,
  onToggle,
  label,
  sub,
  dark,
  accent,
  info,
}: Readonly<ToggleRowProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const onBg = dark ? `${accent}11` : `${accent}08`;
  const borderColor = checked ? `${accent}55` : p.border;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: checked ? onBg : p.surface2,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        style={{
          flex: 1,
          minWidth: 0,
          // Mesma razão do checklist de LGPD: rótulo + subtítulo param em 37px.
          minHeight: vp.compact ? 40 : undefined,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 0,
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: fontSans,
          color: p.text,
        }}
      >
        <span
          style={{
            position: 'relative',
            width: 36,
            height: 20,
            borderRadius: 10,
            background: checked ? accent : p.borderStrong,
            flexShrink: 0,
            transition: 'background 140ms',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: checked ? 18 : 2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.28)',
              transition: 'left 140ms',
            }}
          />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: p.text }}>
            {label}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 11,
              color: p.muted,
              marginTop: 2,
              fontFamily: fontSans,
            }}
          >
            {sub}
          </span>
        </span>
      </button>
      {info}
    </div>
  );
}

// ─── Etapa de acesso · unidades / setores / pessoas ──
const ACCESS_INFO: Record<'acesso' | 'confidencialidade', InfoEntry> = {
  acesso: {
    title: 'Acesso e confidencialidade',
    subtitle: 'Quem consegue visualizar a aplicação',
    sections: [
      {
        h: 'Como funciona',
        b: 'A visibilidade define a regra geral e esta etapa define o alcance. Em aplicações privadas o acesso é montado de cima para baixo: primeiro as unidades (filiais), depois os setores dessas unidades e, se necessário, as pessoas específicas.',
      },
      {
        h: 'Origem dos dados',
        list: [
          'Unidades, setores e colaboradores vêm do cadastro de RH do TOTVS',
          'O mesmo setor pode existir em mais de uma unidade · a lista mostra onde',
          'Desligamentos saem da lista no próximo espelhamento',
        ],
      },
      {
        h: 'Auditoria',
        b: 'O acesso concedido é registrado junto da aplicação e serve de evidência do controle de acesso lógico exigido pela auditoria de TI.',
      },
    ],
  },
  confidencialidade: {
    title: 'Confidencialidade por colaborador',
    subtitle: 'Restrição nominal dentro dos setores',
    sections: [
      {
        h: 'Quando usar',
        b: 'Somente quando o setor inteiro não pode enxergar a aplicação: folha de pagamento, jurídico, fusões e aquisições, dados de auditoria interna.',
      },
      {
        h: 'Efeito prático',
        list: [
          'Desligado · todos os colaboradores dos setores escolhidos enxergam',
          'Ligado · apenas os colaboradores marcados enxergam',
          'A lista de pessoas respeita as unidades e os setores já escolhidos',
        ],
      },
      {
        h: 'Cuidado',
        b: 'Lista nominal exige manutenção: quem trocar de setor ou sair da empresa precisa ser revisado. Prefira restringir por setor sempre que der.',
      },
    ],
  },
};

/** "412 pessoas" · usado como detalhe à direita nas opções. */
function peopleLabel(total: number): string {
  return `${total.toLocaleString('pt-BR')} ${total === 1 ? 'pessoa' : 'pessoas'}`;
}

interface AccessPatch {
  units?: string[];
  sectors?: string[];
  collaborators?: string[];
  perCollaborator?: boolean;
}

/** Unidades do RH como opções · razões sociais extras só quando há mais de uma. */
function toUnitOptions(
  units: readonly { codFilial: string; nome: string; nomes: readonly string[]; total: number }[],
): MultiSelectOption[] {
  return units.map((u) => ({
    value: u.codFilial,
    label: u.nome,
    hint: peopleLabel(u.total),
    meta: u.nomes.length > 1 ? [...u.nomes] : undefined,
  }));
}

/** Setores do RH como opções · o badge mostra em quais unidades existem. */
function toSectorOptions(
  sectors: readonly { setorNome: string; unidades: readonly { nome: string }[]; total: number }[],
): MultiSelectOption[] {
  return sectors.map((sector) => ({
    value: sector.setorNome,
    label: sector.setorNome,
    hint: peopleLabel(sector.total),
    meta: sector.unidades.map((u) => u.nome),
  }));
}

/** Colaboradores como opções · função e setor/filial no detalhe. */
function toPersonOptions(people: readonly AccessCollaborator[]): MultiSelectOption[] {
  return people.map((c) => ({
    value: c.matricula,
    label: c.nome,
    hint: c.funcao ?? c.matricula,
    meta: [...new Set([c.setorNome, c.filial].filter((m): m is string => Boolean(m)))],
  }));
}

/** Tira da lista quem não pertence mais às unidades/setores escolhidos. */
function prunePeople(
  list: readonly string[],
  known: ReadonlyMap<string, AccessCollaborator>,
  allowedUnits: ReadonlySet<string>,
  allowedSectors: ReadonlySet<string>,
): string[] {
  return list.filter((matricula) => {
    const rec = known.get(matricula);
    // sem o registro em mãos não há como julgar · o servidor valida
    if (!rec) return true;
    const unitOk = !rec.codFilial || allowedUnits.has(rec.codFilial);
    const sectorOk = !rec.setorNome || allowedSectors.has(rec.setorNome);
    return unitOk && sectorOk;
  });
}

interface AccessSummaryLineProps {
  form: FormState;
  dark: boolean;
  accent: string;
}

/** Resumo do que está liberado, no pé da etapa de acesso. */
function AccessSummaryLine({ form, dark, accent }: Readonly<AccessSummaryLineProps>) {
  const p = makePalette(dark);
  const scope = form.perCollaborator
    ? ' · somente as pessoas marcadas enxergam'
    : ' · todos os colaboradores desses setores enxergam';
  return (
    <div
      style={{
        fontSize: 11,
        color: p.muted,
        fontFamily: fontSans,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        lineHeight: 1.45,
      }}
    >
      <IconUsers size={12} style={{ color: accent, flexShrink: 0 }} />
      <span>
        {form.accessUnits.length === 0
          ? 'Nada liberado ainda: escolha as unidades e os setores que poderão visualizar.'
          : `${accessSummary(form)}${scope}`}
      </span>
    </div>
  );
}

interface AccessFieldsProps {
  form: FormState;
  onPatch: (patch: AccessPatch) => void;
  errors: FieldErrors;
  dark: boolean;
  accent: string;
}

/**
 * Falha de CARREGAMENTO das opções, mostrada no próprio campo.
 *
 * Antes a mensagem só existia dentro do painel aberto: o campo fechado ficava
 * idêntico a "nada selecionado" e o usuário concluía que a lista estava vazia.
 * Agora o campo ganha borda de atenção (prop `invalid` do MultiSelect) e esta
 * linha abaixo, com o retry do próprio hook de dados.
 */
interface AccessLoadErrorProps {
  message: string;
  onRetry: () => void;
  retrying: boolean;
  dark: boolean;
}

function AccessLoadError({ message, onRetry, retrying, dark }: Readonly<AccessLoadErrorProps>) {
  const p = makePalette(dark);
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 6,
        fontSize: 11,
        fontWeight: 600,
        color: DANGER,
        fontFamily: fontSans,
        lineHeight: 1.45,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ minWidth: 0 }}>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        style={{
          height: 24,
          padding: '0 9px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'transparent',
          border: `1px solid ${p.borderStrong}`,
          borderRadius: 5,
          color: p.textMuted,
          fontSize: 10.5,
          fontWeight: 700,
          fontFamily: fontSans,
          cursor: retrying ? 'progress' : 'pointer',
          opacity: retrying ? 0.6 : 1,
        }}
      >
        <IconRefresh size={11} />
        {retrying ? 'Tentando…' : 'Tentar novamente'}
      </button>
    </div>
  );
}

/**
 * Controles de acesso da aplicação privada. Vive aqui (e não no corpo da tela)
 * porque as regras de coerência dependem dos dados carregados: quais unidades
 * possuem cada setor e a qual setor/unidade cada colaborador pertence.
 */
function AccessFields({ form, onPatch, errors, dark, accent }: Readonly<AccessFieldsProps>) {
  const vp = useViewport();
  const [personQuery, setPersonQuery] = useState('');

  const units = useUnits();
  const sectors = useSectors(form.accessUnits);
  const collaborators = useCollaborators({
    units: form.accessUnits,
    sectors: form.accessSectors,
    search: personQuery,
    enabled: form.perCollaborator,
  });

  // Índices do que já foi carregado · sustentam a poda de itens órfãos mesmo
  // depois de a lista visível ter mudado (busca no servidor troca as opções).
  const sectorOwners = useRef(new Map<string, string[]>());
  useEffect(() => {
    for (const s of sectors.data ?? []) {
      sectorOwners.current.set(
        s.setorNome,
        s.unidades.map((u) => u.codFilial),
      );
    }
  }, [sectors.data]);

  const knownPeople = useRef(new Map<string, AccessCollaborator>());
  useEffect(() => {
    for (const c of collaborators.data ?? []) knownPeople.current.set(c.matricula, c);
  }, [collaborators.data]);

  const unitOptions = useMemo<MultiSelectOption[]>(
    () => toUnitOptions(units.data ?? []),
    [units.data],
  );

  const sectorOptions = useMemo<MultiSelectOption[]>(
    () => toSectorOptions(sectors.data ?? []),
    [sectors.data],
  );

  const personOptions = useMemo<MultiSelectOption[]>(
    () => toPersonOptions(collaborators.data ?? []),
    [collaborators.data],
  );

  // Remover uma unidade derruba os setores que só existiam nela e, em cascata,
  // os colaboradores que ficaram órfãos.
  const changeUnits = (next: string[]) => {
    const allowedUnits = new Set(next);
    const keptSectors = form.accessSectors.filter((name) => {
      const owners = sectorOwners.current.get(name);
      if (!owners) return true;
      return owners.some((cod) => allowedUnits.has(cod));
    });
    onPatch({
      units: next,
      sectors: keptSectors,
      collaborators: prunePeople(
        form.accessCollaborators,
        knownPeople.current,
        allowedUnits,
        new Set(keptSectors),
      ),
    });
  };

  const changeSectors = (next: string[]) => {
    onPatch({
      sectors: next,
      collaborators: prunePeople(
        form.accessCollaborators,
        knownPeople.current,
        new Set(form.accessUnits),
        new Set(next),
      ),
    });
  };

  // Desligar o toggle limpa a lista nominal · guardá-la enviaria acesso que a
  // tela não mostra mais.
  const togglePerCollaborator = () => {
    const next = !form.perCollaborator;
    onPatch({ perCollaborator: next, collaborators: next ? form.accessCollaborators : [] });
    if (!next) setPersonQuery('');
  };

  const noUnits = form.accessUnits.length === 0;
  const noSectors = form.accessSectors.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Notice color={HML_YELLOW} icon={<IconLock size={14} />} dark={dark}>
        O acesso é montado em cascata: <strong>unidade</strong> define quais{' '}
        <strong>setores</strong> aparecem e, com a confidencialidade ligada, quais{' '}
        <strong>colaboradores</strong> podem ser escolhidos. Quem não estiver na lista não vê a
        aplicação no catálogo; administradores continuam vendo tudo.
      </Notice>

      <div style={{ display: 'grid', gridTemplateColumns: byViewport(vp, FIELD_GRID_2), gap: 14 }}>
        <FormField
          label="Unidades com acesso"
          required
          name="accessUnits"
          error={errors.accessUnits}
          hint="Filiais do cadastro de RH · o filtro de setores segue esta escolha"
          info={<InfoPopover {...ACCESS_INFO.acesso} dark={dark} accent={accent} size={18} />}
          dark={dark}
        >
          {(f) => (
            <>
              <MultiSelect
                {...f}
                invalid={f.invalid || units.isError}
                value={form.accessUnits}
                onChange={changeUnits}
                options={unitOptions}
                dark={dark}
                accent={accent}
                ariaLabel="Unidades com acesso"
                placeholder="Escolha as unidades…"
                searchPlaceholder="Buscar unidade…"
                noun={['unidade', 'unidades']}
                loading={units.isLoading}
                error={units.error?.message ?? null}
                emptyLabel="Nenhuma unidade encontrada no cadastro de RH"
              />
              {units.isError && (
                <AccessLoadError
                  message="Falha ao carregar as unidades."
                  onRetry={() => void units.refetch()}
                  retrying={units.isFetching}
                  dark={dark}
                />
              )}
            </>
          )}
        </FormField>

        <FormField
          label="Setores com acesso"
          required
          name="accessSectors"
          error={errors.accessSectors}
          hint="Lista sem repetição · o badge mostra em quais unidades o setor existe"
          dark={dark}
        >
          {(f) => (
            <>
              <MultiSelect
                {...f}
                invalid={f.invalid || sectors.isError}
                value={form.accessSectors}
                onChange={changeSectors}
                options={sectorOptions}
                dark={dark}
                accent={accent}
                ariaLabel="Setores com acesso"
                placeholder="Escolha os setores…"
                searchPlaceholder="Buscar setor ou unidade…"
                noun={['setor', 'setores']}
                // a base tem 204 setores · cabem todos sem pedir refino da busca
                maxVisible={250}
                disabled={noUnits}
                disabledLabel="Escolha uma unidade primeiro"
                loading={sectors.isLoading || sectors.isFetching}
                error={sectors.error?.message ?? null}
                emptyLabel="Nenhum setor nas unidades escolhidas"
              />
              {sectors.isError && (
                <AccessLoadError
                  message="Falha ao carregar os setores."
                  onRetry={() => void sectors.refetch()}
                  retrying={sectors.isFetching}
                  dark={dark}
                />
              )}
            </>
          )}
        </FormField>
      </div>

      <ToggleRow
        checked={form.perCollaborator}
        onToggle={togglePerCollaborator}
        label="Confidencialidade por colaborador"
        sub="Restringe a visualização a pessoas específicas dentro dos setores escolhidos"
        dark={dark}
        accent={accent}
        info={
          <InfoPopover {...ACCESS_INFO.confidencialidade} dark={dark} accent={accent} size={22} />
        }
      />

      {form.perCollaborator && (
        <FormField
          label="Colaboradores com acesso"
          required
          name="accessCollaborators"
          error={errors.accessCollaborators}
          hint="Busca por nome ou matrícula dentro das unidades e setores escolhidos · quem já estava liberado e não veio na lista carregada aparece pela matrícula"
          dark={dark}
        >
          {(f) => (
            <>
              <MultiSelect
                {...f}
                invalid={f.invalid || collaborators.isError}
                value={form.accessCollaborators}
                onChange={(next) => onPatch({ collaborators: next })}
                options={personOptions}
                dark={dark}
                accent={accent}
                ariaLabel="Colaboradores com acesso"
                placeholder="Escolha os colaboradores…"
                searchPlaceholder="Buscar nome ou matrícula…"
                noun={['colaborador', 'colaboradores']}
                disabled={noSectors}
                disabledLabel="Escolha um setor primeiro"
                loading={collaborators.isLoading || collaborators.isFetching}
                error={collaborators.error?.message ?? null}
                emptyLabel="Nenhum colaborador para esta busca"
                onSearchChange={setPersonQuery}
              />
              {collaborators.isError && (
                <AccessLoadError
                  message="Falha ao carregar os colaboradores."
                  onRetry={() => void collaborators.refetch()}
                  retrying={collaborators.isFetching}
                  dark={dark}
                />
              )}
            </>
          )}
        </FormField>
      )}

      <AccessSummaryLine form={form} dark={dark} accent={accent} />
    </div>
  );
}

/** Resumo textual do acesso · usado na etapa e na coluna da direita. */
function accessSummary(form: FormState): string {
  if (form.visibility === 'publico') return 'Todos os colaboradores';
  if (form.visibility === 'interno') return 'Apenas administradores';
  const parts = [
    `${form.accessUnits.length} ${form.accessUnits.length === 1 ? 'unidade' : 'unidades'}`,
    `${form.accessSectors.length} ${form.accessSectors.length === 1 ? 'setor' : 'setores'}`,
  ];
  if (form.perCollaborator) {
    const n = form.accessCollaborators.length;
    parts.push(`${n} ${n === 1 ? 'colaborador' : 'colaboradores'}`);
  }
  return parts.join(' · ');
}

// ─── Auxiliares visuais ───────────────────────
function EnvBadge({ env, dark }: Readonly<{ env: 'prod' | 'homolog' | 'dev'; dark: boolean }>) {
  const config = {
    prod: { l: 'Prod', c: OK_GREEN },
    homolog: { l: 'Hml', c: HML_YELLOW },
    dev: { l: 'Dev', c: makePalette(dark).muted },
  }[env];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 10px',
        borderRadius: 6,
        ...rotuloCaixaAlta,
        fontSize: 10,
        fontWeight: 700,
        background: dark ? `${config.c}22` : `${config.c}1a`,
        color: config.c,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: config.c }} />
      {config.l}
    </span>
  );
}

// ─── Imagem da aplicação · upload de verdade ──
// Contrato da API:
//   POST /api/uploads/application-image  · multipart/form-data, campo `file`,
//        image/png|jpeg|webp, máx 4 MB → 201 { data: { key } }
//        erros: 400 VALIDATION_ERROR · 503 STORAGE_UNAVAILABLE · 502 STORAGE_ERROR
//   GET  /api/uploads/application-image/:key · serve a imagem (exige auth)
// A aplicação guarda a CHAVE em `imageKey`.
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const IMAGE_ACCEPT = IMAGE_TYPES.join(',');
const IMAGE_MAX_BYTES = 4 * 1024 * 1024;
const IMAGE_ENDPOINT = `${import.meta.env.VITE_API_URL || ''}/api/uploads/application-image`;

/**
 * O upload NÃO passa pelo cliente RPC tipado (`lib/api-client.ts`): o
 * hono/client serializa o corpo como JSON e este endpoint é
 * `multipart/form-data`. Então falamos com a API por fetch/XHR relativo,
 * repetindo exatamente o caminho de autenticação do api-client: Bearer do token
 * da sessão do Clerk (quando há Clerk) + `credentials: 'include'`. Em modo dev,
 * sem Clerk, não existe token e a API trata a requisição como dev-user.
 */
async function authToken(): Promise<string | null> {
  try {
    return (await getClerk()?.session?.getToken()) ?? null;
  } catch {
    // sem token (não logado ou Clerk indisponível): segue sem o header
    return null;
  }
}

/** "3,7" · tamanho em MB para a mensagem de recusa. */
function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

/**
 * Chave do objeto na resposta do XHR já concluído, ou o erro correspondente.
 *
 * Vive fora do `uploadImage` para não empilhar mais um nível de função dentro do
 * executor da Promise: o corpo de erro é lido pelo mesmo `readApiError` do resto
 * do app, embrulhando a resposta crua num `Response` para reaproveitar o parser
 * do envelope `{ error, code, details }`.
 */
async function uploadedImageKey(xhr: XMLHttpRequest): Promise<string> {
  if (xhr.status === 0) throw new ApiError(0, { error: 'Falha de rede ao enviar a imagem.' });
  const res = new Response(xhr.responseText || null, { status: xhr.status });
  if (!res.ok) throw await readApiError(res, 'Falha ao enviar a imagem');
  const json = (await res.json().catch(() => null)) as { data?: { key?: unknown } } | null;
  const key = json?.data?.key;
  if (typeof key !== 'string' || key === '') {
    throw new ApiError(res.status, { error: 'A API não devolveu a chave da imagem.' });
  }
  return key;
}

/**
 * Envia a imagem e devolve a chave do objeto. XHR (e não fetch) porque só ele
 * expõe progresso de UPLOAD — a barra da tela mostra o número real.
 */
function uploadImage(file: File, onProgress: (pct: number) => void): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    void (async () => {
      const token = await authToken();
      const body = new FormData();
      body.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', IMAGE_ENDPOINT);
      xhr.withCredentials = true;
      // Sem teto o envio pendura para sempre quando o proxy corta a conexão no
      // meio do corpo (era o sintoma do 413 do nginx): o ontimeout abaixo só
      // dispara se um limite existir. 2 min cobre 4 MB em link bem lento.
      xhr.timeout = 120_000;
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e: ProgressEvent) => {
        // 100% fica reservado para a resposta da API: o byte no fio não é o
        // arquivo gravado no bucket.
        if (e.lengthComputable) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      };
      xhr.onerror = () =>
        reject(new ApiError(0, { error: 'Falha de rede ao enviar a imagem. Tente novamente.' }));
      xhr.ontimeout = () => reject(new ApiError(0, { error: 'O envio da imagem expirou.' }));
      xhr.onload = () => {
        void uploadedImageKey(xhr).then((key) => {
          onProgress(100);
          resolve(key);
        }, reject);
      };
      xhr.send(body);
    })().catch(reject);
  });
}

/**
 * Baixa a imagem já salva e devolve um objectURL. A rota exige autenticação, e
 * um `<img src>` não carrega o header Bearer — daí o fetch + blob. Quem chama
 * revoga o objectURL.
 */
async function fetchImageObjectUrl(key: string): Promise<string> {
  const token = await authToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // A chave pode conter barra (`applications/<uuid>.webp`): encode para caber
  // num único segmento de rota.
  // Preview do formulário: a variante de 640 basta e poupa o original.
  const res = await fetch(`${IMAGE_ENDPOINT}/${encodeURIComponent(key)}?w=640`, {
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar a imagem salva');
  return URL.createObjectURL(await res.blob());
}

function ImagePlaceholderArt({ dark }: Readonly<{ dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background: dark ? p.surface3 : p.surface,
        color: p.muted,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </div>
  );
}

type ImageNoticeTone = 'error' | 'warn' | 'ok';

interface ImageNotice {
  tone: ImageNoticeTone;
  text: string;
}

const NOTICE_COLORS: Record<ImageNoticeTone, string> = {
  error: DANGER,
  warn: HML_YELLOW,
  ok: OK_GREEN,
};

const SAVED_PREVIEW_WARNING: ImageNotice = {
  tone: 'warn',
  text: 'Não foi possível carregar a pré-visualização da imagem salva. Ela continua vinculada à aplicação.',
};

const UPLOAD_OK_NOTICE: ImageNotice = {
  tone: 'ok',
  text: 'Imagem enviada. Ela passa a ilustrar o card no catálogo e a capa do detalhe.',
};

/** Motivo da recusa no cliente (tipo/tamanho), ou `null` quando o arquivo serve. */
function imageRejection(file: File): string | null {
  if (!IMAGE_TYPES.includes(file.type)) {
    return 'Formato não aceito. Envie um arquivo PNG, JPEG ou WebP.';
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return `A imagem tem ${megabytes(file.size)} MB e o limite é 4 MB. Reduza a resolução ou salve em WebP.`;
  }
  return null;
}

/**
 * Aviso do envio que falhou. Storage não configurado no servidor é AVISO (o
 * cadastro segue possível sem imagem); qualquer outra falha é erro.
 */
function uploadFailureNotice(err: unknown): ImageNotice {
  const api = err instanceof ApiError ? err : null;
  if (api && (api.code === 'STORAGE_UNAVAILABLE' || api.status === 503)) {
    return {
      tone: 'warn',
      text: 'O armazenamento de imagens ainda não está configurado no servidor, então o envio não pode ser concluído agora. O cadastro continua possível sem imagem e você pode enviá-la depois.',
    };
  }
  return { tone: 'error', text: api?.message ?? 'Falha ao enviar a imagem. Tente novamente.' };
}

/** Linha de estado: progresso do envio, arquivo escolhido ou imagem já gravada. */
function imageStatusLabel(
  uploading: boolean,
  progress: number,
  fileLabel: string | null,
  imageKey: string | null,
): string {
  if (uploading) return `Enviando ${fileLabel ?? 'a imagem'}… ${progress}%`;
  if (fileLabel) return fileLabel;
  if (imageKey) return 'Imagem cadastrada';
  return 'Nenhuma imagem selecionada';
}

/** Revoga o objectURL do arquivo local ao trocar, ao remover e ao desmontar. */
function useRevokedObjectUrl(url: string | null): void {
  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);
}

/**
 * Preview da imagem JÁ GRAVADA. A rota exige autenticação e um `<img src>` não
 * carrega o header Bearer — daí o fetch + blob, com o objectURL revogado na
 * limpeza. Fica parado enquanto existe preview local (o arquivo recém-escolhido
 * aparece na hora, sem esperar a rede).
 */
function useSavedImagePreview(
  imageKey: string | null,
  localUrl: string | null,
  onFailure: (notice: ImageNotice) => void,
): { url: string | null; loading: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!imageKey || localUrl) {
      setUrl(null);
      return;
    }
    let alive = true;
    let created: string | null = null;
    setUrl(null);
    setLoading(true);
    fetchImageObjectUrl(imageKey)
      .then((objectUrl) => {
        created = objectUrl;
        if (alive) setUrl(objectUrl);
      })
      .catch(() => {
        if (alive) onFailure(SAVED_PREVIEW_WARNING);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [imageKey, localUrl, onFailure]);

  return { url, loading };
}

interface UploadProgressBarProps {
  progress: number;
  dark: boolean;
  accent: string;
  /** Posicionamento do trilho no ponto de uso (a capa o gruda na base do quadro). */
  style?: CSSProperties;
}

/**
 * Barra de progresso do envio.
 *
 * A barra COLORIDA é decorativa: o progresso está na largura dela, e um
 * `<progress>` nativo só muda de cor por pseudo-elemento
 * (`::-webkit-progress-value`), impossível nesta tela de estilos inline. Então
 * quem anuncia o valor é o `<progress>` real, fora da tela mas dentro da árvore
 * de acessibilidade — sem `role="progressbar"` num div, e de graça com o
 * mapeamento nativo do elemento.
 */
function UploadProgressBar({ progress, dark, accent, style }: Readonly<UploadProgressBarProps>) {
  return (
    <div
      style={{
        position: 'relative',
        height: 6,
        borderRadius: 3,
        overflow: 'hidden',
        background: dark ? '#26272d' : '#f1f5f4',
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: `${progress}%`,
          height: '100%',
          background: accent,
          transition: 'width 160ms linear',
        }}
      />
      <progress
        value={progress}
        max={100}
        aria-label="Envio da imagem"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          border: 0,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
        }}
      />
    </div>
  );
}

interface HiddenFileInputProps {
  inputRef: RefObject<HTMLInputElement | null>;
  onFile: (file: File | null | undefined) => void;
}

/** Seletor de arquivo nativo, escondido: quem o abre são os botões da tela. */
function HiddenFileInput({ inputRef, onFile }: Readonly<HiddenFileInputProps>) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept={IMAGE_ACCEPT}
      tabIndex={-1}
      aria-hidden="true"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Zera o input para reescolher o MESMO arquivo depois de um erro.
        e.target.value = '';
        onFile(file);
      }}
      style={{ display: 'none' }}
    />
  );
}

/**
 * Caixa de aviso do envio.
 *
 * `<output>` já é a região viva de status do HTML (role implícito `status`),
 * então só a recusa precisa de `role` explícito: `alert` para o leitor de tela
 * interromper a leitura. `display: block` devolve a caixa do `<div>` que estava
 * aqui — `<output>` nasce inline, e aí `marginTop` não valeria.
 */
function ImageNoticeBox({ notice, dark }: Readonly<{ notice: ImageNotice; dark: boolean }>) {
  const p = makePalette(dark);
  const color = NOTICE_COLORS[notice.tone];
  return (
    <output
      role={notice.tone === 'error' ? 'alert' : undefined}
      style={{
        display: 'block',
        marginTop: 10,
        padding: '10px 12px',
        borderRadius: 8,
        background: dark ? `${color}18` : `${color}0d`,
        border: `1px solid ${color}44`,
        fontSize: 11.5,
        lineHeight: 1.55,
        color: p.textMuted,
        fontFamily: fontSans,
      }}
    >
      {notice.text}
    </output>
  );
}

interface ImageUploadOptions {
  /** Formulário salvando: trava o controle junto com o envio em andamento. */
  busy: boolean;
  /** Onde a chave recém-gravada vai parar (capa ou fim da galeria). */
  onKey: (key: string) => void;
  successNotice: ImageNotice;
  /**
   * A galeria descarta o preview local assim que a chave chega: a tela enviada
   * passa a aparecer como mais um quadro da tira, e manter o mesmo desenho
   * também no botão de adicionar mostraria a imagem duas vezes. A capa faz o
   * contrário — o preview local É o que ela mostra até a próxima troca.
   */
  resetOnSuccess?: boolean;
}

interface ImageUpload {
  /** Envio em andamento OU formulário salvando. */
  locked: boolean;
  uploading: boolean;
  progress: number;
  /** objectURL do arquivo escolhido agora — aparece antes de a rede responder. */
  localUrl: string | null;
  fileLabel: string | null;
  notice: ImageNotice | null;
  setNotice: (notice: ImageNotice | null) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  openPicker: () => void;
  send: (file: File | null | undefined) => void;
  /** Descarta o preview local (usado ao remover a imagem). */
  clearLocal: () => void;
}

/**
 * Núcleo do envio de UMA imagem — o mesmo para a capa e para cada tela da
 * galeria: recusa de tipo/tamanho antes de gastar rede, progresso real do XHR,
 * preview local imediato e tradução da falha em aviso.
 *
 * Só o DESTINO da chave muda entre os dois usos, e é por isso que ele entra
 * como callback em vez de o hook mexer no formulário: uma segunda cópia deste
 * POST seria uma segunda regra de tipo/tamanho para divergir da primeira.
 */
function useImageUpload(options: Readonly<ImageUploadOptions>): ImageUpload {
  const { busy, onKey, successNotice, resetOnSuccess = false } = options;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState<ImageNotice | null>(null);
  const [fileLabel, setFileLabel] = useState<string | null>(null);

  useRevokedObjectUrl(localUrl);

  const locked = uploading || busy;

  const clearLocal = () => {
    setLocalUrl(null);
    setFileLabel(null);
  };

  const openPicker = () => {
    if (!locked) inputRef.current?.click();
  };

  const send = (file: File | null | undefined) => {
    if (!file || locked) return;
    const rejection = imageRejection(file);
    if (rejection) {
      setNotice({ tone: 'error', text: rejection });
      return;
    }

    setNotice(null);
    setFileLabel(file.name);
    setLocalUrl(URL.createObjectURL(file));
    setProgress(0);
    setUploading(true);
    void uploadImage(file, setProgress)
      .then((key) => {
        onKey(key);
        if (resetOnSuccess) clearLocal();
        setNotice(successNotice);
      })
      .catch((err: unknown) => {
        // Falhou: derruba o preview local e volta a mostrar o que estava gravado.
        clearLocal();
        setNotice(uploadFailureNotice(err));
      })
      .finally(() => setUploading(false));
  };

  return {
    locked,
    uploading,
    progress,
    localUrl,
    fileLabel,
    notice,
    setNotice,
    inputRef,
    openPicker,
    send,
    clearLocal,
  };
}

interface ImageDropZoneProps {
  preview: string | null;
  remoteLoading: boolean;
  uploading: boolean;
  progress: number;
  locked: boolean;
  onFile: (file: File | null | undefined) => void;
  /** Abre o seletor de arquivos nativo — o mesmo do botão "Selecionar arquivo". */
  onPick: () => void;
  dark: boolean;
  accent: string;
}

/**
 * Área de arrastar e soltar — que também abre o seletor de arquivos no clique,
 * o padrão universal de drop zone — com preview e barra de progresso real do
 * envio.
 *
 * `pointerEvents: none` no conteúdo: sem isso o arrastar sobre os filhos dispara
 * dragleave no container e o destaque fica piscando.
 *
 * `<fieldset>` (e não um `div role="group"`) é o elemento que já nomeia um grupo
 * de controles. Os dois `reset` do estilo neutralizam a folha do navegador —
 * `margin-inline: 2px`, que deslocaria a caixa, e `min-inline-size: min-content`,
 * que ignoraria o `width: 100%` em texto longo. O resto (borda, padding, display)
 * já vinha declarado, então o desenho é o mesmo de antes, pixel a pixel.
 */
function ImageDropZone({
  preview,
  remoteLoading,
  uploading,
  progress,
  locked,
  onFile,
  onPick,
  dark,
  accent,
}: Readonly<ImageDropZoneProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [dragging, setDragging] = useState(false);
  const dragBg = dark ? `${accent}18` : `${accent}0d`;

  return (
    <fieldset
      aria-label="Imagem da aplicação"
      onDragEnter={(e: React.DragEvent) => {
        e.preventDefault();
        if (!locked) setDragging(true);
      }}
      onDragOver={(e: React.DragEvent) => {
        e.preventDefault();
        if (!locked) setDragging(true);
      }}
      onDragLeave={(e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        onFile(e.dataTransfer.files?.[0]);
      }}
      style={{
        margin: 0,
        minInlineSize: 0,
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        maxHeight: 220,
        borderRadius: 12,
        background: dragging ? dragBg : p.surface2,
        border: `2px dashed ${dragging ? accent : p.borderStrong}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        // A caixa é 16:9: a 320px ela tem ~123px de altura, e 20px de padding em
        // cima e embaixo não deixavam o texto de apoio caber junto do ícone.
        padding: byViewport(vp, { mobile: 10, tablet: 16, desktop: 20 }),
        textAlign: 'center',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      {preview ? (
        <img
          src={preview}
          alt="Pré-visualização da imagem da aplicação"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Mesma âncora do card do catálogo (AppScreenshot): quando a
            // proporção não bate, corta embaixo e preserva o cabeçalho — o
            // preview mostra o mesmo pedaço que o catálogo vai mostrar.
            objectPosition: 'top',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            pointerEvents: 'none',
          }}
        >
          <ImagePlaceholderArt dark={dark} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 4 }}>
              {remoteLoading ? 'Carregando a imagem salva…' : 'Arraste a imagem para cá'}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: p.muted,
                fontFamily: fontSans,
                lineHeight: 1.5,
                maxWidth: 460,
              }}
            >
              PNG, JPEG ou WebP de até 4 MB, na proporção 16:9. Sem imagem, o catálogo usa o ícone
              com a inicial e a cor da marca.
            </div>
          </div>
        </div>
      )}

      {/* Barra de progresso real do envio (evento de upload do XHR). */}
      {uploading && (
        <UploadProgressBar
          progress={progress}
          dark={dark}
          accent={accent}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}
        />
      )}

      {/*
        Clicar na área também abre o seletor de arquivos (o padrão universal de
        drop zone: arrastar E clicar). É um <button> transparente cobrindo a
        área — e não um onClick no <fieldset> — porque o botão nativo já é
        focável e responde a Enter/Espaço de graça, e o <fieldset> segue sendo
        o grupo: um `role="button"` nele tornaria o <progress> descendente
        apresentacional. Último filho para pintar por cima do preview; o
        conteúdo de apoio já tem `pointerEvents: none` e os eventos de arrasto
        borbulham do botão para os handlers do <fieldset>. `disabled` durante o
        envio/salvamento: o clique não abre o picker no meio de um upload.
      */}
      <button
        type="button"
        onClick={onPick}
        disabled={locked}
        aria-label={
          preview ? 'Substituir a imagem da aplicação' : 'Selecionar a imagem da aplicação'
        }
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 'none',
          padding: 0,
          borderRadius: 10,
          cursor: locked ? 'progress' : 'pointer',
        }}
      />
    </fieldset>
  );
}

interface ImageActionsProps {
  preview: string | null;
  imageKey: string | null;
  uploading: boolean;
  progress: number;
  fileLabel: string | null;
  locked: boolean;
  onPick: () => void;
  onRemove: () => void;
  dark: boolean;
  accent: string;
}

/** Botões de selecionar/substituir e remover + linha de estado do envio. */
function ImageActions({
  preview,
  imageKey,
  uploading,
  progress,
  fileLabel,
  locked,
  onPick,
  onRemove,
  dark,
  accent,
}: Readonly<ImageActionsProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const accentBg = dark ? `${accent}1e` : `${accent}0f`;
  const pickBorderColor = preview ? p.borderStrong : accent;
  const pickBorderStyle = preview ? 'solid' : 'dashed';
  const btnHeight = byViewport(vp, SMALL_BTN_H);

  return (
    <div
      style={{
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <button
        type="button"
        onClick={onPick}
        disabled={locked}
        style={{
          height: btnHeight,
          padding: '0 12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          background: preview ? p.surface2 : accentBg,
          border: `1px ${pickBorderStyle} ${pickBorderColor}`,
          borderRadius: 7,
          color: preview ? p.text : accent,
          fontSize: 12,
          fontWeight: 700,
          fontFamily: fontSans,
          cursor: locked ? 'progress' : 'pointer',
          opacity: locked ? 0.6 : 1,
        }}
      >
        {preview ? 'Substituir' : 'Selecionar arquivo'}
      </button>

      {(preview || imageKey) && (
        <button
          type="button"
          onClick={onRemove}
          disabled={locked}
          style={{
            height: btnHeight,
            padding: '0 12px',
            background: 'transparent',
            border: `1px solid ${p.border}`,
            borderRadius: 7,
            color: locked ? p.muted : DANGER,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: fontSans,
            cursor: locked ? 'progress' : 'pointer',
            opacity: locked ? 0.6 : 1,
          }}
        >
          Remover
        </button>
      )}

      <span
        aria-live="polite"
        style={{
          fontSize: 11,
          color: p.muted,
          fontFamily: fontSans,
          minWidth: 0,
          lineHeight: 1.45,
        }}
      >
        {imageStatusLabel(uploading, progress, fileLabel, imageKey)}
      </span>
    </div>
  );
}

interface ImageFieldProps {
  imageKey: string | null;
  onChange: (key: string | null) => void;
  dark: boolean;
  accent: string;
  /** Trava os controles enquanto o formulário está sendo salvo. */
  busy?: boolean;
}

/**
 * Upload da imagem panorâmica: seletor de arquivo, arrastar e soltar, validação
 * de tipo/tamanho no cliente, barra de progresso real e preview.
 *
 * O preview vem de duas fontes: `URL.createObjectURL` do arquivo escolhido agora
 * (imediato, sem esperar a rede) e o blob da imagem já gravada quando o
 * formulário abre com `imageKey`. Cada objectURL criado aqui é revogado.
 */
function ImageField({ imageKey, onChange, dark, accent, busy = false }: Readonly<ImageFieldProps>) {
  const up = useImageUpload({ busy, onKey: onChange, successNotice: UPLOAD_OK_NOTICE });
  const saved = useSavedImagePreview(imageKey, up.localUrl, up.setNotice);

  const preview = up.localUrl ?? saved.url;

  const remove = () => {
    if (up.locked) return;
    up.clearLocal();
    up.setNotice(null);
    onChange(null);
  };

  return (
    <div>
      <HiddenFileInput inputRef={up.inputRef} onFile={up.send} />

      <ImageDropZone
        preview={preview}
        remoteLoading={saved.loading}
        uploading={up.uploading}
        progress={up.progress}
        locked={up.locked}
        onFile={up.send}
        onPick={up.openPicker}
        dark={dark}
        accent={accent}
      />

      <ImageActions
        preview={preview}
        imageKey={imageKey}
        uploading={up.uploading}
        progress={up.progress}
        fileLabel={up.fileLabel}
        locked={up.locked}
        onPick={up.openPicker}
        onRemove={remove}
        dark={dark}
        accent={accent}
      />

      {up.notice && <ImageNoticeBox notice={up.notice} dark={dark} />}
    </div>
  );
}

// ─── Telas adicionais · a tira do carrossel ───
// A CAPA (`imageKey`) continua sendo a primeira imagem da apresentação; o que
// entra aqui são as telas SEGUINTES, na ordem em que o apresentador vai passá-las
// (`[capa, ...galeriaKeys]`). O teto de `APPLICATION_MAX_IMAGES` conta o
// conjunto, então a tira aceita uma a menos que ele.
const GALERIA_MAX = APPLICATION_MAX_IMAGES - 1;

const GALERIA_OK_NOTICE: ImageNotice = {
  tone: 'ok',
  text: 'Tela adicionada ao fim da sequência. Use as setas do quadro para mudar a ordem do carrossel.',
};

/** Quantas imagens a aplicação tem no total · a capa ocupa uma vaga do teto. */
function totalImagens(capaKey: string | null, galeria: readonly string[]): number {
  return (capaKey ? 1 : 0) + galeria.length;
}

/**
 * Lista com o item `from` deslocado de uma casa (`delta`). Destino fora da
 * lista devolve a MESMA referência: quem chama não precisa conferir a ponta
 * antes, e um clique inócuo não vira render novo.
 */
function moverItem(lista: string[], from: number, delta: -1 | 1): string[] {
  const to = from + delta;
  if (from < 0 || from >= lista.length || to < 0 || to >= lista.length) return lista;
  const copia = [...lista];
  const [item] = copia.splice(from, 1);
  if (item !== undefined) copia.splice(to, 0, item);
  return copia;
}

type GaleriaTone = 'neutral' | 'accent' | 'danger';

/** Cor do ícone/rótulo de um botão do quadro. */
function galeriaBtnColor(tone: GaleriaTone, accent: string, text: string): string {
  if (tone === 'danger') return DANGER;
  if (tone === 'accent') return accent;
  return text;
}

interface GaleriaBtnProps {
  /** Nome acessível E `title`: os botões de ícone não têm texto visível. */
  label: string;
  onClick: () => void;
  disabled: boolean;
  dark: boolean;
  accent: string;
  tone?: GaleriaTone;
  /** Botão com texto (a promoção a capa) cresce; os de ícone ficam quadrados. */
  wide?: boolean;
  children: ReactNode;
}

/** Botão da linha de ações de um quadro da galeria. */
function GaleriaBtn({
  label,
  onClick,
  disabled,
  dark,
  accent,
  tone = 'neutral',
  wide = false,
  children,
}: Readonly<GaleriaBtnProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const h = byViewport(vp, GALERIA_BTN_H);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: h,
        minWidth: h,
        padding: wide ? '0 7px' : 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        background: p.surface2,
        border: `1px solid ${p.border}`,
        borderRadius: 6,
        color: disabled ? p.muted : galeriaBtnColor(tone, accent, p.text),
        fontSize: 10.5,
        fontWeight: 700,
        fontFamily: fontSans,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

interface GaleriaThumbProps {
  imageKey: string;
  /** Posição NA APRESENTAÇÃO — a capa é a 1, então a tira começa em 2. */
  position: number;
  first: boolean;
  last: boolean;
  locked: boolean;
  onPromote: () => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  dark: boolean;
  accent: string;
}

/**
 * Um quadro da tira: a tela gravada, o número dela no carrossel e as quatro
 * ações (virar capa, andar para trás, andar para frente, remover).
 *
 * A ordem é mudada por BOTÃO, e não por arrastar: a tira também é usada no
 * celular, onde arrastar concorre com o scroll da página, e um alvo de 34px com
 * nome acessível é o que qualquer leitor de tela consegue operar.
 */
function GaleriaThumb({
  imageKey,
  position,
  first,
  last,
  locked,
  onPromote,
  onMove,
  onRemove,
  dark,
  accent,
}: Readonly<GaleriaThumbProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // A prévia é baixada por quadro (a rota exige Bearer). Falha aqui não é erro
  // do formulário: a tela continua gravada e o aviso fica dentro do quadro.
  const [failed, setFailed] = useState(false);
  const markFailed = useCallback(() => setFailed(true), []);
  const saved = useSavedImagePreview(imageKey, null, markFailed);

  return (
    <div style={{ width: byViewport(vp, GALERIA_THUMB_W), flexShrink: 0 }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: 8,
          overflow: 'hidden',
          background: p.surface2,
          border: `1px solid ${p.borderStrong}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {saved.url ? (
          <img
            src={saved.url}
            alt={`Tela ${position} da apresentação`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // Mesma âncora da capa e do card do catálogo.
              objectPosition: 'top',
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 10.5,
              color: p.muted,
              fontFamily: fontSans,
              textAlign: 'center',
              padding: '0 8px',
              lineHeight: 1.4,
            }}
          >
            {failed ? 'Prévia indisponível · a tela segue cadastrada' : 'Carregando…'}
          </span>
        )}
        <span
          aria-hidden="true"
          style={{
            ...numeroTabular,
            position: 'absolute',
            top: 5,
            left: 5,
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 5,
            background: 'rgba(15,17,21,0.72)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {position}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
        <GaleriaBtn
          label={`Mover a tela ${position} para antes`}
          onClick={() => onMove(-1)}
          disabled={locked || first}
          dark={dark}
          accent={accent}
        >
          {/* O chevron da casa aponta para a direita; o giro faz o par. */}
          <IconChevronR size={13} style={{ transform: 'rotate(180deg)' }} />
        </GaleriaBtn>
        <GaleriaBtn
          label={`Mover a tela ${position} para depois`}
          onClick={() => onMove(1)}
          disabled={locked || last}
          dark={dark}
          accent={accent}
        >
          <IconChevronR size={13} />
        </GaleriaBtn>
        <GaleriaBtn
          label={`Tornar a tela ${position} a capa da aplicação`}
          onClick={onPromote}
          disabled={locked}
          dark={dark}
          accent={accent}
          tone="accent"
          wide
        >
          Capa
        </GaleriaBtn>
        <GaleriaBtn
          label={`Remover a tela ${position}`}
          onClick={onRemove}
          disabled={locked}
          dark={dark}
          accent={accent}
          tone="danger"
        >
          <IconX size={13} />
        </GaleriaBtn>
      </div>
    </div>
  );
}

interface GaleriaAddTileProps {
  /** Teto atingido: o botão sai de cena como alvo, com explicação ao lado. */
  full: boolean;
  locked: boolean;
  /**
   * objectURL do arquivo que está subindo AGORA. Fica no fundo do próprio
   * quadro enquanto o envio corre: é a confirmação de que o arquivo certo foi
   * escolhido, e o quadro volta a ser um "+" assim que a tela entra na tira.
   */
  preview: string | null;
  onFile: (file: File | null | undefined) => void;
  onPick: () => void;
  dark: boolean;
  accent: string;
}

/**
 * Quadro vazio que adiciona uma tela: clique abre o seletor, arrastar solta o
 * arquivo em cima — o mesmo par de gestos da capa, no mesmo tamanho de quadro
 * da tira.
 */
function GaleriaAddTile({
  full,
  locked,
  preview,
  onFile,
  onPick,
  dark,
  accent,
}: Readonly<GaleriaAddTileProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [dragging, setDragging] = useState(false);
  const disabled = full || locked;

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      title={
        full
          ? `Limite de ${APPLICATION_MAX_IMAGES} imagens por aplicação, contando a capa.`
          : 'Adicionar uma tela à apresentação'
      }
      aria-label="Adicionar uma tela à apresentação"
      onDragEnter={(e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragOver={(e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={(e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) onFile(e.dataTransfer.files?.[0]);
      }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: byViewport(vp, GALERIA_THUMB_W),
        flexShrink: 0,
        aspectRatio: '16 / 9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: 8,
        borderRadius: 8,
        background: dragging ? `${accent}14` : 'transparent',
        border: `2px dashed ${dragging ? accent : p.borderStrong}`,
        color: disabled ? p.muted : accent,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: fontSans,
        lineHeight: 1.35,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      {preview && (
        <img
          src={preview}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top',
            // Apagado o bastante para o rótulo continuar legível por cima.
            opacity: 0.4,
            pointerEvents: 'none',
          }}
        />
      )}
      {/*
        O "X" da casa girado 45° é o "+" — sem ícone novo só para isto. Ícone e
        rótulo ficam POSICIONADOS para pintar acima do preview absoluto do
        arquivo em envio (conteúdo estático fica atrás dele).
      */}
      <IconX size={16} style={{ transform: 'rotate(45deg)', position: 'relative' }} />
      <span style={{ position: 'relative' }}>{full ? 'Limite atingido' : 'Adicionar tela'}</span>
    </button>
  );
}

interface GaleriaFieldProps {
  /** A capa entra só para a CONTA do teto e para o aviso de "sem capa". */
  capaKey: string | null;
  keys: string[];
  onAdd: (key: string) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, delta: -1 | 1) => void;
  onPromote: (index: number) => void;
  dark: boolean;
  accent: string;
  busy?: boolean;
}

/**
 * Tira de telas adicionais: adicionar, reordenar, promover a capa e remover.
 *
 * O envio é o MESMO `useImageUpload` da capa (um POST, uma regra de
 * tipo/tamanho, um tratamento de erro) — aqui a chave que volta só tem outro
 * destino: o fim da lista.
 */
function GaleriaField({
  capaKey,
  keys,
  onAdd,
  onRemove,
  onMove,
  onPromote,
  dark,
  accent,
  busy = false,
}: Readonly<GaleriaFieldProps>) {
  const p = makePalette(dark);
  const up = useImageUpload({
    busy,
    onKey: onAdd,
    successNotice: GALERIA_OK_NOTICE,
    resetOnSuccess: true,
  });

  const total = totalImagens(capaKey, keys);
  // Os DOIS tetos do schema: o do conjunto e o da lista. Sem capa gravada o
  // conjunto ainda tem folga na oitava vaga, mas a galeria sozinha para em 7 —
  // é a vaga que a capa vai ocupar quando ela for enviada.
  const full = total >= APPLICATION_MAX_IMAGES || keys.length >= GALERIA_MAX;
  const semCapa = capaKey === null && keys.length > 0;

  return (
    <fieldset style={{ margin: '18px 0 0', minInlineSize: 0, border: 'none', padding: 0 }}>
      <legend style={{ padding: 0, fontSize: 11, fontWeight: 700, color: p.text }}>
        Telas adicionais da apresentação
      </legend>

      <div
        style={{
          margin: '6px 0 10px',
          fontSize: 11,
          color: p.muted,
          fontFamily: fontSans,
          lineHeight: 1.5,
        }}
      >
        A ordem desta tira é a ordem do carrossel na apresentação: a capa abre como tela{' '}
        <span style={{ ...numeroTabular, fontWeight: 700 }}>1</span> e as demais seguem na sequência
        numerada abaixo.{' '}
        <span style={{ ...numeroTabular, fontWeight: 700, color: p.text }}>
          {keys.length} de {GALERIA_MAX}
        </span>{' '}
        telas adicionais · {total} de {APPLICATION_MAX_IMAGES} imagens no total.
      </div>

      <HiddenFileInput inputRef={up.inputRef} onFile={up.send} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
        {keys.map((k, i) => (
          <GaleriaThumb
            key={k}
            imageKey={k}
            position={i + 2}
            first={i === 0}
            last={i === keys.length - 1}
            locked={up.locked}
            onPromote={() => onPromote(i)}
            onMove={(delta) => onMove(i, delta)}
            onRemove={() => onRemove(i)}
            dark={dark}
            accent={accent}
          />
        ))}
        <GaleriaAddTile
          full={full}
          locked={up.locked}
          preview={up.localUrl}
          onFile={up.send}
          onPick={up.openPicker}
          dark={dark}
          accent={accent}
        />
      </div>

      {full && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: p.muted,
            fontFamily: fontSans,
            lineHeight: 1.5,
          }}
        >
          Limite de {APPLICATION_MAX_IMAGES} imagens por aplicação, contando a capa. Remova uma tela
          para abrir vaga — o envio é recusado no servidor antes de gravar, então não adianta
          insistir.
        </div>
      )}

      {semCapa && (
        <div
          role="alert"
          style={{
            marginTop: 10,
            padding: '8px 10px',
            borderRadius: 8,
            background: dark ? `${HML_YELLOW}18` : `${HML_YELLOW}0d`,
            border: `1px solid ${HML_YELLOW}44`,
            fontSize: 11,
            color: p.textMuted,
            fontFamily: fontSans,
            lineHeight: 1.5,
          }}
        >
          A aplicação tem telas cadastradas, mas está sem capa: o catálogo vai mostrar o ícone com a
          inicial. Use "Capa" em uma das telas acima para promovê-la.
        </div>
      )}

      {/* Estado do envio da tira · o da capa vive dentro do próprio ImageField. */}
      {up.uploading && (
        <div style={{ marginTop: 10 }}>
          <UploadProgressBar progress={up.progress} dark={dark} accent={accent} />
        </div>
      )}
      <div
        aria-live="polite"
        style={{
          marginTop: 6,
          fontSize: 11,
          color: p.muted,
          fontFamily: fontSans,
          lineHeight: 1.45,
        }}
      >
        {up.uploading ? `Enviando ${up.fileLabel ?? 'a tela'}… ${up.progress}%` : ''}
      </div>

      {up.notice && <ImageNoticeBox notice={up.notice} dark={dark} />}
    </fieldset>
  );
}

// ─── Conteúdo dos popovers de LGPD ────────────
interface InfoEntry {
  title: string;
  subtitle: string;
  sections: InfoSection[];
}

const LGPD_INFO: Record<LgpdKey, InfoEntry> = {
  dadosPessoais: {
    title: 'Tratamento de dados pessoais',
    subtitle: 'LGPD · Lei 13.709/2018',
    sections: [
      {
        h: 'O que é',
        b: 'Qualquer operação realizada com dados de pessoa natural identificada ou identificável: coleta, armazenamento, uso, compartilhamento, eliminação. Inclui nome, CPF, e-mail, IP, telefone, foto, geolocalização e outros.',
      },
      {
        h: 'Bases legais aplicáveis',
        list: [
          'Consentimento do titular (art. 7º, I)',
          'Cumprimento de obrigação legal ou regulatória (art. 7º, II)',
          'Execução de contrato com o titular (art. 7º, V)',
          'Legítimo interesse, com teste de balanceamento (art. 7º, IX)',
        ],
      },
      {
        h: 'O que a Masterboi exige',
        list: [
          'DPIA (Relatório de Impacto) preenchido e arquivado',
          'DPO designado · responsável: DPO corporativo Masterboi',
          'Registro de operações de tratamento (RoPA)',
          'Política de retenção e descarte definida',
          'Notificação à ANPD em até 72h em caso de incidente',
        ],
      },
      {
        h: 'Validação na auditoria',
        b: 'O DPO valida o DPIA e o RoPA; a auditoria externa verifica a evidência cruzada com o catálogo Master Apps.',
      },
    ],
  },
  dadosSensiveis: {
    title: 'Dados sensíveis',
    subtitle: 'LGPD · art. 5º, II e art. 11',
    sections: [
      {
        h: 'Categorias consideradas sensíveis',
        list: [
          'Origem racial ou étnica',
          'Convicção religiosa',
          'Opinião política',
          'Filiação a sindicato ou organização',
          'Dados de saúde ou vida sexual',
          'Dados genéticos ou biométricos',
        ],
      },
      {
        h: 'Tratamento mais restrito',
        b: 'O art. 11 permite tratamento apenas com consentimento específico e destacado, ou nas hipóteses fechadas da lei (proteção da vida, saúde, exercício regular de direitos, etc.).',
      },
      {
        h: 'Exigências adicionais',
        list: [
          'DPIA obrigatório (não pode ser dispensado)',
          'Anonimização ou pseudonimização sempre que possível',
          'Acessos com MFA · logs auditáveis',
          'Criptografia em trânsito (TLS 1.2+) e em repouso (AES-256)',
        ],
      },
    ],
  },
  integracaoExt: {
    title: 'Integração com sistemas externos',
    subtitle: 'Governança de terceiros · cadeia de fornecedores',
    sections: [
      {
        h: 'O que cobre',
        b: 'Qualquer integração com sistemas fora da rede Masterboi: bancos, órgãos governamentais (SEFAZ, SISBI), parceiros, ERPs de cliente, plataformas SaaS, gateways de pagamento, etc.',
      },
      {
        h: 'O que precisa estar formalizado',
        list: [
          'Mapa de integrações no Master Apps · com URL/endpoint',
          'Contrato ou DPA (Data Processing Agreement) com o terceiro',
          'Cláusulas-padrão para transferência internacional, se aplicável',
          'Acordo de SLA + responsabilidade em caso de incidente',
        ],
      },
      {
        h: 'Riscos cobertos pela auditoria',
        list: [
          'Cadeia de fornecedores (terceiro do terceiro)',
          'Vazamentos por brecha em parceiros',
          'Dependência crítica · plano de saída',
          'Conformidade do terceiro com LGPD/GDPR',
        ],
      },
    ],
  },
  ia: {
    title: 'Uso de IA · modelos generativos',
    subtitle: 'ISO 42001 · Sistema de Gestão de IA',
    sections: [
      {
        h: 'O que entra nesta categoria',
        b: 'Aplicações que usam modelos de IA, ML ou GenAI: classificadores, recomendadores, OCR, NLP, chatbots, copilots, modelos fundacionais (GPT, Claude, Gemini), modelos próprios treinados pela Masterboi.',
      },
      {
        h: 'Classificação de risco',
        list: [
          'Inaceitável · banido (manipulação subliminar, scoring social)',
          'Alto risco · ex: triagem de RH, crédito (avaliação obrigatória)',
          'Risco limitado · usuários informados quando interagem com IA',
          'Risco mínimo · uso livre · ex: filtros de spam',
        ],
      },
      {
        h: 'O que a Masterboi exige',
        list: [
          'Inventário de IA atualizado (ISO 42001)',
          'Documentação do modelo: dados de treino, métricas, limitações',
          'Transparência: aviso ao usuário quando IA está em uso',
          'Auditabilidade: logs de prompts e respostas (mín. 90d)',
          'Plano de fallback se o modelo falhar',
        ],
      },
    ],
  },
};

const LGPD_ITEMS: { k: LgpdKey; l: string; sub: string }[] = [
  {
    k: 'dadosPessoais',
    l: 'Trata dados pessoais (LGPD)',
    sub: 'DPIA exigido · responsável: DPO',
  },
  {
    k: 'dadosSensiveis',
    l: 'Possui dados sensíveis (art. 5º LGPD)',
    sub: 'Saúde, biometria, etnia, religião, etc.',
  },
  {
    k: 'integracaoExt',
    l: 'Possui integração com sistemas externos',
    sub: 'Bancos, governo, parceiros, SaaS',
  },
  {
    k: 'ia',
    l: 'Faz uso de IA / modelos generativos',
    sub: 'Inventário de IA · ISO 42001',
  },
];

// ─── Tela principal ───────────────────────────
export interface RegisterSubmitHandlers {
  /**
   * Chamar quando a API confirmar: é o que descarta o rascunho antes de navegar
   * (a tela desmonta em seguida, então um efeito baseado em "salvo" não rodaria
   * em tempo).
   */
  onSuccess: () => void;
  /**
   * Chamar no erro da mutação: lê `details.issues[]` do 400 VALIDATION_ERROR
   * (chaves pontuadas como `access.collaborators` incluídas), pinta cada campo
   * recusado, rola até o primeiro e devolve `true` quando identificou pelo menos
   * um campo — a página usa o retorno para escolher o texto do toast.
   */
  onError: (error: unknown) => boolean;
}

export interface ScreenRegisterProps extends ScreenProps {
  /** `create` publica (POST) · `edit` salva alterações (PATCH sobre `initial`). */
  mode?: 'create' | 'edit';
  /**
   * Aplicação carregada. Obrigatória no modo edição: é a fonte do prefill e dos
   * campos que este formulário não edita e o PATCH não pode zerar.
   */
  initial?: AppWithAccess;
  onSubmit?: (input: ApplicationCreateInput, handlers: RegisterSubmitHandlers) => void;
  submitting?: boolean;
}

/** Acesso do rascunho difere do gravado? Decide o `accessTouched` na restauração. */
function accessDiffers(a: FormState, b: FormState): boolean {
  const key = (f: FormState) =>
    JSON.stringify([f.accessUnits, f.accessSectors, f.accessCollaborators, f.perCollaborator]);
  return key(a) !== key(b);
}

/**
 * As imagens do rascunho ainda valem?
 *
 * Só quando o registro servido AGORA está com o mesmo conjunto de imagens que
 * tinha quando o rascunho foi escrito. Se alguém (outra pessoa, outra aba)
 * cadastrou ou removeu telas no meio do caminho, restaurar a galeria do
 * rascunho apagaria o trabalho dela — e a galeria é o único campo desta tela
 * que já existe como objeto GRAVADO no bucket antes de o formulário ser salvo.
 *
 * Rascunho sem carimbo (escrito antes de a galeria existir) cai no mesmo lado
 * seguro: não dá para conferir, então manda o dado servido.
 */
function draftImages(
  base: FormState,
  draft: FormState,
): Pick<FormState, 'imageKey' | 'galeriaKeys'> {
  const mesmoRegistro =
    typeof draft.imagensServidas === 'string' && draft.imagensServidas === base.imagensServidas;
  if (!mesmoRegistro) return { imageKey: base.imageKey, galeriaKeys: [...base.galeriaKeys] };
  return {
    // Capa AUSENTE no rascunho é remoção deliberada de quem estava editando,
    // não campo faltando — por isso `null`, e não a capa servida.
    imageKey: typeof draft.imageKey === 'string' ? draft.imageKey : null,
    galeriaKeys: Array.isArray(draft.galeriaKeys)
      ? draft.galeriaKeys.filter((k) => typeof k === 'string')
      : [],
  };
}

/**
 * Rascunho do localStorage sobre a base do formulário. Os campos de lista, a
 * imagem e o toggle são reconferidos: um rascunho antigo (ou editado à mão) não
 * pode entrar no estado com o tipo errado.
 */
function mergeDraft(base: FormState, draft: FormState): FormState {
  return {
    ...base,
    ...draft,
    // Espalhar um `lgpd` ausente é inofensivo: `...undefined` não adiciona nada.
    lgpd: { ...base.lgpd, ...draft.lgpd },
    ...draftImages(base, draft),
    // O carimbo é SEMPRE o do dado servido: depois de restaurar, o formulário
    // está editando o registro que acabou de chegar, não o de quando o rascunho
    // nasceu.
    imagensServidas: base.imagensServidas,
    stacks: Array.isArray(draft.stacks) ? draft.stacks : [],
    accessUnits: Array.isArray(draft.accessUnits) ? draft.accessUnits : [],
    accessSectors: Array.isArray(draft.accessSectors) ? draft.accessSectors : [],
    accessCollaborators: Array.isArray(draft.accessCollaborators) ? draft.accessCollaborators : [],
    perCollaborator: draft.perCollaborator === true,
    // Rascunho anterior ao campo não tem a chave: cai no valor SERVIDO, não em
    // `false` — senão restaurar um rascunho velho religaria a apresentação de
    // quem foi desmarcado no lobby depois.
    apresentacaoDesabilitada:
      typeof draft.apresentacaoDesabilitada === 'boolean'
        ? draft.apresentacaoDesabilitada
        : base.apresentacaoDesabilitada,
  };
}

/**
 * Erros por campo a partir de um erro do servidor: cada item de
 * `details.issues[]` é resolvido pelo caminho COMPLETO (`access.collaborators`
 * não cai mais em Unidades) e o que não casar com nenhum campo vira o aviso
 * geral (`_`) do topo da tela.
 */
function errorsFromServer(error: unknown): FieldErrors {
  const next: FieldErrors = {};
  const apiError = error instanceof ApiError ? error : null;
  for (const issue of apiError?.details?.issues ?? []) {
    const key = fieldForPayloadKey(issue.campo ?? '');
    if (!next[key]) next[key] = issue.mensagem;
  }
  // 409 CONFLICT (slug duplicado) não traz issues, mas o campo é conhecido.
  if (!next.slug && apiError?.status === 409) next.slug = apiError.message;
  if (!FIELD_ORDER.some((f) => next[f]) && !next._) {
    next._ = error instanceof Error ? error.message : 'Falha ao salvar a aplicação.';
  }
  return next;
}

/**
 * Erros do formulário antes do envio: os obrigatórios da etapa (mesma lista da
 * barra de progresso) seguidos do espelho das regras do schema da API sobre o
 * payload já montado. O primeiro erro de cada campo é o que fica.
 */
function validateForm(form: FormState, payload: ApplicationCreateInput): FieldErrors {
  const next: FieldErrors = {};
  for (const spec of requiredSpecs(form)) {
    if (!spec.ok(form)) next[spec.field] = spec.message;
  }
  for (const issue of validatePayload(payload)) {
    const key = fieldForPayloadKey(issue.campo);
    if (!next[key]) next[key] = issue.mensagem;
  }
  return next;
}

/** Texto do indicador de autosave · vazio quando nada foi salvo nesta sessão. */
function draftStatusLabel(status: FormDraftStatus, savedLabel: string | null): string {
  if (status === 'saving') return 'Salvando rascunho…';
  if (savedLabel) return `Rascunho salvo ${savedLabel}`;
  return '';
}

/** Rótulo do botão principal, por modo e por estado do envio. */
function submitLabel(editing: boolean, submitting: boolean): string {
  if (editing) return submitting ? 'Salvando…' : 'Salvar alterações';
  return submitting ? 'Publicando…' : 'Publicar no catálogo';
}

/** Estado do card de ambiente no resumo da direita. */
function publishLabel(filled: boolean, editing: boolean): string {
  if (!filled) return 'Sem URL';
  return editing ? 'Publicado' : 'Será publicado';
}

interface DraftBannerProps {
  editing: boolean;
  onRestore: () => void;
  onDiscard: () => void;
  dark: boolean;
  accent: string;
}

/** Aviso de rascunho encontrado no navegador, com restaurar/descartar. */
function DraftBanner({ editing, onRestore, onDiscard, dark, accent }: Readonly<DraftBannerProps>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        marginBottom: 16,
        padding: '12px 14px',
        background: dark ? `${accent}14` : `${accent}0a`,
        border: `1px solid ${accent}44`,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ color: accent, display: 'inline-flex', flexShrink: 0 }}>
        <IconBolt size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: p.text }}>
          {editing
            ? 'Existe um rascunho desta edição neste navegador'
            : 'Existe um rascunho deste cadastro neste navegador'}
        </div>
        <div
          style={{
            fontSize: 11,
            color: p.muted,
            marginTop: 2,
            fontFamily: fontSans,
          }}
        >
          Restaure para continuar de onde parou ou descarte para começar do zero.
        </div>
      </div>
      <button
        type="button"
        onClick={onRestore}
        style={{
          height: 30,
          padding: '0 12px',
          background: accent,
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 11.5,
          fontWeight: 700,
          fontFamily: fontSans,
          cursor: 'pointer',
        }}
      >
        Restaurar
      </button>
      <ButtonGhost dark={dark} onClick={onDiscard}>
        Descartar
      </ButtonGhost>
    </div>
  );
}

interface VisibilityPickerProps {
  value: string;
  onChange: (value: string) => void;
  /** id do label · vai no PRIMEIRO botão do grupo. */
  controlId?: string;
  dark: boolean;
}

/**
 * Seletor segmentado da visibilidade (público / privado / interno).
 *
 * `<fieldset>` é o elemento que nomeia um grupo de controles (era um `div
 * role="group"`); `margin`/`padding`/`border`/`min-inline-size` zerados desfazem a
 * folha do navegador e mantêm a grade de três colunas idêntica.
 */
function VisibilityPicker({ value, onChange, controlId, dark }: Readonly<VisibilityPickerProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <fieldset
      aria-label="Visibilidade"
      style={{
        margin: 0,
        padding: 0,
        border: 0,
        minInlineSize: 0,
        display: 'grid',
        // No celular os três segmentos viram três linhas: cada botão precisa de
        // 74px (medido) e a 320px o campo inteiro tem ~220px — com gap, sobravam
        // 69px por coluna e o rótulo estourava a borda.
        gridTemplateColumns: byViewport(vp, {
          mobile: '1fr',
          tablet: 'repeat(3, 1fr)',
          desktop: 'repeat(3, 1fr)',
        }),
        gap: 6,
      }}
    >
      {VISIBILITY_OPTIONS.map((o, i) => {
        const selected = value === o.value;
        const selectedBg = dark ? `${o.color}22` : `${o.color}15`;
        return (
          <button
            key={o.value}
            id={i === 0 ? controlId : undefined}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.value)}
            style={{
              height: byViewport(vp, CONTROL_H),
              // 6px (era 8) + minWidth 0: item de grid tem min-width AUTO, e o
              // conteúdo mínimo do botão (74-75px medidos) vencia o track de
              // ~73,7px na coluna do desktop — o grid transbordava o fieldset e
              // o "Interno" saía 2px da borda do campo. Com minWidth 0 o track
              // manda, e o padding menor devolve a folga do rótulo.
              padding: '0 6px',
              minWidth: 0,
              background: selected ? selectedBg : p.surface2,
              border: `1px solid ${selected ? o.color : p.border}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
              color: selected ? o.color : p.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontFamily: fontSans,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: o.color,
              }}
            />
            {o.label}
          </button>
        );
      })}
    </fieldset>
  );
}

/**
 * Conteúdo da etapa 3. Só a aplicação PRIVADA tem acesso a definir: pública
 * libera a empresa inteira e interna é exclusiva de administradores.
 */
function AccessStep({ form, onPatch, errors, dark, accent }: Readonly<AccessFieldsProps>) {
  if (form.visibility === 'privado') {
    return (
      <AccessFields form={form} onPatch={onPatch} errors={errors} dark={dark} accent={accent} />
    );
  }
  if (form.visibility === 'publico') {
    return (
      <Notice color={OK_GREEN} icon={<IconGlobe size={14} />} dark={dark}>
        Nada a definir aqui. A aplicação é <strong>pública</strong>: todos os colaboradores da
        empresa terão acesso a visualizar esta aplicação. Mude a visibilidade para{' '}
        <strong>Privado</strong> se o acesso precisar ser limitado por unidade, setor ou pessoa.
      </Notice>
    );
  }
  return (
    <Notice color={DANGER} icon={<IconShield size={14} />} dark={dark}>
      Nada a definir aqui. <strong>Interno</strong> é a visibilidade de infraestrutura e ferramentas
      de TI: apenas administradores enxergam a aplicação, e ela não aparece no catálogo do
      colaborador.
    </Notice>
  );
}

// ─── Seção "Projeto" · o pedido, não a execução ──
/**
 * Os cinco campos do bloco (quatro na tela: as duas parcelas da calculadora
 * dividem o campo "Tempo de projeto"). Todos são `string` no formulário (ver
 * `FormState`) e nenhum entra em `requiredSpecs`: o cadastro tem de poder ser
 * publicado com a seção inteira em branco — os 44 registros que já existem não
 * teriam como responder isto retroativamente.
 */
type ProjetoCampo =
  | 'projetoDias'
  | 'projetoHorasDia'
  | 'projetoRetorno'
  | 'projetoArea'
  | 'projetoDono';

interface MiniFieldProps {
  htmlFor: string;
  label: string;
  dark: boolean;
  children: ReactNode;
}

/**
 * Rótulo interno da calculadora de esforço. Menor e mais discreto que o do
 * `FormField` de propósito: "Dias" e "Horas por dia" são as duas parcelas de UM
 * campo ("Tempo de projeto"), não dois campos irmãos.
 */
function MiniField({ htmlFor, label, dark, children }: Readonly<MiniFieldProps>) {
  const p = makePalette(dark);
  return (
    <div style={{ minWidth: 0 }}>
      <label
        htmlFor={htmlFor}
        style={{
          ...rotuloCaixaAlta,
          display: 'block',
          marginBottom: 5,
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: p.muted,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/** Total de horas legível em pt-BR · sem casa decimal inútil ("120", "22,5"). */
function formatHoras(total: number): string {
  return total.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

interface EsforcoCalculatorProps {
  /** Injetado pelo `FormField` · fica com o input de DIAS, que o label aponta. */
  control: FieldControl;
  dias: string;
  horasDia: string;
  onDias: (valor: string) => void;
  onHorasDia: (valor: string) => void;
  dark: boolean;
  accent: string;
}

/**
 * Calculadora de esforço: dias × horas por dia, com o total ao vivo.
 *
 * O total NÃO é um campo — não existe coluna para ele (ver o schema): valor
 * calculado que se armazena é valor que um dia discorda das parcelas. A conta
 * vem de `horasDeProjeto`, no shared, a MESMA que a ficha da aplicação usa; duas
 * implementações da mesma multiplicação divergem no arredondamento e ninguém
 * percebe.
 */
function EsforcoCalculator({
  control,
  dias,
  horasDia,
  onDias,
  onHorasDia,
  dark,
  accent,
}: Readonly<EsforcoCalculatorProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const uid = useId();
  const horasId = `${uid}-horas`;

  const total = horasDeProjeto({
    projetoDias: numeroOuNull(dias),
    projetoHorasDia: numeroOuNull(horasDia),
  });
  const parcial = total === null;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: byViewport(vp, FIELD_GRID_2), gap: 12 }}>
        <MiniField htmlFor={control.id} label="Dias" dark={dark}>
          <Input
            {...control}
            value={dias}
            // Dias é coluna `integer` no banco: separador decimal nem entra, ou
            // o servidor recusaria o payload INTEIRO por causa de "7,5 dias".
            onChange={(v) => onDias(digitacaoNumerica(v, MAX_PROJETO_DIAS, false))}
            placeholder="Ex.: 15"
            dark={dark}
            accent={accent}
            inputMode="numeric"
            suffix="dias"
          />
        </MiniField>
        <MiniField htmlFor={horasId} label="Horas por dia" dark={dark}>
          <Input
            id={horasId}
            // O `htmlFor` do FormField já foi gasto no campo de dias, então o
            // nome acessível deste segundo input vem do aria-label.
            aria-label="Horas por dia"
            value={horasDia}
            onChange={(v) => onHorasDia(digitacaoNumerica(v, MAX_PROJETO_HORAS_DIA, true))}
            placeholder="Ex.: 7,5"
            dark={dark}
            accent={accent}
            inputMode="decimal"
            suffix="h/dia"
          />
        </MiniField>
      </div>

      {/* `aria-live`: o total muda sem interação própria · quem usa leitor de
          tela só saberia do resultado saindo do campo para procurá-lo. */}
      <div
        aria-live="polite"
        style={{
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 7,
          background: parcial ? p.surface2 : hotBackground(accent, dark),
          border: `1px solid ${parcial ? p.border : `${accent}44`}`,
          fontSize: 11.5,
          lineHeight: 1.45,
          color: p.textMuted,
          fontFamily: fontSans,
        }}
      >
        <IconBolt size={12} style={{ color: parcial ? p.muted : accent, flexShrink: 0 }} />
        {/* Sem as duas parcelas o total NÃO vira 0: "5 dias × ?" é desconhecido,
            e um zero afirmaria esforço nenhum. */}
        {parcial ? (
          <span>Informe os dois campos para ver o total de horas do projeto.</span>
        ) : (
          <span>
            ≈{' '}
            <strong style={{ ...numeroTabular, fontSize: 13, color: p.text }}>
              {formatHoras(total)} h
            </strong>{' '}
            no total
          </span>
        )}
      </div>
    </div>
  );
}

interface ProjetoFieldsProps {
  form: FormState;
  onChange: (campo: ProjetoCampo, valor: string) => void;
  errors: FieldErrors;
  dark: boolean;
  accent: string;
}

/**
 * Conteúdo da seção "Projeto". Vive fora do corpo da tela porque precisa de
 * dados carregados (os setores do espelho do TOTVS), como a etapa de acesso.
 */
function ProjetoFields({ form, onChange, errors, dark, accent }: Readonly<ProjetoFieldsProps>) {
  const units = useUnits();

  /**
   * A Área é o setor SOLICITANTE: nada a ver com as unidades liberadas no
   * acesso, o universo é a base inteira.
   *
   * Mandar TODAS as filiais é um rodeio necessário: `GET /api/totvs/sectors` sem
   * `units` já devolve o universo completo, mas `useSectors` desliga a consulta
   * com a lista vazia (`enabled: key !== ''`) — regra da etapa de acesso, onde
   * mostrar os 204 setores antes de escolher a filial não significa nada. O
   * resultado é o mesmo e o hook continua servindo as duas seções sem mudar.
   */
  const todasUnidades = useMemo(() => (units.data ?? []).map((u) => u.codFilial), [units.data]);
  const sectors = useSectors(todasUnidades);

  const sectorOptions = useMemo<MultiSelectOption[]>(
    () => toSectorOptions(sectors.data ?? []),
    [sectors.data],
  );

  // Carregar as unidades é só o meio de chegar aos setores: para esta seção as
  // duas consultas são um estado só.
  const carregandoAreas = units.isLoading || sectors.isLoading || sectors.isFetching;
  const falhouAreas = units.isError || sectors.isError;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Notice color={accent} icon={<IconUsers size={14} />} dark={dark}>
        Tudo aqui é <strong>opcional</strong> e nada bloqueia a publicação. É o lado de{' '}
        <strong>quem pediu</strong> a aplicação: a área solicitante, a pessoa dessa área que puxou o
        projeto, o esforço gasto e o que ele devolve. Não confunda com{' '}
        <strong>Time responsável</strong> e <strong>Responsável</strong> (etapa 4), que são quem
        executa e quem mantém tecnicamente.
      </Notice>

      <FormField
        label="Tempo de projeto"
        optional
        // Um `data-field` para as duas parcelas · o erro de qualquer uma delas
        // chega aqui por `PAYLOAD_TO_FIELD`.
        name="projetoDias"
        error={errors.projetoDias}
        hint="Quanto foi trabalhado, na conta de quem pediu · o total em horas é calculado aqui e não é gravado"
        dark={dark}
      >
        {(f) => (
          <EsforcoCalculator
            control={f}
            dias={form.projetoDias}
            horasDia={form.projetoHorasDia}
            onDias={(v) => onChange('projetoDias', v)}
            onHorasDia={(v) => onChange('projetoHorasDia', v)}
            dark={dark}
            accent={accent}
          />
        )}
      </FormField>

      <FormField
        label="Área"
        optional
        name="projetoArea"
        error={errors.projetoArea}
        hint="Setor que pediu a aplicação · vem do cadastro de RH (TOTVS), não é texto livre · um setor só"
        dark={dark}
      >
        {(f) => (
          <>
            {/*
              MultiSelect em modo de escolha ÚNICA (o novo clique substitui o
              anterior) em vez do `Select` da casa: são 204 setores e o Select
              não tem busca — achar "PECUÁRIA" rolando uma lista desse tamanho é
              pior do que o campo parecer múltiplo por um instante. O chip único
              no campo é o que comunica o limite.
            */}
            <MultiSelect
              {...f}
              invalid={f.invalid || falhouAreas}
              value={form.projetoArea ? [form.projetoArea] : []}
              onChange={(next) => onChange('projetoArea', next.at(-1) ?? '')}
              // Esconde "Selecionar todos": aqui só um setor sobrevive, então o
              // botão acenderia 204 linhas para o `at(-1)` descartar 203.
              escolhaUnica
              options={sectorOptions}
              dark={dark}
              accent={accent}
              ariaLabel="Área solicitante"
              placeholder="Escolha o setor solicitante…"
              searchPlaceholder="Buscar setor ou unidade…"
              noun={['setor', 'setores']}
              // a base tem 204 setores · cabem todos sem pedir refino da busca
              maxVisible={250}
              loading={carregandoAreas}
              error={sectors.error?.message ?? units.error?.message ?? null}
              emptyLabel="Nenhum setor encontrado no cadastro de RH"
            />
            {falhouAreas && (
              <AccessLoadError
                message="Falha ao carregar os setores."
                onRetry={() => void (units.isError ? units.refetch() : sectors.refetch())}
                retrying={units.isFetching || sectors.isFetching}
                dark={dark}
              />
            )}
          </>
        )}
      </FormField>

      <FormField
        label="Retorno"
        optional
        name="projetoRetorno"
        error={errors.projetoRetorno}
        hint="O que o projeto devolve: horas economizadas, custo evitado, receita, risco ou retrabalho a menos"
        dark={dark}
      >
        {(f) => (
          <TextArea
            {...f}
            value={form.projetoRetorno}
            onChange={(v) => onChange('projetoRetorno', v)}
            placeholder="Ex.: cerca de 12 horas por semana da equipe de logística, antes gastas conferindo planilha."
            dark={dark}
            accent={accent}
          />
        )}
      </FormField>

      <FormField
        label="Dono do projeto"
        optional
        name="projetoDono"
        error={errors.projetoDono}
        hint="A pessoa da ÁREA que pediu a aplicação · digite para buscar na base do RH · não é o Responsável da etapa 4, que é quem mantém a aplicação tecnicamente"
        dark={dark}
      >
        {(f) => (
          <OwnerField
            {...f}
            value={form.projetoDono}
            onChange={(v) => onChange('projetoDono', v)}
            dark={dark}
            accent={accent}
          />
        )}
      </FormField>
    </div>
  );
}

interface UrlEnvRowProps {
  env: 'prod' | 'homolog' | 'dev';
  control: FieldControl;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Cor do aviso de card · `null` no ambiente que não publica card. */
  cardColor: string | null;
  dark: boolean;
  accent: string;
}

/** Linha de URL por ambiente: badge + campo + aviso do card no catálogo. */
function UrlEnvRow({
  env,
  control,
  value,
  onChange,
  placeholder,
  cardColor,
  dark,
  accent,
}: Readonly<UrlEnvRowProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  /**
   * No celular o aviso do card ("card será criado", 95px e `nowrap`) desce para
   * uma linha própria: com badge (55px) + aviso na mesma linha sobravam 60px para
   * a URL, ou seja, o campo principal da linha era o menor de todos.
   */
  const noticeBasis = vp.mobile ? '100%' : undefined;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: vp.mobile ? 'wrap' : 'nowrap',
      }}
    >
      <EnvBadge env={env} dark={dark} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Input
          {...control}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          dark={dark}
          accent={accent}
          mono
        />
      </div>
      {cardColor === null ? (
        <span
          style={{
            fontSize: 11,
            color: p.muted,
            fontFamily: fontSans,
            whiteSpace: 'nowrap',
            flexBasis: noticeBasis,
          }}
        >
          uso interno
        </span>
      ) : (
        <span
          style={{
            fontSize: 11,
            color: value ? cardColor : p.muted,
            fontWeight: 600,
            fontFamily: fontSans,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            whiteSpace: 'nowrap',
            flexBasis: noticeBasis,
          }}
        >
          {value ? (
            <>
              <IconCheck size={12} /> card será criado
            </>
          ) : (
            'card não criado'
          )}
        </span>
      )}
    </div>
  );
}

interface LgpdChecklistProps {
  value: Record<LgpdKey, boolean>;
  onToggle: (key: LgpdKey) => void;
  dark: boolean;
  accent: string;
}

/** Os quatro toggles de conformidade, cada um com o próprio popover de apoio. */
function LgpdChecklist({ value, onToggle, dark, accent }: Readonly<LgpdChecklistProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const onBg = dark ? `${accent}11` : `${accent}08`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {LGPD_ITEMS.map((opt) => {
        const selected = value[opt.k];
        const borderColor = selected ? `${accent}55` : p.border;
        return (
          <div
            key={opt.k}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              background: selected ? onBg : p.surface2,
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              transition: 'background 120ms, border-color 120ms',
            }}
          >
            {/*
              BOTÃO DE ALTERNÂNCIA (`aria-pressed`), não `role="checkbox"`: o
              elemento carrega o quadrado de 18px, o título e o subtítulo, e
              `<input type="checkbox">` é void — não pode ter filhos. O truque
              "input escondido + span visual" custaria o anel de foco, que aqui
              vem de graça do `<button>` nativo (a tela é 100% estilo inline, sem
              `:focus-visible`). O par botão + `aria-pressed` é padrão ARIA de
              toggle e anuncia o estado ligado/desligado igual ao checkbox.
            */}
            <button
              type="button"
              aria-pressed={selected}
              onClick={() => onToggle(opt.k)}
              style={{
                flex: 1,
                minWidth: 0,
                // Título + subtítulo somam 37px de conteúdo: 3px a menos que o
                // alvo de toque mínimo, medido a 375px e a 768px.
                minHeight: vp.compact ? 40 : undefined,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: 6,
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: fontSans,
                color: p.text,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  background: selected ? accent : 'transparent',
                  border: `1.5px solid ${selected ? accent : p.borderStrong}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#fff',
                }}
              >
                {selected && <IconCheck size={12} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: p.text,
                  }}
                >
                  {opt.l}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 11,
                    color: p.muted,
                    marginTop: 2,
                    fontFamily: fontSans,
                  }}
                >
                  {opt.sub}
                </span>
              </span>
            </button>
            <InfoPopover {...LGPD_INFO[opt.k]} dark={dark} accent={accent} size={22} />
          </div>
        );
      })}
    </div>
  );
}

/** Título de bloco da coluna da direita. */
function SidebarHeading({ children, dark }: Readonly<{ children: ReactNode; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: p.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

interface PublishCardProps {
  env: 'prod' | 'homolog';
  url: string;
  color: string;
  /** Fundo no tema claro · tom sólido, não derivado da cor do ambiente. */
  lightBg: string;
  editing: boolean;
  dark: boolean;
}

/** Card de ambiente no resumo: diz se aquele card sai (ou já está) no catálogo. */
function PublishCard({ env, url, color, lightBg, editing, dark }: Readonly<PublishCardProps>) {
  const p = makePalette(dark);
  const filled = url !== '';
  const filledBg = dark ? `${color}22` : lightBg;
  const borderColor = filled ? `${color}55` : p.border;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: filled ? filledBg : p.surface2,
        border: `1px solid ${borderColor}`,
        borderRadius: 7,
      }}
    >
      <EnvBadge env={env} dark={dark} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: filled ? color : p.muted,
          }}
        >
          {publishLabel(filled, editing)}
        </div>
      </div>
      {filled && <IconCheck size={14} style={{ color }} />}
    </div>
  );
}

interface RegisterSidebarProps {
  form: FormState;
  filledCount: number;
  totalRequired: number;
  editing: boolean;
  dark: boolean;
  accent: string;
}

/** Coluna da direita: progresso, cards que saem no catálogo, acesso e atalhos. */
function RegisterSidebar({
  form,
  filledCount,
  totalRequired,
  editing,
  dark,
  accent,
}: Readonly<RegisterSidebarProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const progressPct = (filledCount / totalRequired) * 100;
  const visibilityMeta = VISIBILITY_OPTIONS.find((o) => o.value === form.visibility);
  const visibilityLabel = visibilityMeta?.label ?? 'Público';
  const visibilityColor = visibilityMeta?.color ?? OK_GREEN;
  const shortcuts: [string, string][] = [
    [editing ? 'Salvar alterações' : 'Publicar', 'Ctrl+Enter'],
    ['Cancelar', 'Esc'],
  ];

  return (
    <div
      style={{
        /**
         * No compacto o painel deixa de ser coluna lateral e passa a ser um bloco
         * no fim do formulário: `sticky` + `maxHeight` + `overflowY` só fazem
         * sentido quando ele acompanha o scroll ao lado do conteúdo. Grudado no
         * topo de um bloco empilhado, ele criaria uma segunda área de rolagem
         * dentro da página.
         */
        position: vp.compact ? 'static' : 'sticky',
        top: 0,
        padding: byViewport(vp, { mobile: '14px 12px', tablet: '18px 16px', desktop: '24px 20px' }),
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        maxHeight: vp.compact ? undefined : 'calc(100vh - 96px)',
        overflowY: vp.compact ? undefined : 'auto',
      }}
    >
      <div>
        <SidebarHeading dark={dark}>Progresso</SidebarHeading>

        <div
          style={{
            height: 8,
            background: dark ? '#26272d' : '#f1f5f4',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              background: progressPct === 100 ? OK_GREEN : accent,
              transition: 'width 360ms',
            }}
          />
        </div>

        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: p.text,
            fontFamily: fontSans,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>
            <span style={{ ...numeroTabular, fontWeight: 700 }}>{progressPct.toFixed(0)}%</span>{' '}
            preenchido
          </span>
          <span style={{ color: p.muted }}>
            <span style={numeroTabular}>
              {filledCount}/{totalRequired}
            </span>{' '}
            obrigatórios
          </span>
        </div>
      </div>

      <div>
        <SidebarHeading dark={dark}>
          {editing ? 'Cards no catálogo' : 'Cards que serão publicados'}
        </SidebarHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <PublishCard
            env="prod"
            url={form.urlProd}
            color={OK_GREEN}
            lightBg="#dcfce7"
            editing={editing}
            dark={dark}
          />
          <PublishCard
            env="homolog"
            url={form.urlHml}
            color={HML_YELLOW}
            lightBg="#fef9c3"
            editing={editing}
            dark={dark}
          />
        </div>
      </div>

      <div>
        <SidebarHeading dark={dark}>Acesso</SidebarHeading>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 12px',
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 7,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              marginTop: 4,
              flexShrink: 0,
              background: visibilityColor,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: visibilityColor }}>
              {visibilityLabel}
            </div>
            <div
              style={{
                fontSize: 11,
                color: p.muted,
                marginTop: 2,
                fontFamily: fontSans,
                lineHeight: 1.45,
              }}
            >
              {accessSummary(form)}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 12,
          background: dark ? `${accent}11` : `${accent}08`,
          border: `1px solid ${accent}33`,
          borderRadius: 8,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ color: accent, marginTop: 1, flexShrink: 0 }}>
          <IconShield size={14} />
        </div>
        <div
          style={{
            fontSize: 11,
            color: p.textMuted,
            lineHeight: 1.55,
            fontFamily: fontSans,
          }}
        >
          <strong>Públicas</strong> ficam visíveis a todos os colaboradores.{' '}
          <strong>Privadas</strong> só para as unidades, setores e pessoas escolhidos na etapa 3.{' '}
          <strong>Internas</strong> só para administradores.
        </div>
      </div>

      <div>
        {/*
          Celular não tem Ctrl nem Esc: anunciar "Ctrl+Enter para publicar" num
          aparelho sem teclado físico é instrução que o usuário não consegue
          seguir. O atalho continua ATIVO (quem plugar um teclado usa), só não é
          mais oferecido — e a nota do autosave, que vale em qualquer aparelho,
          fica.
        */}
        {!vp.mobile && <SidebarHeading dark={dark}>Atalhos</SidebarHeading>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!vp.mobile &&
            shortcuts.map(([l, k]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: p.muted,
                  fontFamily: fontSans,
                }}
              >
                <span>{l}</span>
                <span
                  style={{
                    padding: '2px 6px',
                    borderRadius: 3,
                    background: p.surface2,
                    border: `1px solid ${p.border}`,
                    fontSize: 10,
                    fontFamily: fontMono,
                  }}
                >
                  {k}
                </span>
              </div>
            ))}
          <div
            style={{
              fontSize: 11,
              color: p.muted,
              fontFamily: fontSans,
              lineHeight: 1.45,
              marginTop: 2,
            }}
          >
            O rascunho é salvo sozinho neste navegador enquanto você digita.
          </div>
        </div>
      </div>
    </div>
  );
}

/** Rola até o primeiro campo com erro, na ordem visual da tela. */
function focusFirstError(errors: FieldErrors): void {
  const first =
    FIELD_ORDER.find((f) => errors[f]) ?? Object.keys(errors).find((k) => k !== '_' && errors[k]);
  if (first) focusField(first);
}

export function ScreenRegister({
  dark,
  accent = NOC_RED,
  mode = 'create',
  initial,
  onSubmit,
  submitting = false,
}: Readonly<ScreenRegisterProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();

  const editing = mode === 'edit';

  /**
   * Base do formulário: vazio na criação, a aplicação carregada na edição. Também
   * é o `pristine` do autosave (nada é gravado enquanto nada mudou) e a
   * referência do "formulário sujo". A página de edição só monta a tela depois de
   * a aplicação chegar, então `initial` existe já no primeiro render.
   */
  const baseForm = useMemo(() => (initial ? formFromApp(initial) : EMPTY_FORM), [initial]);

  const [form, setForm] = useState<FormState>(baseForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  // Na edição o slug não é derivado do nome (é a URL pública já divulgada).
  const [slugTouched, setSlugTouched] = useState(editing);
  /**
   * O usuário mexeu no acesso nesta sessão? Decide se o PATCH leva o bloco
   * `access` (ver a regra completa em `handleSubmit`).
   */
  const [accessTouched, setAccessTouched] = useState(false);

  // Rascunho por MODO: a edição usa uma chave por aplicação, então um cadastro
  // novo interrompido nunca reaparece dentro da edição de outra app, e o
  // rascunho de uma edição não vaza para outra.
  const draftKey = editing ? editDraftKey(initial?.id ?? 'sem-id') : DRAFT_KEY;

  const draft = useFormDraft<FormState>(draftKey, form, {
    pristine: baseForm,
    paused: submitting,
  });

  const clearError = (k: string) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearError(k as string);
  };

  // Nome alimenta o slug enquanto o usuário não editar o campo à mão.
  const setName = (v: string) => {
    setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }));
    clearError('name');
    if (!slugTouched) clearError('slug');
  };

  const setSlug = (v: string) => {
    setSlugTouched(true);
    set('slug', slugifyInput(v));
  };

  const toggleLgpd = (k: LgpdKey) =>
    setForm((f) => ({ ...f, lgpd: { ...f.lgpd, [k]: !f.lgpd[k] } }));

  const toggleApresentacao = () =>
    setForm((f) => ({ ...f, apresentacaoDesabilitada: !f.apresentacaoDesabilitada }));

  /**
   * Telas adicionais. Os quatro gestos usam atualização FUNCIONAL porque a
   * mesma lista também cresce de forma assíncrona (o upload responde depois do
   * render que registrou o clique): a lista válida é a do estado na hora de
   * aplicar, nunca a capturada no fechamento.
   */
  const addGaleria = (key: string) =>
    setForm((f) => {
      // Tetos e duplicata reconferidos aqui — o servidor recusa os três, e este
      // é o único ponto que enxerga a capa e a lista ao mesmo tempo.
      const cheio =
        totalImagens(f.imageKey, f.galeriaKeys) >= APPLICATION_MAX_IMAGES ||
        f.galeriaKeys.length >= GALERIA_MAX;
      if (cheio || f.galeriaKeys.includes(key)) return f;
      return { ...f, galeriaKeys: [...f.galeriaKeys, key] };
    });

  const removeGaleria = (index: number) =>
    setForm((f) => ({ ...f, galeriaKeys: f.galeriaKeys.filter((_, i) => i !== index) }));

  const moveGaleria = (index: number, delta: -1 | 1) =>
    setForm((f) => {
      const next = moverItem(f.galeriaKeys, index, delta);
      // Mesma referência = clique na ponta da lista, sem nada a mover.
      return next === f.galeriaKeys ? f : { ...f, galeriaKeys: next };
    });

  /**
   * Promover uma tela a capa é uma TROCA com a capa atual, não uma remoção: a
   * imagem que estava na capa continua na apresentação, ocupando a vaga que a
   * promovida deixou (é o gesto que evita reenviar arquivo já gravado). Sem
   * capa, a tela apenas sai da tira.
   *
   * As duas listas mudam no MESMO `setForm`: dois `set` separados deixariam um
   * render no meio com a mesma chave na capa e na galeria.
   */
  const promoteGaleria = (index: number) =>
    setForm((f) => {
      const nova = f.galeriaKeys[index];
      if (nova === undefined) return f;
      const galeria = [...f.galeriaKeys];
      if (f.imageKey === null) galeria.splice(index, 1);
      else galeria[index] = f.imageKey;
      return { ...f, imageKey: nova, galeriaKeys: galeria };
    });

  /**
   * Trocar o tipo para FULLSTACK pré-seleciona as stacks do padrão da casa
   * (FULLSTACK_DEFAULT_STACKS, em catalog-config): mesmo ferramental em todos
   * os monorepos fullstack da Masterboi. A mesclagem PRESERVA o que o usuário
   * já marcou (sem duplicar), só dispara na TRANSIÇÃO para fullstack (não em
   * rascunho restaurado nem edição que já nasce fullstack) e nada é removido
   * ao trocar para outro tipo — stack marcada é decisão do usuário.
   */
  const setType = (v: string) => {
    setForm((f) => {
      if (v !== 'fullstack' || f.type === 'fullstack') return { ...f, type: v };
      const novas = FULLSTACK_DEFAULT_STACKS.filter((s) => !f.stacks.includes(s));
      return { ...f, type: v, stacks: [...f.stacks, ...novas] };
    });
    clearError('type');
    // As stacks obrigatórias acabaram de ser satisfeitas pelo preset.
    clearError('stacks');
  };

  /**
   * Trocar a visibilidade zera o acesso quando ele deixa de fazer sentido:
   * `publico` libera para a empresa inteira e `interno` é infraestrutura (só
   * administradores). Guardar as listas nesses casos publicaria acesso
   * inconsistente, que o servidor recusa.
   */
  const setVisibility = (v: string) => {
    setForm((f) =>
      v === 'privado'
        ? { ...f, visibility: v }
        : {
            ...f,
            visibility: v,
            accessUnits: [],
            accessSectors: [],
            accessCollaborators: [],
            perCollaborator: false,
          },
    );
    // TROCAR para uma visibilidade sem acesso por unidade/setor limpa as listas
    // de propósito: na edição isso é uma alteração DE ACESSO e precisa viajar no
    // PATCH (senão as associações antigas ficariam gravadas para uma app que não
    // é mais restrita por elas). Reclicar na visibilidade já ativa não conta.
    if (v !== 'privado' && v !== form.visibility) setAccessTouched(true);
    clearError('visibility');
    clearError('accessUnits');
    clearError('accessSectors');
    clearError('accessCollaborators');
  };

  // Patch parcial do acesso · a etapa altera mais de um campo por interação
  // (remover unidade poda setores e colaboradores em cascata).
  const patchAccess = (patch: AccessPatch) => {
    setForm((f) => ({
      ...f,
      accessUnits: patch.units ?? f.accessUnits,
      accessSectors: patch.sectors ?? f.accessSectors,
      accessCollaborators: patch.collaborators ?? f.accessCollaborators,
      perCollaborator: patch.perCollaborator ?? f.perCollaborator,
    }));
    setAccessTouched(true);
    if (patch.units !== undefined) clearError('accessUnits');
    if (patch.sectors !== undefined) clearError('accessSectors');
    if (patch.collaborators !== undefined) clearError('accessCollaborators');
  };

  // Repositório: sempre GitHub. Obrigatório para TODOS os tipos, menos Power
  // Apps (low-code, sem repositório Git) — ver `repoIsRequired`.
  const repoRequired = repoIsRequired(form.type);

  // Progresso e validação saem da MESMA lista de obrigatórios.
  const specs = useMemo(() => requiredSpecs(form), [form]);
  const filledCount = specs.filter((s) => s.ok(form)).length;

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseForm), [form, baseForm]);

  /** Pinta os campos recusados pelo servidor · `true` quando achou algum. */
  const applyServerIssues = useCallback((error: unknown): boolean => {
    const next = errorsFromServer(error);
    setErrors(next);
    focusFirstError(next);
    return FIELD_ORDER.some((f) => next[f]);
  }, []);

  const handleSubmit = useCallback(() => {
    if (submitting) return;

    /**
     * REGRA DE ENVIO DO `access` (pegadinha verificada do PATCH):
     *   • `access` presente com listas VAZIAS apaga as associações gravadas;
     *   • `access` AUSENTE preserva o que está gravado.
     * Na edição, portanto, só enviamos o bloco quando ele é intenção explícita:
     *   1. visibilidade final `privado` — as listas SÃO o conteúdo do campo e o
     *      servidor recusa app privada sem restrição; ou
     *   2. o usuário mexeu no acesso nesta sessão (`accessTouched`, que inclui
     *      sair de `privado`, quando as listas são limpas de propósito).
     * Resultado: corrigir a descrição de uma app privada ou interna nunca apaga
     * a matriz de acesso dela. Na criação o bloco vai sempre (não há o que
     * preservar e o POST precisa dele).
     */
    const includeAccess = !editing || form.visibility === 'privado' || accessTouched;

    // Mesmas regras que rodam no servidor · nada é enviado com fallback mudo.
    const payload = buildPayload(form, {
      includeAccess,
      preserve: initial
        ? {
            favorite: initial.favorite,
            iconBg: initial.iconBg,
            deployedAt: initial.deployedAt,
            lastUpdate: initial.lastUpdate,
          }
        : undefined,
    });
    const next = validateForm(form, payload);
    setErrors(next);
    if (Object.keys(next).some((k) => next[k])) {
      focusFirstError(next);
      return;
    }

    onSubmit?.(payload, { onSuccess: draft.clear, onError: applyServerIssues });
  }, [form, submitting, editing, accessTouched, initial, onSubmit, draft.clear, applyServerIssues]);

  const handleCancel = useCallback(() => {
    if (submitting) return;
    const question = editing
      ? 'Sair da edição? O que você alterou continua salvo como rascunho neste navegador.'
      : 'Sair do cadastro? O que você preencheu continua salvo como rascunho neste navegador.';
    if (dirty && !window.confirm(question)) return;
    navigate('/admin/inventory');
  }, [dirty, submitting, editing, navigate]);

  // Atalhos · Ctrl+Enter salva, Esc cancela. Fica inerte quando há popover ou
  // menu aberto: nesse caso o Esc pertence a ele, não ao formulário.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (hasOpenLayer()) return;
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
        return;
      }
      if (e.key === 'Escape') handleCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSubmit, handleCancel]);

  const restoreDraft = () => {
    const d = draft.restored;
    if (!d) return;
    const merged = mergeDraft(baseForm, d);
    setForm(merged);
    setSlugTouched(editing || (merged.slug !== '' && merged.slug !== slugify(merged.name)));
    // Rascunho com acesso diferente do gravado: o usuário havia mexido no acesso,
    // então o PATCH precisa levar o bloco.
    if (editing && accessDiffers(merged, baseForm)) setAccessTouched(true);
    setErrors({});
    draft.dismissRestored();
  };

  const savedLabel = draft.savedAt
    ? draft.savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  const generalError = errors._;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="inventory" dark={dark} accent={accent} role="admin" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          breadcrumb={
            editing
              ? ['Administração', 'Inventário', initial?.name ?? 'Aplicação']
              : ['Administração', 'Inventário']
          }
          title={editing ? 'Editar aplicação' : 'Cadastrar aplicação'}
          subtitle={
            editing
              ? 'As alterações valem no catálogo assim que você salvar'
              : 'Preencha todos os campos obrigatórios para garantir conformidade com a auditoria'
          }
          right={
            <>
              {/* Indicador do autosave · substitui o botão "Salvar rascunho".
                  No celular ele toma a linha inteira acima dos botões: os 132px
                  de reserva mais "Cancelar" (79) mais "Publicar no catálogo"
                  (184) somam 395px, e a faixa de ações tem ~246px a 320px. */}
              <span
                aria-live="polite"
                style={{
                  fontSize: 11,
                  fontFamily: fontSans,
                  color: p.muted,
                  minWidth: byViewport(vp, { mobile: 0, tablet: 132, desktop: 132 }),
                  flexBasis: byViewport(vp, {
                    mobile: '100%',
                    tablet: undefined,
                    desktop: undefined,
                  }),
                  textAlign: byViewport(vp, { mobile: 'left', tablet: 'right', desktop: 'right' }),
                  whiteSpace: 'nowrap',
                }}
              >
                {draftStatusLabel(draft.status, savedLabel)}
              </span>
              <ButtonGhost dark={dark} onClick={handleCancel}>
                Cancelar
              </ButtonGhost>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  // No celular a ação principal ocupa o espaço que sobra da linha
                  // e cresce para 40px — o mínimo de um alvo de toque.
                  height: byViewport(vp, PRIMARY_BTN_H),
                  flex: byViewport(vp, { mobile: 1, tablet: undefined, desktop: undefined }),
                  justifyContent: byViewport(vp, {
                    mobile: 'center',
                    tablet: undefined,
                    desktop: undefined,
                  }),
                  padding: '0 16px',
                  background: accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: fontSans,
                  cursor: submitting ? 'progress' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <IconCheck size={14} />
                {submitLabel(editing, submitting)}
              </button>
            </>
          }
        />

        <div
          style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            display: 'grid',
            /**
             * A coluna de resumo é FIXA em 320px: no compacto ela sozinha come
             * mais espaço do que resta para o formulário (a 375px sobravam 7px
             * para os campos, medido). Fora do desktop a grade tem uma coluna só e
             * o resumo desce para o fim da página — é bloco de CONFERÊNCIA (cards
             * que serão publicados, acesso resultante), então lê-lo depois de
             * preencher é a ordem natural; no topo ele apareceria vazio.
             */
            gridTemplateColumns: byViewport(vp, {
              mobile: '1fr',
              tablet: '1fr',
              desktop: '1fr 320px',
            }),
            gap: 0,
          }}
        >
          <div
            style={{
              padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
              minWidth: 0,
            }}
          >
            {/* Rascunho encontrado no navegador */}
            {draft.restored && (
              <DraftBanner
                editing={editing}
                onRestore={restoreDraft}
                onDiscard={draft.clear}
                dark={dark}
                accent={accent}
              />
            )}

            {generalError && (
              <div
                role="alert"
                style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  background: dark ? `${DANGER}18` : `${DANGER}0d`,
                  border: `1px solid ${DANGER}55`,
                  borderRadius: 10,
                  fontSize: 12,
                  color: p.text,
                  fontFamily: fontSans,
                }}
              >
                {generalError}
              </div>
            )}

            {/* Seção 1: capa panorâmica + tira de telas da apresentação */}
            <FormSection
              step="1"
              title="Imagens da aplicação"
              subtitle={`Capa do catálogo e as telas da apresentação · até ${APPLICATION_MAX_IMAGES} imagens, PNG, JPEG ou WebP de 4 MB cada`}
              dark={dark}
              accent={accent}
            >
              {/*
                O selo de CAPA fica fora do quadro de arrastar e soltar: dentro
                ele disputaria espaço com o preview, que é justamente a imagem
                que o selo está nomeando.
              */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    ...rotuloCaixaAlta,
                    fontSize: 9.5,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: accent,
                    background: dark ? `${accent}1e` : `${accent}0f`,
                    border: `1px solid ${accent}44`,
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  Capa · tela 1
                </span>
                <span style={{ fontSize: 11, color: p.muted, fontFamily: fontSans }}>
                  Ilustra o card no catálogo, a ficha da aplicação e abre o carrossel da
                  apresentação.
                </span>
              </div>

              <ImageField
                imageKey={form.imageKey}
                onChange={(key) => set('imageKey', key)}
                dark={dark}
                accent={accent}
                busy={submitting}
              />

              <GaleriaField
                capaKey={form.imageKey}
                keys={form.galeriaKeys}
                onAdd={addGaleria}
                onRemove={removeGaleria}
                onMove={moveGaleria}
                onPromote={promoteGaleria}
                dark={dark}
                accent={accent}
                busy={submitting}
              />

              <div
                style={{
                  marginTop: 12,
                  fontSize: 11,
                  color: p.muted,
                  fontFamily: fontSans,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  lineHeight: 1.45,
                }}
              >
                <IconBolt size={12} style={{ color: accent, flexShrink: 0 }} />
                <span>
                  Use capturas reais do app (dashboard, lista ou tela de boas-vindas) na proporção
                  16:9. Evite logo isolado.
                </span>
              </div>
            </FormSection>

            {/* Seção 2: Identificação */}
            <FormSection
              step="2"
              title="Identificação"
              subtitle="Como a aplicação aparece no catálogo"
              dark={dark}
              accent={accent}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: byViewport(vp, FIELD_GRID_2),
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                {/*
                  O `span` acompanha a grade: numa grade de UMA coluna, `span 2`
                  cria uma segunda coluna implícita e o campo sai da tela (medido
                  a 320px: 49px para fora).
                */}
                <FormField
                  label="Nome da aplicação"
                  required
                  name="name"
                  error={errors.name}
                  dark={dark}
                  span={byViewport(vp, { mobile: 1, tablet: 2, desktop: 2 })}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.name}
                      onChange={setName}
                      placeholder="Ex: Portal do Pecuarista"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                {/*
                  Slug BLOQUEADO na edição (decisão): ele é a URL pública da
                  aplicação (`/app/<slug>`), já compartilhada em chamados,
                  e-mails e favoritos, e a API não mantém redirecionamento do
                  slug antigo. Trocá-lo aqui quebraria silenciosamente todos os
                  links existentes, então a alteração fica fora da tela e o campo
                  explica o motivo.
                */}
                <FormField
                  label="Identificador (slug)"
                  required
                  name="slug"
                  error={errors.slug}
                  hint={
                    editing
                      ? 'Bloqueado na edição · é a URL pública da aplicação e mudá-la quebraria os links já compartilhados'
                      : 'Usado na URL do catálogo · vem do nome, mas você pode ajustar'
                  }
                  dark={dark}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.slug}
                      onChange={setSlug}
                      readOnly={editing}
                      placeholder="portal-pecuarista"
                      dark={dark}
                      accent={accent}
                      mono
                      prefix="/app/"
                    />
                  )}
                </FormField>

                <FormField
                  label="Tipo da aplicação"
                  required
                  name="type"
                  error={errors.type}
                  info={<InfoPopover {...FIELD_HELP.tipo} dark={dark} accent={accent} size={18} />}
                  dark={dark}
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={form.type}
                      onChange={setType}
                      options={TYPE_OPTIONS}
                      placeholder="Selecione…"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>
              </div>

              <FormField
                label="Descrição"
                required
                name="description"
                error={errors.description}
                hint="2 a 3 linhas explicando o que a aplicação faz e para quem"
                dark={dark}
              >
                {(f) => (
                  <TextArea
                    {...f}
                    value={form.description}
                    onChange={(v) => set('description', v)}
                    placeholder="Ex: Portal externo para fornecedores de gado consultarem escalas, classificações e pagamentos."
                    dark={dark}
                    accent={accent}
                  />
                )}
              </FormField>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: byViewport(vp, FIELD_GRID_3),
                  gap: 14,
                  marginTop: 14,
                }}
              >
                <FormField
                  label="Visibilidade"
                  required
                  name="visibility"
                  error={errors.visibility}
                  info={
                    <InfoPopover
                      {...FIELD_HELP.visibilidade}
                      dark={dark}
                      accent={accent}
                      size={18}
                    />
                  }
                  dark={dark}
                >
                  {(f) => (
                    <VisibilityPicker
                      value={form.visibility}
                      onChange={setVisibility}
                      controlId={f.id}
                      dark={dark}
                    />
                  )}
                </FormField>

                <FormField
                  label="Situação"
                  required
                  name="status"
                  error={errors.status}
                  info={
                    <InfoPopover {...FIELD_HELP.situacao} dark={dark} accent={accent} size={18} />
                  }
                  dark={dark}
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={form.status}
                      onChange={(v) => set('status', v)}
                      options={STATUS_OPTIONS}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="Criticidade"
                  required
                  name="criticality"
                  error={errors.criticality}
                  info={
                    <InfoPopover
                      {...FIELD_HELP.criticidade}
                      dark={dark}
                      accent={accent}
                      size={18}
                    />
                  }
                  dark={dark}
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={form.criticality}
                      onChange={(v) => set('criticality', v)}
                      options={CRITICALITY_OPTIONS}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>
              </div>

              {/* Consequência da visibilidade escolhida · aviso, não erro. */}
              <div style={{ marginTop: 12 }}>
                {form.visibility === 'publico' && (
                  <Notice color={OK_GREEN} icon={<IconGlobe size={14} />} dark={dark}>
                    Todos os colaboradores da empresa terão acesso a visualizar esta aplicação
                  </Notice>
                )}
                {form.visibility === 'privado' && (
                  <Notice color={HML_YELLOW} icon={<IconLock size={14} />} dark={dark}>
                    Acesso restrito. Escolha na etapa{' '}
                    <strong>3 · Acesso e confidencialidade</strong> quais unidades, setores e
                    colaboradores enxergam esta aplicação.
                  </Notice>
                )}
                {form.visibility === 'interno' && (
                  <Notice color={DANGER} icon={<IconShield size={14} />} dark={dark}>
                    Visibilidade de infraestrutura. A aplicação não aparece no catálogo do
                    colaborador: somente administradores conseguem visualizar, então não há acesso
                    por unidade ou setor a definir.
                  </Notice>
                )}
              </div>
            </FormSection>

            {/* Seção 3: Acesso e confidencialidade */}
            <FormSection
              step="3"
              title="Acesso e confidencialidade"
              subtitle="Quem enxerga a aplicação no catálogo · unidades, setores e pessoas do TOTVS"
              dark={dark}
              accent={accent}
            >
              <AccessStep
                form={form}
                onPatch={patchAccess}
                errors={errors}
                dark={dark}
                accent={accent}
              />
            </FormSection>

            {/* Seção 4: Rastreabilidade */}
            <FormSection
              step="4"
              title="Rastreabilidade"
              subtitle="Identificadores internos · obrigatórios para a auditoria"
              dark={dark}
              accent={accent}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: byViewport(vp, FIELD_GRID_2),
                  gap: 14,
                }}
              >
                <FormField
                  label="Número do chamado"
                  required
                  name="ticket"
                  error={errors.ticket}
                  dark={dark}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.ticket}
                      onChange={(v) => set('ticket', v)}
                      placeholder="CHM-2026-XXXX"
                      dark={dark}
                      accent={accent}
                      mono
                    />
                  )}
                </FormField>
                <FormField
                  label="Número do projeto"
                  required
                  name="project"
                  error={errors.project}
                  dark={dark}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.project}
                      onChange={(v) => set('project', v)}
                      placeholder="PRJ-XXX-NNN"
                      dark={dark}
                      accent={accent}
                      mono
                    />
                  )}
                </FormField>
                <FormField
                  label="Time responsável"
                  required
                  name="team"
                  error={errors.team}
                  dark={dark}
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={form.team}
                      onChange={(v) => set('team', v)}
                      options={TEAM_OPTIONS}
                      placeholder="Selecione um time…"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>
                <FormField
                  label="Responsável"
                  required
                  name="owner"
                  error={errors.owner}
                  hint="Pessoa nomeada da Masterboi · digite para buscar na base do RH"
                  dark={dark}
                >
                  {(f) => (
                    <OwnerField
                      {...f}
                      value={form.owner}
                      onChange={(v) => set('owner', v)}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>
              </div>
            </FormSection>

            {/* Seção 5: Projeto · logo depois da Rastreabilidade, que é o bloco
                mais próximo em assunto (chamado, projeto, time, responsável). */}
            <FormSection
              step="5"
              title="Projeto"
              subtitle="Ponto de vista de quem pediu a aplicação · seção inteira opcional"
              dark={dark}
              accent={accent}
            >
              <ProjetoFields
                form={form}
                onChange={(campo, valor) => set(campo, valor)}
                errors={errors}
                dark={dark}
                accent={accent}
              />
            </FormSection>

            {/* Seção 6: URLs por ambiente */}
            <FormSection
              step="6"
              title="URLs por ambiente"
              subtitle="As URLs geram automaticamente os cards do catálogo · abrem em nova aba"
              dark={dark}
              accent={accent}
            >
              <div
                style={{
                  padding: 12,
                  marginBottom: 16,
                  background: dark ? `${accent}11` : `${accent}08`,
                  border: `1px dashed ${accent}55`,
                  borderRadius: 8,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  fontSize: 11.5,
                  color: p.textMuted,
                  lineHeight: 1.5,
                  fontFamily: fontSans,
                }}
              >
                <span style={{ color: accent, marginTop: 1, flexShrink: 0 }}>
                  <IconBolt size={14} />
                </span>
                <div>
                  Cada URL preenchida gera um card no catálogo. <strong>Produção</strong> aparece
                  por padrão; <strong>Homologação</strong> só aparece no filtro Hml com banner
                  amarelo de aviso. <strong>Dev</strong> é interna.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FormField
                  label="URL de produção"
                  // Obrigatória SÓ quando a situação é produção (missingStatusUrl):
                  // uma app em dev/homolog pode ainda não ter produção no ar.
                  required={form.status === 'prod'}
                  optional={form.status !== 'prod'}
                  name="urlProd"
                  error={errors.urlProd}
                  hint="Card publicado no filtro PRODUÇÃO · padrão do catálogo · obrigatória quando a situação é Produção"
                  dark={dark}
                >
                  {(f) => (
                    <UrlEnvRow
                      env="prod"
                      control={f}
                      value={form.urlProd}
                      onChange={(v) => set('urlProd', v)}
                      placeholder="https://app.masterboi.com.br"
                      cardColor={OK_GREEN}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="URL de homologação"
                  // Espelho da regra da produção: exigida quando a situação é
                  // homologação.
                  required={form.status === 'homolog'}
                  optional={form.status !== 'homolog'}
                  name="urlHml"
                  error={errors.urlHml}
                  hint="Card no filtro HOMOLOGAÇÃO com banner amarelo · obrigatória quando a situação é Homologação"
                  dark={dark}
                >
                  {(f) => (
                    <UrlEnvRow
                      env="homolog"
                      control={f}
                      value={form.urlHml}
                      onChange={(v) => set('urlHml', v)}
                      placeholder="https://hml.app.masterboi.com.br"
                      cardColor={HML_YELLOW}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="URL de desenvolvimento"
                  optional
                  name="urlDev"
                  error={errors.urlDev}
                  hint="Não publica card · disponível apenas no painel administrativo"
                  dark={dark}
                >
                  {(f) => (
                    <UrlEnvRow
                      env="dev"
                      control={f}
                      value={form.urlDev}
                      onChange={(v) => set('urlDev', v)}
                      placeholder="https://dev.app.masterboi.local"
                      cardColor={null}
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>
              </div>
            </FormSection>

            {/* Seção 7: Documentação */}
            <FormSection
              step="7"
              title="Documentação · links"
              subtitle="Apenas links · nenhum documento é hospedado aqui"
              dark={dark}
              accent={accent}
            >
              <div
                style={{ display: 'grid', gridTemplateColumns: byViewport(vp, DOC_GRID), gap: 14 }}
              >
                <FormField
                  label="Documentação técnica"
                  required
                  name="docsTech"
                  error={errors.docsTech}
                  hint="Arquitetura, deploy, dependências · escolha o site"
                  dark={dark}
                >
                  {(f) => (
                    <SiteUrlInput
                      {...f}
                      site={form.docsTechSite}
                      onSite={(v) => set('docsTechSite', v)}
                      value={form.docsTech}
                      onChange={(v) => set('docsTech', v)}
                      placeholder="sites/TI/docs/arquitetura"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="Regra de negócio"
                  required
                  name="docsBiz"
                  error={errors.docsBiz}
                  hint="Processo e regras do negócio · escolha o site"
                  dark={dark}
                >
                  {(f) => (
                    <SiteUrlInput
                      {...f}
                      site={form.docsBizSite}
                      onSite={(v) => set('docsBizSite', v)}
                      value={form.docsBiz}
                      onChange={(v) => set('docsBiz', v)}
                      placeholder="sites/TI/docs/regra-negocio"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="Documentação da API"
                  optional
                  name="docsApi"
                  error={errors.docsApi}
                  hint="Swagger / OpenAPI"
                  dark={dark}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.docsApi}
                      onChange={(v) => set('docsApi', v)}
                      placeholder="https://api.masterboi.com.br/.../docs"
                      dark={dark}
                      accent={accent}
                      mono
                    />
                  )}
                </FormField>

                <FormField
                  label="Levantamento de requisitos"
                  optional
                  name="requirements"
                  error={errors.requirements}
                  hint="Escolha o site · SharePoint ou GitHub"
                  dark={dark}
                >
                  {(f) => (
                    <SiteUrlInput
                      {...f}
                      site={form.requirementsSite}
                      onSite={(v) => set('requirementsSite', v)}
                      value={form.requirements}
                      onChange={(v) => set('requirements', v)}
                      placeholder="sites/TI/docs/req-app"
                      dark={dark}
                      accent={accent}
                    />
                  )}
                </FormField>

                <FormField
                  label="Repositório (GitHub)"
                  required={repoRequired}
                  optional={!repoRequired}
                  name="repo"
                  error={errors.repo}
                  hint={
                    repoRequired
                      ? 'Obrigatório · sempre GitHub (só Power Apps é dispensado)'
                      : 'Dispensado para Power Apps · quando houver, use o GitHub'
                  }
                  dark={dark}
                  // `DOC_GRID` só tem duas colunas no desktop · fora dele o span
                  // precisa ser 1, senão surge uma coluna implícita.
                  span={byViewport(vp, { mobile: 1, tablet: 1, desktop: 2 })}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={form.repo}
                      onChange={(v) => set('repo', v)}
                      placeholder="https://github.com/masterboiteam/..."
                      dark={dark}
                      accent={accent}
                      mono
                    />
                  )}
                </FormField>
              </div>
            </FormSection>

            {/* Seção 8: Stack */}
            <FormSection
              step="8"
              title="Stack técnico"
              subtitle="Escolha na base de conhecimento da Masterboi ou cadastre uma tecnologia nova"
              dark={dark}
              accent={accent}
            >
              <FormField
                label="Tecnologias / stacks"
                required
                name="stacks"
                error={errors.stacks}
                hint="A busca cobre 86 tecnologias agrupadas por categoria · o que não estiver na lista pode ser adicionado como texto livre"
                dark={dark}
              >
                {(f) => (
                  <StackPicker
                    {...f}
                    value={form.stacks}
                    onChange={(next) => set('stacks', next)}
                    dark={dark}
                    accent={accent}
                  />
                )}
              </FormField>
            </FormSection>

            {/* Seção 9: Compliance */}
            <FormSection
              step="9"
              title="Compliance · LGPD"
              subtitle="Conformidade regulatória e governança de dados · clique no (i) para detalhes"
              dark={dark}
              accent={accent}
            >
              <LgpdChecklist value={form.lgpd} onToggle={toggleLgpd} dark={dark} accent={accent} />
            </FormSection>

            {/* Seção 10: Apresentação. Seção própria, e não um quinto item do
                checklist de LGPD: aquele bloco é evidência de auditoria, e um
                check de apresentação lá dentro pareceria declaração de
                conformidade. O interruptor é o mesmo da confidencialidade. */}
            <FormSection
              step="10"
              title="Apresentação"
              subtitle="Deck do portfólio · o mesmo check da lista de plataformas no lobby da apresentação"
              dark={dark}
              accent={accent}
            >
              <ToggleRow
                checked={form.apresentacaoDesabilitada}
                onToggle={toggleApresentacao}
                label="Desabilitar apresentação"
                sub="Não entra no deck nem no PDF · desmarcar a plataforma no lobby liga este check"
                dark={dark}
                accent={accent}
              />
            </FormSection>
          </div>

          {/* Coluna direita · o wrapper mantém a borda/fundo em toda a altura,
              o conteúdo é sticky e acompanha o scroll. Empilhado, a divisória
              deixa de ser a borda esquerda e passa a ser a de cima. */}
          <div
            style={{
              borderLeft: byViewport(vp, {
                mobile: undefined,
                desktop: `1px solid ${p.border}`,
              }),
              borderTop: byViewport(vp, {
                mobile: `1px solid ${p.border}`,
                desktop: undefined,
              }),
              background: p.surface,
              minWidth: 0,
            }}
          >
            <RegisterSidebar
              form={form}
              filledCount={filledCount}
              totalRequired={specs.length}
              editing={editing}
              dark={dark}
              accent={accent}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
